class Company {
    urlMatchConfidenceThresholds = [
        {
            confidence: "low",
            scoreLowerThreshold: 0,
            description: "Low website match confidence"
        }, {
            confidence: "medium",
            scoreLowerThreshold: 7,
            description: "Medium website match confidence"
        }, {
            confidence: "high",
            scoreLowerThreshold: 12,
            description: "High website match confidence"
        }, {
            confidence: "veryhigh",
            scoreLowerThreshold: 16,
            description: "Very high website match confidence"
        }
    ]

    constructor(companyData, parent) {
        const self = this;
        let companyDetails, groupStructure, fundingDetails, personsWithSignificantControl, shareHolders, urlMatchStats, _360GivingFundingData, innovateUkFunding, companyJobPostings, companyJobPostingsBySkill, companyRtics, companyCics, companyJobPostingsOverTime, companyJobPostingsByLot, esgStatementAnchors;

        companyDetails = Object.hasOwn(companyData, "CompanyDetails") ? companyData.CompanyDetails : companyData;
        this.mismatchedRticTomSelect;
        this.parent = parent;
        this.companyNumber = companyDetails.CompanyNumber;
        this.companyName = companyDetails.CompanyName;
        this.companyCategory = companyDetails.CompanyCategory;
        this.incorporationDate = companyDetails.IncorporationDate;
        this.companyURL = companyDetails.Homepage_domain;
        this.countryOfOrigin = companyDetails.CountryOfOrigin;
        this.email = companyDetails.Email;
        this.emailAddresses = companyDetails.EmailAddresses;
        this.registeredAddress = companyDetails.RegisteredAddress;
        this.registeredPostcode = companyDetails.RegisteredPostcode;
        this.phone = companyDetails.Phone;
        this.phoneNumbers = companyDetails.PhoneNumbers;
        this.sectorKeywords = companyDetails.SectorKeywords;
        this.innovationKeywords = companyDetails.InnovationKeywords;
        this.facebook = companyDetails.Facebook;
        this.twitter = companyDetails.Twitter;
        this.instagram = companyDetails.Instagram;
        this.youtube = companyDetails.Youtube;
        this.linkedin = companyDetails.Linkedin;
        this.sics = companyDetails.SICs;
        this.rsics = Object.hasOwn(companyDetails, 'RSICs') ? (companyDetails.RSICs ? companyDetails.RSICs.map(el => new Rsic(el.Id, el.SIC_Code, el.Score)) : []) : null;
        this.sichlus = companyDetails.SICHLUs;
        this.companyDescription = companyDetails.CompanyDescription;
        this.tonnesOfC02equivGHGPerYear = companyDetails.TonnesOfC02equivGHGPerYear;
        this.hasUrlMismatchReport = companyDetails.hasUrlMismatchReport ?? false;
        this.locations = [];
        this.financials = [];
        this.directors = [];
        this.rtics = [];
        this.cics = [];
        this.innovationScore = companyDetails.InnovationScore;
        this.similarCompanies = companyDetails.SimilarCompanies;
        this.similarCompanyDetails = [];
        this.bestEstimateEmployees = companyDetails.BestEstimateEmployees;
        this.bestEstimateTurnover = companyDetails.BestEstimateTurnover;
        this.groupCompanyLevel = null;
        this.groupHoldingType = null;
        this.groupParentName = null;
        this.groupParentCompanyNumber = null;
        this.groupParentNation = null;
        this.groupUltimateParentName = null;
        this.groupUltimateParentCompanyNumber = null;
        this.groupUltimateParentNation = null;
        this.lastFundingDate = companyData.LastFundingDate ?? null;
        this.totalFundingEuroMillions = companyData.TotalFundingRaised_GBP_Million ?? null;
        this.dealroomIsSpinout = companyData.Spinout ?? null;
        this.personsWithSignificantControl = new PersonsWithSignificantControl();
        this.shareHolders = new ShareHolders(this.companyNumber);
        this.urlMatchStats = null;
        this._360GivingFunding = new _360GivingFunding(this.companyNumber);
        this.innovateUkFunding = new InnovateUkFunding(this.companyNumber);
        this.companyJobPostings = new CompanyJobPostings(this.companyNumber);
        this.bestEstimateEmployeeGrowthPercentagePerYear = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.BestEstimateEmployeeGrowthPercentagePerYear : null;
        this.bestEstimateTurnoverGrowthPercentagePerYear = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.BestEstimateTurnoverGrowthPercentagePerYear : null;
        this.bestEstimateGrowthPercentagePerYear = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.BestEstimateGrowthPercentagePerYear : null;
        this.companyGrowthStageEstimate = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.CompanyGrowthStageEstimate : null;
        this.employmentEstimates = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.EmploymentEstimates : null;
        this.turnoverEstimates = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.TurnoverEstimates : null;
        this.oecdScaleup = (companyDetails.CompanyGrowthTrends != undefined) ? companyDetails.CompanyGrowthTrends.OECDScaleup : null;
        this.fundingDetails = [];
        this.estimatedGVA = companyData.EstimatedGVA ?? null;
        this.womenLedStats = companyData.WomenLedStats ?? null;
        this.esgStatementUrl = companyData.ESGStatementURL;
        this.esgStatementAnchors = [];
        this.active = companyDetails.Active ?? null;
        this.companyStatus = companyDetails.CompanyStatus ?? null;
        this.lastKnownActiveDate = companyDetails.LastKnownActiveDate ?? null;
        this.industrialStrategySectors = companyDetails.ISCs ? companyDetails.ISCs.map(element => new IndustrialStrategySector({code: element.ISC_Code, name: element.ISC_Name})) : null;
        this.matchedWebsiteScore = companyData.MatchedWebsiteScore ?? null;

        if (companyData.Officers) {
            // Add director details
            companyData.Officers.forEach(director => self.newCompanyDirector(director.PersonNumber, director.Forenames, director.Surname, director.Role, director.Appointedon, director.ResignationDate));
        }

        if (companyDetails.LocationDetails) {
            const maxLocationTotalOccupationCount = this.getMaxLocationTotalOccupationCount(companyDetails.LocationDetails); // If available will return count of occupations at the company location with the most employees
            // Add location details
            companyDetails.LocationDetails.forEach(location => self.newCompanyLocation(location.Postcode_withspaces, location.LAname, location.UKConstituency, location.LEPname, location.NUTS1NM, location.RegisteredPostcode, location.StandardisedCity, location.Latitude, location.Longitude, location.OECDFUACorePlusCommuting, location.OccupationCounts, maxLocationTotalOccupationCount));
        }

        if (companyDetails.OccupationCountByNation) {
            this.occupationCountByNation = companyDetails.OccupationCountByNation.reduce((accumulator, element) => {accumulator[element.ID] = new OccupationCountByNation({code: element.ID, name: element.Thing, count: element.Count, occupationCounts: element.OccupationCounts }); return accumulator}, {});
        }

        // If available add financial details
        if (companyDetails.CompanyFinancials_CreditSafe) {
            companyDetails.CompanyFinancials_CreditSafe.slice(-7).forEach(financials => self.newFinancials(((financials.YEAR != undefined) ? financials.YEAR : null), ((financials.TURNOVER != undefined) ? financials.TURNOVER : null), ((financials.PRE_TAX_PROFIT != undefined) ? financials.PRE_TAX_PROFIT : null), ((financials.PROFIT_AFTER_TAX != undefined) ? financials.PROFIT_AFTER_TAX : null), ((financials.CASH != undefined) ? financials.CASH : null), ((financials.TOTAL_CURRENT_ASSETS != undefined) ? financials.TOTAL_CURRENT_ASSETS : null), ((financials.TOTAL_ASSETS != undefined) ? financials.TOTAL_ASSETS : null), ((financials.TOTAL_LIABILITIES != undefined) ? financials.TOTAL_LIABILITIES : null), ((financials.SHAREHOLDER_FUNDS != undefined) ? financials.SHAREHOLDER_FUNDS : null), ((financials.NET_WORTH != undefined) ? financials.NET_WORTH : null), ((financials.NUMBER_OF_EMPLOYEES != undefined) ? financials.NUMBER_OF_EMPLOYEES : null), ((financials.DEBTORS_DUE_AFTER_ONE_YEAR != undefined) ? financials.DEBTORS_DUE_AFTER_ONE_YEAR : null), ((financials.DEBTORS != undefined) ? financials.DEBTORS : null), ((financials.TRADE_DEBTORS != undefined) ? financials.TRADE_DEBTORS : null), ((financials.GROUP_DEBTORS != undefined) ? financials.GROUP_DEBTORS : null), ((financials.CREDITORS != undefined) ? financials.CREDITORS : null), ((financials.EXPORT != undefined) ? financials.EXPORT : null), ((financials.WAGES_AND_SALARIES != undefined) ? financials.WAGES_AND_SALARIES : null), ((financials.DEPRECIATION != undefined) ? financials.DEPRECIATION : null), ((financials.BANK_OVERDRAFT != undefined) ? financials.BANK_OVERDRAFT : null), ((financials.EBITDA != undefined) ? financials.EBITDA : null), ((financials.AUDITORS != undefined) ? financials.AUDITORS : null), ((financials.DECLARED_EMPLOYEES_ANOMALOUS != undefined) ? financials.DECLARED_EMPLOYEES_ANOMALOUS : null), ((financials.EMPLOYEES_ANOMALOUS_PROBABILITY != undefined) ? financials.EMPLOYEES_ANOMALOUS_PROBABILITY : null), financials.NET_ASSETS ?? null));
        }

        // If available add group structure
        if (groupStructure = companyDetails.GroupStructure) {
            this.addGroupStructureDetail(groupStructure.COMPANY_LEVEL, groupStructure.HOLDING_TYPE, groupStructure.PARENT_NAME, groupStructure.PARENT_NO, groupStructure.ULTIMATE_PARENT_NAME, groupStructure.ULTIMATE_PARENT_NO, groupStructure.PARENT_NATION, groupStructure.ULTIMATE_PARENT_NATION);
        }

        if ((fundingDetails = companyDetails.DealroomFunding) && Array.isArray(fundingDetails)) {
            this.addFundingDetails(fundingDetails);
        }

        if (personsWithSignificantControl = companyDetails.PersonsWithSignificantControl) {
            this.addPersonsWithSignificantControlDetails(personsWithSignificantControl);
        }

        if (shareHolders = companyDetails.ShareHolders) {
            this.addShareHolderDetails(shareHolders);
        }

        if (urlMatchStats = companyDetails.URLMatchStats) {
            this.addUrlMatchStatsDetails(urlMatchStats)
        }

        if (_360GivingFundingData = companyDetails._360GivingFunding) {
            this._360GivingFunding.add360GivingFunding(_360GivingFundingData);
        }

        if (innovateUkFunding = companyDetails.InnovateUKFunding) {
            this.innovateUkFunding.addInnovateUkFundingGrants(innovateUkFunding);
        }

        if (companyJobPostings = companyDetails.CompanyJobPostings) {
            this.companyJobPostings.processCompanyJobPostings(companyJobPostings);
        }

        if (companyJobPostingsBySkill = companyDetails.CompanyJobPostingsBySkill) {
            this.companyJobPostings.processCompanyJobPostingsBySkill(companyJobPostingsBySkill);
        }

        if (companyJobPostingsByLot = companyDetails.CompanyJobPostingsByLot) {
            this.companyJobPostings.processCompanyJobPostingsByLot(companyJobPostingsByLot);
        }

        if (companyJobPostingsOverTime = companyDetails.CompanyJobPostingsOverTime) {
            this.companyJobPostingsOverTime = companyJobPostingsOverTime;
        }

        if (companyRtics = companyDetails.RTICs) {
            this.processCompanyRtics(companyRtics);
        }

        if (companyCics = companyDetails.CICs) {
            this.processCompanyRtics(companyCics);
        }

        if (esgStatementAnchors = companyDetails.ESGStatementAnchors) {
            this.processESGStatementAnchors(esgStatementAnchors);
        }

        this.companyStatus = companyDetails.CompanyStatus;
        this.companyExports = companyDetails.CompanyExports;
        this.companyImports = companyDetails.CompanyImports;
    }

    getMaxLocationTotalOccupationCount(locationDetails) {
        return Math.max(...locationDetails.reduce((acc, el) => {
            if (el.OccupationCounts) {
                acc.push(el.OccupationCounts.reduce((occAcc, occ) => {
                    occAcc += occ.Count;
                    return occAcc;
                }, 0));
            };
            return acc;
        }, []));
    }

    getTotalOccupationCount(locationDetails) {
        return locationDetails.reduce((acc, el) => {
            if (el.OccupationCounts) {
                acc += el.OccupationCounts.reduce((occAcc, occ) => {
                    occAcc += occ.Count;
                    return occAcc;
                }, 0);
            }
            return acc;
        }, 0);
    }

    formatCompanyStatusStyle() {
        if (!this.active) {
            return "orange";
        } else if (this.companyStatus == "Active") {
            return "green";
        } else {
            return "";
        }
    }

    addCompanyStatusClass() {
        let companyStatusClass;
        switch (this.companyStatus) {
            case "Active":
                companyStatusClass = "company-status-active";
                break;
            case "Inactive":
                companyStatusClass = "company-status-inactive";
                break;
            default:
                companyStatusClass = "company-status-intermediate";
        }
        return companyStatusClass;
    }

    createCompanyPageElement(position, matchCount, orderResultsBy, userHasCicLists) {
        let companyHolder, topBar, topBarFieldsHolder, headlineFieldsFlexHolder, websiteDetails, websiteDetailsFields, extraFieldsHolder, fieldsFlexHolder, locationsAvailable, financialsForEmployeeCount;
        const companyObject = this;
        const currencyFormat = new Intl.NumberFormat('en-EN', { style: 'currency', maximumFractionDigits: 0, currency: 'GBP' });

        companyHolder = coreFunctions.createElementHelper("div", { class: "similarCompany", "data-companynumber": this.companyNumber, "data-companyname": this.companyName, "data-companyactive": Number(this.active) });

        companyHolder.appendChild(coreFunctions.createElementHelper("div", { innerHTML: `${position.toLocaleString()}${(matchCount > 0) ? ` / ${matchCount.toLocaleString()}` : ""}`, class: "numberPosition" })); // Position of company in list

        topBar = companyHolder.appendChild(coreFunctions.createElementHelper("div", { class: "similarCompaniesTopBar" }));

        const companyListName = topBar.appendChild(coreFunctions.createElementHelper("h3", {class: "company-list-name flex-row-holder align-center"}));
        companyListName.appendChild(this.addCompanyIcon());
        companyListName.appendChild(coreFunctions.createElementHelper("div", {innerHTML: this.companyName, class: "flex-grow"}));

        topBarFieldsHolder = topBar.appendChild(coreFunctions.createElementHelper("div", { class: "topBarFieldsHolder fieldHolderFlexContainer headlineFieldsFlexHolder" })); // Column fields in header

        topBarFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "companyNumber topCompanyDetails", innerHTML: `<span class='topCompanyDetailsText'>Company number: <a href="https://find-and-update.company-information.service.gov.uk/company/${this.companyNumber}" target="_blank">${this.companyNumber}</a></span>` })); // Company number with link to companies house
        topBarFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "topCompanyDetails", innerHTML: `<span class='topCompanyDetailsText'>Incorporation date: <span class='topCompanyDetailsValue'>${moment(this.incorporationDate).format("Do  MMMM YYYY")}</span></span>` })); // Incorporation date
        topBarFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "topCompanyDetails", innerHTML: `<span class='topCompanyDetailsText'>Registered postcode: <span class='topCompanyDetailsValue'>${this.registeredPostcode}</span></span>` })); // Registered postcode

        topBarFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "topCompanyDetails", innerHTML: `<span class='topCompanyDetailsText'>Company status: <span class='topCompanyDetailsValue ${this.formatCompanyStatusStyle()}'>${this.companyStatus}</span></span>`}));

        if (!this.active) {
            topBarFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "topCompanyDetails", innerHTML: `<span class='topCompanyDetailsText'>Last known active date: <span class='topCompanyDetailsValue orange'>${moment(this.lastKnownActiveDate).format("Do  MMMM YYYY")}</span></span>`}));
        }

        topBar.appendChild(coreFunctions.createElementHelper("div", { class: "similarCompaniesRightHolder" })).appendChild(coreFunctions.createElementHelper("div", { class: `similarCompaniesRightElement scoreRightElement`, innerHTML: `<span class='scoreFieldSmallWidth'>${(document.getElementById("sortByField")) ? document.getElementById("sortByField").options[document.getElementById("sortByField").selectedIndex].dataset.label : "Score"}:&nbsp;</span>${this.addCompanyScore(position, orderResultsBy)}` })); // Score element

        websiteDetails = companyHolder.appendChild(coreFunctions.createElementHelper("div", { class: "webpageDetailsHolder" }));

        websiteDetails.appendChild(this.addWebsiteImage());

        websiteDetailsFields = websiteDetails.appendChild(coreFunctions.createElementHelper("div", { class: "websiteDetailsFields" }));

        websiteDetailsFields.appendChild(this.createDataFieldHolder("Homepage_domain", "Website", this.formatCompanyUrl(), null)); // Webpage link

        websiteDetailsFields.appendChild(this.createDataFieldHolder("Company_description", "Description", this.companyDescription, null)); // Webpage description

        const companySectorsFlexHolder = companyHolder.appendChild(coreFunctions.createElementHelper("div", {class: "company-details-flex"}));

        companySectorsFlexHolder.appendChild(this.createDataFieldHolder("SICs", "SICs", ((this.isFieldAvailable(this.sics)) ? this.formatSicField(this.sics) : "Unavailable"), null)); // SICs

        if (this.isFieldAvailable(this.rsics))
            companySectorsFlexHolder.appendChild(this.createDataFieldHolder("RSICs", `RSICs${this.checkRsicsCertification()}`, (this.isFieldAvailable(this.rsics) ? this.formatSicField(this.rsics.map(el => el.sicCode)) : "Unavailable"), null)); // RSICs

        companySectorsFlexHolder.appendChild(this.createDataFieldHolder("RTICs", "RTICs", this.addCompanyRticsToPage(false)), null); // RTICs

        // CICs
        if (this.isFieldAvailable(this.cics)) {
            companySectorsFlexHolder.appendChild(this.createDataFieldHolder("CICs", "CICs", this.addCompanyRticsToPage(true), null));
        } else if (userHasCicLists) {
            companySectorsFlexHolder.appendChild(this.createDataFieldHolder("CICs", "CICs", "None" + this.addCompanyRticsToPage(true), null));
        }

        if (this.isFieldAvailable(this.industrialStrategySectors)) {
            companySectorsFlexHolder.appendChild(this.createDataFieldHolder("industrialStrategySectors", "Industrial strategy sectors", this.industrialStrategySectors.reduce((acc, element) => {acc += element.formatIndustrialStrategySectorDetails(); return acc}, ''))); // Format industrial strategy sectors
        }

        headlineFieldsFlexHolder = companyHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldHolderFlexContainer headlineFieldsFlexHolder" })); // Column fields holder

        const innovationScoreTooltip = `<p>Our proprietary Innovation Score is generated using ML trained on available R&D spend data. The model predicts whether a company is Innovative or Not Innovative, based on evidence of innovativeness in the company's webtext. The final score reflects our confidence in this prediction (3 being most confident).</p><p>Find out more ${this.formatTooltipLink("https://help.thedatacity.com/knowledge/innovation-score", "here", "_blank")}.</p>`;

        headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("InnovationScore", "Innovation score <span class='alphaDataPoint'>(ALPHA)</span>", ((this.isFieldAvailable(this.innovationScore)) ? this.formatInnovationScore() : "Unknown"), null, innovationScoreTooltip)); // Innovation score

        const companyGrowthTooltip = `<p>We use our company growth rates to estimate employees and to estimate turnover.</p><p>Read more ${this.formatTooltipLink("https://help.thedatacity.com/knowledge/estimated-turnover-employees-growth", "here", "_blank")}.</p>`;

        headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("BestEstimateTurnover", `Estimated turnover`, `${((this.isFieldAvailable(this.bestEstimateTurnover)) ? `<span class="estimate">${currencyFormat.format(this.bestEstimateTurnover)}</span>` : "Unknown")}`, null, companyGrowthTooltip)); // Estimated turnover

        headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("BestEstimateEmployees", `Estimated employee count`, `${((this.isFieldAvailable(this.bestEstimateEmployees)) ? `<span class="estimate">${this.bestEstimateEmployees.toLocaleString()}</span>` : "Unknown")}`, null, companyGrowthTooltip)); // Estimated employees


        // headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("BestEstimateGrowthPercentagePerYear", "Company growth per year", `${(this.bestEstimateGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Company growth

        // headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("BestEstimateEmployeeGrowthPercentagePerYear", "Employee growth per year", `${(this.bestEstimateEmployeeGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateEmployeeGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateEmployeeGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Employee growth

        // headlineFieldsFlexHolder.appendChild(this.createDataFieldHolder("BestEstimateTurnoverGrowthPercentagePerYear", "Turnover growth per year", `${(this.bestEstimateTurnoverGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateTurnoverGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateTurnoverGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Turnover growth

        // TODO change this be a link
        // companyHolder.appendChild(document.createElement("div")).appendChild(coreFunctions.createElementHelper("button", { class: "actionControl extra-fields", innerHTML: "<span class='iconRotate'>&nbsp;</span>Details" }, { click: function () { companyObject.toggleExtraCompanyFieldsDisplay(this) } })); // Show extra details control
        companyHolder.appendChild(document.createElement("div")).appendChild(coreFunctions.createElementHelper("a", { innerHTML: "<span>Full info</span>", class: "actionControl chevron-right companyPageLink", href: `${urlPath}/companypage?company_id=${this.companyNumber}`, target: "_blank" }));

        extraFieldsHolder = companyHolder.appendChild(coreFunctions.createElementHelper("div", { class: "extraFieldsHolder" }));

        companyHolder.append(this.createAlertsCompanyControl())

        return companyHolder;
    }

    checkRsicsCertification() {
        let rsicsCertification = "";
        if (this.isFieldAvailable(this.rsics)) {
            rsicsCertification = `<span class='rsic-certificate-holder' data-tooltip="Data City AI-assigned SIC code" data-tooltip_position="right"><img loading="lazy" class='rsic-certificate' src='${urlPath}/images/certified-pink.svg'/></span>`;
        }
        return rsicsCertification;
    }

    createAlertsCompanyControl() {
        const self = this;
        let alertsCompanyHolder, alertTitle, alertStatus;
        let companyAlert = false;

        alertsCompanyHolder = coreFunctions.createElementHelper("div", { class: "alertsCompanyHolder", "data-companynumber": this.companyNumber });

        alertTitle = "Follow this company";
        alertStatus = false;

        if(window.explore != undefined && window.explore.companyAlerts.companyAlerts.length > 0) {
            companyAlert = window.explore.companyAlerts.companyAlerts.find(alert => alert.companyNumber == this.companyNumber);
        } else if (window.mlList != undefined && window.mlList.mlList.companyAlerts.length > 0) {
            companyAlert = window.mlList.mlList.companyAlerts.find(alert => alert.companyNumber == this.companyNumber);
        }

        if(companyAlert) {
            console.log(companyAlert);
            alertTitle = "Unfollow this company";
            alertStatus = true;
            alertsCompanyHolder.classList.add("alertsCompanyHolder-active");
        }

        alertsCompanyHolder.appendChild(coreFunctions.createElementHelper("input", { type: "checkbox", "data-companynumber": this.companyNumber, id: `alertCompanyToggle-${this.companyNumber}`, "data-alertstatus": alertStatus, checked: alertStatus }, { change: function () { self.toggleCompanyAlert(this, self.companyNumber) } }));
        alertsCompanyHolder.appendChild(coreFunctions.createElementHelper("label", { innerHTML: "&nbsp;", for: `alertCompanyToggle-${this.companyNumber}`, title: alertTitle }));
        return alertsCompanyHolder;
    }

    toggleCompanyAlert(element, companyNumber) {
        const self = this;
        const companyAlert = new CompanyAlert();
        if (element.getAttribute("data-alertstatus") == 'true') {
            companyAlert.unsubscribeFromCompanyAlert(companyNumber, function () {
                element.title = "Follow this company";
                element.setAttribute("data-alertstatus", false);
                element.parentElement.classList.remove("alertsCompanyHolder-active");
            });
        } else {
            companyAlert.subscribeToCompanyAlert(companyNumber, function () {
                element.title = "Unfollow this company";
                element.setAttribute("data-alertstatus", true);
                element.parentElement.classList.add("alertsCompanyHolder-active");
            }, (error) => {
                element.title = "Follow this company";
                element.setAttribute("data-alertstatus", false);
                element.parentElement.classList.remove("alertsCompanyHolder-active");
                element.checked = false;
            });
        }
    }

    formatTooltipLink(url, anchor, target) {
        return `<a href='${url}' target='${target}'>${anchor}</a>`;
    }

    formatEstimatesData(field, dataType) {
        let currentYear, content, measuredEntry, estimatedEntry;
        const dataFormat = (dataType == "currency") ? new Intl.NumberFormat('en-EN', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0}) : new Intl.NumberFormat("ee-EE", {maximumFractionDigits: 0, roundingMode: "ceil"});
        content = "";
        currentYear = new Date().getFullYear();
        if (this.isFieldAvailable(field)) {
            if (measuredEntry = this[field].findLast(element => element.Measure)) {
                content += `<br>Measured: ${dataFormat.format(measuredEntry.Measure)} (${measuredEntry.Year})`;
            }
            if (estimatedEntry = this[field].findLast(element => (element.Estimated != undefined && element.Year <= currentYear))) {
                content += `<br>Estimated: ${dataFormat.format(estimatedEntry.Estimated)} (${estimatedEntry.Year})`;
            }
        }
        return content;
    }

    processCompanyRtics(verticals) {
        const companyProfileObject = this;
        if (verticals != undefined) {
            verticals.forEach(vertical => companyProfileObject.newCompanyVertical(vertical.CompanyNumber, vertical.RTICCode, vertical.RTICName, vertical.ParentCode, vertical.ParentName, vertical.CIC));
        }
    }

    processESGStatementAnchors(esgStatementAnchors) {
        if (esgStatementAnchors && Array.isArray(esgStatementAnchors)) {
            esgStatementAnchors.forEach(element => this.esgStatementAnchors.push(new EsgStatementAnchor(element.Domain, element.Anchor, element.Word)));
        }
        return this;
    }

    addEmailMatchElement(email) {
        let matchedDirector = this.matchEmailToDirector(email);
        if (matchedDirector) {
            return `<span class="emailMatchElement"> - matches ${matchedDirector.role.toLowerCase()} <a href="${urlPath}/directors?person_number=${matchedDirector.personNumber}" target="_blank">${matchedDirector.forenames.split(" ")[0]} ${matchedDirector.surname}</a></span>`;
        } else {
            return "";
        }
    }

    matchEmailToDirector(email) {
        let emailUser, filteredDirectors, matchedDirector;
        // Remove anything that is not text from email user
        emailUser = email.toLowerCase().split("@")[0].replace(/[^a-z]/g, "");
        if (this.directorsAvailable()) {
            // Filter by surname
            filteredDirectors = this.directors.filter(director => {
                return emailUser.includes(director.surname.toLowerCase());
            });

            if (filteredDirectors.length > 0) {
                // match format johnsmith or jsmith
                matchedDirector = filteredDirectors.find(director => {
                    let emailUserFirstname, firstname;
                    emailUserFirstname = emailUser.split(director.surname.toLowerCase())[0];
                    firstname = (director.forenames ?? "").split(" ")[0].toLowerCase();
                    return (emailUserFirstname.length > 0 && firstname.startsWith(emailUserFirstname));
                });
            } else {
                matchedDirector = this.directors.find(director => {
                    let firstname = (director.forenames ?? "").split(" ")[0].toLowerCase();
                    return emailUser == firstname;
                });
            }

        }
        return matchedDirector;
    }

    addCompanyExtraDetails() {
        const companyObject = this;
        let extraFieldsHolder, fieldsFlexHolder, locationsAvailable, financialsForEmployeeCount, employeeEstimatesFieldHolder;
        extraFieldsHolder = document.querySelector(`.similarCompany[data-companynumber='${this.companyNumber}'] .extraFieldsHolder`);

        if (!extraFieldsHolder.classList.contains("extraDetailsAdded")) {
            extraFieldsHolder.classList.add("extraDetailsAdded");

            extraFieldsHolder.appendChild(this.createDataFieldHolder("EmailAddresses", "Emails", ((this.isFieldAvailable(this.emailAddresses)) ? this.emailAddresses.reduce(function (html, email) { html += `<a href="mailto:${email}">${email}</a>${companyObject.addEmailMatchElement(email)}<br>`; return html; }, '') : "Unknown"), "email_white.svg")); // Email addresses

            extraFieldsHolder.appendChild(this.createDataFieldHolder("RegisteredAddress", "Registered address", this.registeredAddress, "office_white.svg")); // Address

            extraFieldsHolder.appendChild(this.createDataFieldHolder("PhoneNumbers", "Telephone", (this.isFieldAvailable(this.phoneNumbers) ? this.phoneNumbers.reduce(function (html, phone) { html += `<a href="tel:${phone}">${phone}</a><br>`; return html; }, '') : "Unknown"), "phone_white.svg")); // Phone number

            extraFieldsHolder.appendChild(this.createDataFieldHolder("Social", "Social", this.formatSocialDetails(), "social_white.svg")); // Social media

            // Add group stucture details if available for company
            if (this.groupStructureAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("groupStructure", "Group structure", this.createGroupStructureDetailsContent(), null));
            }

            // Add funding details for company where available
            if (this.fundingDataAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("fundingDetails", `Funding<a class="dealroom" href='https://dealroom.co/' target='_blank'><img src='${urlPath}/images/dealroom-logo-white.svg' loading='lazy'/></a>`, null, null)).appendChild(this.createFundingDetailsContent());
            }

            extraFieldsHolder.appendChild(this.createDataFieldHolder("SimilarCompanies", "Similar companies <span class='alphaDataPoint'>(ALPHA)</span>", ((this.isFieldAvailable(this.similarCompanies)) ? this.formatSimilarCompanies() : "Unknown"), null));  // Similar company

            extraFieldsHolder.appendChild(this.createDataFieldHolder("BestEstimateGrowthPercentagePerYear", "Estimated company growth per year", `${(this.bestEstimateGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Company growth

            extraFieldsHolder.appendChild(this.createDataFieldHolder("CompanyGrowthStageEstimate", "Company size estimate", `${(this.companyGrowthStageEstimate != undefined) ? this.companyGrowthStageEstimate : 'Unknown'}`, null)); // Company growth

            // extraFieldsHolder.appendChild(this.createDataFieldHolder("BestEstimateEmployeeGrowthPercentagePerYear", "Employee growth per year", `${(this.bestEstimateEmployeeGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateEmployeeGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateEmployeeGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Employee growth

            // extraFieldsHolder.appendChild(this.createDataFieldHolder("BestEstimateTurnoverGrowthPercentagePerYear", "Turnover growth per year", `${(this.bestEstimateTurnoverGrowthPercentagePerYear != undefined) ? `<span class='${(this.bestEstimateTurnoverGrowthPercentagePerYear < 0) ? 'negativePercentage' : 'positivePercentage'}'>${(this.bestEstimateTurnoverGrowthPercentagePerYear * 100).toFixed(1)}%</span>` : 'Unknown'}`, null)); // Turnover growth

            extraFieldsHolder.appendChild(this.createDataFieldHolder("TonnesOfC02equivGHGPerYear", "Estimated greenhouse gas emissions (tonnes of CO<sub>2</sub> equivalent per year)", ((this.isFieldAvailable(this.tonnesOfC02equivGHGPerYear)) ? this.tonnesOfC02equivGHGPerYear : "Unknown"), null)); // Greenhouse gas emissions

            fieldsFlexHolder = extraFieldsHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldHolderFlexContainer" })); // Column fields holder

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("CompanyCategory", "Company category", ((this.isFieldAvailable(this.companyCategory)) ? this.companyCategory : "Unknown"), null)); // Company category

            // Location details
            locationsAvailable = this.isFieldAvailable(this.locations);

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("OECDFUACorePlusCommuting", "OECD Cities", ((locationsAvailable) ? this.getCompanyLocationsByField("oecdfuaCorePlusCommuting") : "Unavailable"), null)); // Cities

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("LaName", "Local authorities", ((locationsAvailable) ? this.getCompanyLocationsByField("laName") : "Unavailable"), null)); // Local authorities

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("PostcodeWithSpaces", "Postcodes", ((locationsAvailable) ? this.getCompanyLocationsByField("postcodeWithSpaces") : "Unavailable"), null)); // Postcodes

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("SICs", "SICs", ((this.isFieldAvailable(this.sics)) ? this.formatSicField(this.sics) : "Unavailable"), null)); // SICs

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("SICLUs", "SICLUs", ((this.isFieldAvailable(this.sics)) ? this.formatSicField(this.sichlus) : "Unavailable"), null)); // SICHLUs

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("SectorKeywords", "Sector keywords", ((this.isFieldAvailable(this.sectorKeywords)) ? this.sectorKeywords.join(", ") : "Unavailable"), null)); // sector keywords

            fieldsFlexHolder.appendChild(this.createDataFieldHolder("InnovationKeywords", "Innovation keywords", ((this.isFieldAvailable(this.innovationKeywords)) ? this.innovationKeywords.join(", ") : "Unavailable"), null)); // innovation keywords

            if (this.employmentEstimatesDataAvailable()) {
                employeeEstimatesFieldHolder = extraFieldsHolder.appendChild(this.createDataFieldHolder("EmployeeEstimates", "Employment estimates", null, null))
                employeeEstimatesFieldHolder.querySelector(".fieldDataHolder").appendChild(this.createEmploymentEstimatesLineChartHolder());
                this.drawEmploymentEstimatesLineChart();
            }

            if (this.directorsAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("Directors", "Directors", this.createDirectorsHolder().outerHTML, null)); // Directors
            }
            // Add people with significant control if available
            if (this.personsWithSignificantControl.personsWithSignificantControlAvailable()) {
                extraFieldsHolder.appendChild(this.personsWithSignificantControl.createPersonsWithSignificantControlElement())
            }

            // Add shareholder details if available
            if (this.shareHolders.shareHoldersAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("shareHolders", "Shareholders", null, null)).appendChild(this.createPaginatedTable(this.shareHolders));
            }

            // Add per round Dealroom funding if data is available
            if (this.perRoundFundingAvailable()) {
                extraFieldsHolder.appendChild(this.createPerRoundFundingElement());
            }

            extraFieldsHolder.appendChild(this.addCompanyFinancialsToElement()); // Company financials

            if (this._360GivingFunding.grantFundingAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("_360GivingFunding", "<a href='https://grantnav.threesixtygiving.org/' target='_blank'>360 Giving funding grants</a>", null, null)).appendChild(this.createPaginatedTable(this._360GivingFunding));
            }

            if (this.innovateUkFunding.innovateUkFundingGrantsAvailable()) {
                extraFieldsHolder.appendChild(this.createDataFieldHolder("innovateUkFunding", "Innovate UK funding grants", null, null)).appendChild(this.createPaginatedTable(this.innovateUkFunding));
            }

            if (this.companyJobPostings.companyJobPostingsAvailable()) {
                extraFieldsHolder.appendChild(this.companyJobPostings.createCompanyJobPostingsElement());
            }

            if (this.companyJobPostings.companyJobPostingsBySkillAvailable()) {
                extraFieldsHolder.appendChild(this.companyJobPostings.createCompanyJobPostingsBySkillElement());
            }

            extraFieldsHolder.appendChild(coreFunctions.createElementHelper("a", { innerHTML: "<span>Full info</span>", class: "actionControl chevron-right companyPageLink", href: `${urlPath}/companypage?company_id=${this.companyNumber}`, target: "_blank" }));

        }

        return this;

    }

    groupStructureAvailable() {
        return (this.groupCompanyLevel != undefined);
    }

    directorsAvailable() {
        return this.directors.length > 0;
    }

    addGroupStructureDetail(groupCompanyLevel, groupHoldingType, groupParentName, groupParentCompanyNumber, groupUltimateParentName, groupUltimateParentCompanyNumber, groupParentNation, groupUltimateParentNation) {
        this.groupCompanyLevel = groupCompanyLevel;
        this.groupHoldingType = groupHoldingType;
        this.groupParentName = groupParentName;
        this.groupParentCompanyNumber = groupParentCompanyNumber;
        this.groupParentNation = groupParentNation;
        this.groupUltimateParentName = groupUltimateParentName;
        this.groupUltimateParentCompanyNumber = groupUltimateParentCompanyNumber;
        this.groupUltimateParentNation = groupUltimateParentNation;
    }

    addPersonsWithSignificantControlDetails(personsWithSignificantControl) {
        const companyObject = this;
        personsWithSignificantControl.forEach(person => companyObject.personsWithSignificantControl.processPersonWithSignificantControl(person.CompanyNumber, person.Name, person.DoB, person.CountryOfResidence, ((person.Type) ? coreFunctions.capitaliseFirstLetter(person.Type.replace(/-/g, " ")) : null), ((person.NatureOfControl) ? coreFunctions.capitaliseFirstLetter(person.NatureOfControl.replace(/-/g, " ")) : null)));
    }

    addShareHolderDetails(shareHolders) {
        const companyObject = this;
        shareHolders.forEach(shareHolder => companyObject.shareHolders.addNewShareHolder(shareHolder.COMPANY_REG, shareHolder.SHARE_TYPE, shareHolder.SHARE_HOLDER_NAME, shareHolder.ALL_INFO, shareHolder.TOTAL_SHARES, shareHolder.CURRENCY, shareHolder.TOTAL_SHARE_VALUE, shareHolder.SHARE_PERCENT));
        return this;
    }

    addUrlMatchStatsDetails(urlMatchStats) {
        this.urlMatchStats = new UrlMatchStats(urlMatchStats.MatchedWebsite, urlMatchStats.MatchedWebsiteScore, urlMatchStats.MatchedWebsiteReasoning, urlMatchStats.MatchedWebsiteSources);
        return this;
    }

    createGroupStructureElement() {
        let groupStructureHolder, groupStructureContent;

        groupStructureHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "groupStructure" });

        groupStructureContent = groupStructureHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));

        groupStructureContent.appendChild(coreFunctions.createElementHelper("h4", { innerHTML: "Group structure" }));

        groupStructureContent.appendChild(coreFunctions.createElementHelper("div", { class: "fieldDataHolder", "data-field": "groupStructure", innerHTML: this.createGroupStructureDetailsContent() }));

        return groupStructureHolder;
    }

    createGroupStructureDetailsContent() {
        return `<div><span class="groupStructureField">Group level:</span> ${this.groupCompanyLevel}</div><div><span class="groupStructureField">Parent company:</span> ${this.groupHoldingType} ${this.groupParentName} ${this.addGroupStructureCompanyNumberElement(this.groupParentCompanyNumber)}</div><div><span class="groupStructureField">Parent company nation:</span> ${this.checkGroupStructureNationality(this.groupParentNation, this.groupParentCompanyNumber)}</div><div><span class="groupStructureField">Ultimate parent company:</span> ${this.groupUltimateParentName} ${this.addGroupStructureCompanyNumberElement(this.groupUltimateParentCompanyNumber)}</div><div><span class="groupStructureField">Ultimate parent company nation:</span> ${this.checkGroupStructureNationality(this.groupUltimateParentNation, this.groupUltimateParentCompanyNumber)}</div>`;
    }

    // Check nation value, see if it is UK
    checkGroupStructureNationality(nation, companyNumber) {
        if (nation.length > 0) {
            return nation;
        } else if (this.validatedUkCompanyNumber(companyNumber)) {
            return "UK";
        } else {
            return "Unknown";
        }
    }

    addGroupStructureCompanyNumberElement(companyNumber) {
        if (this.validatedUkCompanyNumber(companyNumber)) {
            return `<a target="_blank" href="${urlPath}/companypage?company_id=${companyNumber}">${companyNumber}</a>`;
        } else {
            return ` - ${companyNumber}`;
        }
    }

    // Check company number matches UK format
    validatedUkCompanyNumber(companyNumber) {
        return (companyNumber.match(/^(AC|EN|ES|FC|GE|GN|GS|IC|IP|LP|NA|NC|NF|NI|NL|NO|NP|NR|NV|NZ|OC|RC|R0|SA|SC|SE|SF|SI|SL|SO|SP|SR|SZ|ZC|[0-9]{2})[0-9]{6}$/));
    }

    addFundingDetails(fundingDetails) {
        const self = this;
        fundingDetails.forEach(funding => self.newDealroomFunding(funding));
    }

    newDealroomFunding(funding) {
        let newFunding = new DealroomFunding(funding.CompanyNumber, funding.RoundAmount, funding.RoundDate, funding.RoundType, funding.RoundVerified, funding.Spinout, funding.RoundInvestorName, funding.ContributeToTotalFundingAmount);
        this.fundingDetails.push(newFunding);
        return newFunding;
    }

    perRoundFundingAvailable() {
        return this.fundingDetails.length > 0;
    }

    createPerRoundFundingElement() {
        let fieldHolder, fieldContentWrapper, tableHolder;
        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "fundingDetails", "data-companynumber": this.companyNumber });
        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldContentWrapper.appendChild(coreFunctions.createElementHelper("h4", { innerHTML: `Per round funding<a class="dealroom" href='https://dealroom.co/' target='_blank'><img src='${urlPath}/images/dealroom-logo-white.svg' loading='lazy'/></a>`}));
        tableHolder = fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", {class: "fundingDetailsTableHolder"}));
        tableHolder.appendChild(this.createPerRoundFundingTable());
        return fieldHolder;
    }

    createPerRoundFundingTable() {
        let perRoundFundingTable, perRoundFundingTbody;
        perRoundFundingTable = coreFunctions.createElementHelper("table", {class: "shareHoldersTable", "data-companynumber": this.companyNumber});
        this.fundingDetails.sort(this.sortPerRoundFundingByDate).forEach((fundingRound, index) => {
            if (index == 0) {
                perRoundFundingTable.appendChild(fundingRound.addTableHeadings());
                perRoundFundingTbody = perRoundFundingTable.appendChild(coreFunctions.createElementHelper("tbody"));
            }
            perRoundFundingTbody.appendChild(fundingRound.createFundingTableRow());
        });
        return perRoundFundingTable;
    }

    sortPerRoundFundingByDate(a, b) {
        return (moment(a.roundDate).isAfter(b.roundDate)) ? 1 : ((moment(a.roundDate).isBefore(b.roundDate)) ? -1 : 0);
    }

    fundingDataAvailable() {
        return (this.lastFundingDate != undefined);
    }

    createFundingDetailsContent() {
        let fundingDetailsHolder = document.createElement("div");
        fundingDetailsHolder.appendChild(coreFunctions.createElementHelper("div", {innerHTML: `<span class="fundingDetailsField ">Estimated total funding: </span>${(this.totalFundingEuroMillions) ? `&pound;${(this.totalFundingEuroMillions * 1000000).toLocaleString()}` : "N/A"}`}));
        fundingDetailsHolder.appendChild(coreFunctions.createElementHelper("div", {innerHTML: `<span class="fundingDetailsField ">Last funding date: </span>${moment(this.lastFundingDate, "MMM/YYYY").format("MMMM YYYY")}`}));
        if (this.dealroomIsSpinout != undefined) {
            fundingDetailsHolder.appendChild(coreFunctions.createElementHelper("div", {innerHTML: `<span class="fundingDetailsField ">Is spinout?: </span><img src='${(this.dealroomIsSpinout == true) ?  `${urlPath}/images/tick-lightgray.svg` : `${urlPath}/images/cross-lightgray.svg`}'/>`}));
        }
        return fundingDetailsHolder;
    }

    checkDealroomFundingInPeriod(startDateParsed) {
        return (this.fundingDataAvailable() && this.fundingDetails.find(el => moment(el.roundDate, "YYYY-MM-DD").isSameOrAfter(startDateParsed)) != undefined);
    }

    formatSimilarCompanies() {
        let similarCompanyNumbers, similarCompaniesContent, totalSimilarCompanies;
        similarCompaniesContent = "";
        similarCompanyNumbers = this.similarCompanies.map(element => element.CompanyTwo);
        totalSimilarCompanies = similarCompanyNumbers.length;
        similarCompanyNumbers.forEach((companyNumber, index) => similarCompaniesContent += `<a target="_blank" href="${urlPath}/companypage?company_id=${companyNumber}">${companyNumber}${(index == (totalSimilarCompanies - 1)) ? "" : ", "}</a>`);
        return similarCompaniesContent;

    }

    formatInnovationScore() {
        let innovationScoreContent = "";
        innovationScoreContent += `<img class="innovationScoreStar" src='${urlPath}/images/${(this.innovationScore > 0) ? "star-filled-white.svg" : "star-outline-gray.svg"}'>`;
        innovationScoreContent += `<img class="innovationScoreStar" src='${urlPath}/images/${(this.innovationScore >= 1.5) ? "star-filled-white.svg" : "star-outline-gray.svg"}'>`;
        innovationScoreContent += `<img class="innovationScoreStar" src='${urlPath}/images/${(this.innovationScore >= 3) ? "star-filled-white.svg" : "star-outline-gray.svg"}'>`;
        return innovationScoreContent;
    }

    toggleExtraCompanyFieldsDisplay(element) {
        let companyExtraDetailsHolder = element.closest(".similarCompany").querySelector(".extraFieldsHolder");
        if (element.classList.contains("extraFieldsVisible")) {
            element.classList.remove("extraFieldsVisible");
            companyExtraDetailsHolder.classList.remove("extraFieldsVisible");
        } else {
            this.addCompanyExtraDetails();
            element.classList.add("extraFieldsVisible");
            companyExtraDetailsHolder.classList.add("extraFieldsVisible");
        }
    }

    formatPercentageScore(field) {
        const scoreElement = document.createElement("span");
        if (this[field]) {
            const percentFormat = new Intl.NumberFormat('en', {style: 'percent',maximumSignificantDigits: 3, minimumSignificantDigits: 2 });
            const isNegativeValue = this[field] < 0;
            scoreElement.innerHTML = percentFormat.format(this[field]);
            scoreElement.classList.add((isNegativeValue) ? 'negativePercentage' : 'positivePercentage');
        } else {
            scoreElement.innerHTML = "N/A";
        }
        const scoreContent = scoreElement.outerHTML;
        return scoreContent;
    }

    formatTotalLightcastJobs() {
        let score = "nbsp;";
        if (this.companyJobPostingsOverTime) {
            // Consider data from the last 5 years
            const startTimestamp = parseInt(moment().subtract(5, "years").format("X"));
            const total = this.companyJobPostingsOverTime.CompanyJobPostingsOverTimeBySoc4.filter(element => element.Timestamp >= startTimestamp).reduce((accumulator, element) => accumulator + element.Count, 0);
            score = total.toLocaleString();
        };
        return score;
    }

    addCompanyScore(position, orderResultsBy) {
        let score;
        switch (orderResultsBy) {
            case "CompanyName":
                score = "&nbsp;";
                break;
            case "Employees":
                score = this.bestEstimateEmployees.toLocaleString();
                break;
            case "Keywords":
                score = position;
                break;
            case "SectorKeywordCount":
                score = (Array.isArray(this.sectorKeywords)) ? this.sectorKeywords.length : "N/A";
                break;
            case "IncorporationDate":
                score = moment(this.incorporationDate).format("YYYY-MM-DD");
                break;
            case "LastKnownActiveDate":
                score = moment(this.lastKnownActiveDate).format("YYYY-MM-DD");
                break;
            case "Innovation":
                score = this.formatInnovationScore();
                break;
            case "Turnover":
                score = `£${this.bestEstimateTurnover.toLocaleString()}`;
                break;
            case "Score":
                score = "&nbsp;";
                break;
            case "TurnoverGrowth":
                score = this.formatPercentageScore("bestEstimateTurnoverGrowthPercentagePerYear");
                break;
            case "EmployeeGrowth":
                score = this.formatPercentageScore("bestEstimateEmployeeGrowthPercentagePerYear");
                break;
            case "BestGrowth":
                score = this.formatPercentageScore("bestEstimateGrowthPercentagePerYear");
                break;
            case "TotalInnovateUKGrants":
                score = `£${this.innovateUkFunding.elements.reduce((sum, element) => sum + element.awardOffered, 0).toLocaleString()}`;
                break;
            case "Lightcast":
                score = this.formatTotalLightcastJobs();
                break;
            default:
                score = "&nbsp;";
        }
        return score;
    }

    createDataFieldHolder(field, label, content, icon, tooltip) {
        let fieldHolder, fieldIcon, fieldHeading, fieldContentWrapper, fieldContentLink, reportMismatchedRticButton, suggestRticButton, suggestCicButton;
        const companyObject = this;

        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": field });
        if (icon) {
            fieldIcon = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldIcon" }));
            fieldIcon.appendChild(coreFunctions.createElementHelper("img", { src: `${urlPath}/images/${icon}` }))
        }

        // Add holder for screenshot of website in description field
        if (field == "Company_description") {
            //     fieldHolder.appendChild(this.addWebsiteImage());
        }

        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldHeading = coreFunctions.createElementHelper("h4", { innerHTML: label });
        if (tooltip) {
            fieldHeading.appendChild(this.createTooltipElement(tooltip, field));
        }
        fieldContentWrapper.appendChild(fieldHeading);
        if (field == "Homepage_domain") {
            fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", { class: "fieldDataHolder", "data-field": field, innerHTML: content })).appendChild(this.addMismatchedUrlReportControl());
            if (fieldContentLink = fieldContentWrapper.querySelector('a')) {
                fieldContentLink.addEventListener("click", this.companyUrlVisited.bind(this));
            }
        } else {
            fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", { class: "fieldDataHolder", "data-field": field, innerHTML: content }));
        }

        if (field == "RTICs" || field == "CICs") {
            const reportBtn = fieldContentWrapper.querySelectorAll(".reportMismatchedRticButton");
            reportBtn.forEach(button => {
                button.setAttribute("data-companyname", companyObject.companyName);
                button.setAttribute("data-list_type", (field == "RTICs") ? "rtic" : "cic");
                button.addEventListener("click", function () {
                    companyObject.reportMismatchedRticButtonClick(this);
                });
            });

            if (suggestRticButton = fieldContentWrapper.querySelector("button.suggestRticButton")) {
                suggestRticButton.setAttribute("data-companyname", companyObject.companyName);
                suggestRticButton.addEventListener("click", function () {
                    companyObject.suggestRticsButtonClick(this);
                });
            }

            if (suggestCicButton = fieldContentWrapper.querySelector("button.suggestCicButton")) {
                suggestCicButton.setAttribute("data-companyname", companyObject.companyName);
                suggestCicButton.addEventListener("click", function () {
                    companyObject.suggestCicsButtonClick(this);
                });
            }
        }

        return fieldHolder;
    }

    createTooltipElement(tooltip, field) {
        let holder = coreFunctions.createElementHelper("span", {class: "tooltipExplanationHolder"});
        holder.appendChild(coreFunctions.createElementHelper("input", {type: "checkbox", id: `${this.companyNumber}_${field}_explanation`}));
        holder.appendChild(coreFunctions.createElementHelper("label", {for: `${this.companyNumber}_${field}_explanation`, innerHTML: `<span class="questionMark">?</span><span class="tooltipExplanation">${tooltip}</span>`}));
        return holder;
    }

    // Create a new director and save it in the collection
    newCompanyDirector(personNumber, forenames, surname, role, appointedOn, resignationDate) {
        let director = new Director(personNumber, forenames, surname, role, appointedOn, resignationDate);
        this.directors.push(director);
        return director;
    }

    // Create a new location and save it to the collection
    newCompanyLocation(postcodeWithSpaces, laName, constituency, lepName, nuts1Name, registeredPostcode, standardisedCity, latitude, longitude, oecdfuaCorePlusCommuting, occupationCounts, maxLocationTotalOccupationCount) {
        let location = new CompanyLocation(postcodeWithSpaces, laName, constituency, lepName, nuts1Name, registeredPostcode, standardisedCity, latitude, longitude, oecdfuaCorePlusCommuting, occupationCounts, maxLocationTotalOccupationCount);
        this.locations.push(location);
        return location;
    }

    // Create new financial details and save to the collection
    newFinancials(year, turnover, preTaxProfit, profitAfterTax, cash, totalCurrentAssets, totalAssets, totalLiabilities, shareholderFunds, netWorth, numberOfEmployees, debtorsDueAfterOneYear, debtors, tradeDebtors, groupDebtors, creditors, exportTotal, wagesAndSalaries, depreciation, bankOverdraft, ebitda, auditors, declaredEmployeesAnomalous, employeesAnomalousProbability, netAssets) {
        let financials = new Financials(year, turnover, preTaxProfit, profitAfterTax, cash, totalCurrentAssets, totalAssets, totalLiabilities, shareholderFunds, netWorth, numberOfEmployees, debtorsDueAfterOneYear, debtors, tradeDebtors, groupDebtors, creditors, exportTotal, wagesAndSalaries, depreciation, bankOverdraft, ebitda, auditors, declaredEmployeesAnomalous, employeesAnomalousProbability, netAssets);
        this.financials.push(financials);
        return financials;
    }

    // Add new RTIC for company
    newCompanyVertical(companyNumber, rticCode, rticName, rticSectorCode, rticSectorName, cic) {
        let companyRtics = new CompanyRtic(companyNumber, rticCode, rticName, rticSectorCode, rticSectorName, cic);
        this[(cic === true) ? "cics" : "rtics"].push(companyRtics);
        return companyRtics;
    }

    // Check whether data available for a field
    isFieldAvailable(value) {
        if (Array.isArray(value) && value.length > 0) {
            return true;
        } else if (value && value.toString().length > 0) {
            return true;
        } else {
            return false;
        }
    }

    formatCompanyUrl() {
        if (this.isFieldAvailable(this.companyURL)) {
            let companyUrlContent = "";
            companyUrlContent += `<a href="${(this.companyURL.startsWith("http")) ? "" : "http://"}${this.companyURL}" target="_blank" data-visited="${this.checkCompanyUrlVisited()}" data-confidence="${this.getCompanyUrlConfidence}">${this.companyURL}</a>`;
            if (this.isFieldAvailable(this.matchedWebsiteScore)) {
                const confidenceGroup = this.urlMatchConfidenceThresholds.reduce((max, element) => {
                    if (this.matchedWebsiteScore == 200) {
                        return this.urlMatchConfidenceThresholds[1];
                    }
                    return (this.matchedWebsiteScore >= element.scoreLowerThreshold) ? element : max;
                }, this.urlMatchConfidenceThresholds[0]);
                companyUrlContent += `<span class='url-match-confidence' data-tooltip='${confidenceGroup.description}' data-tooltip_position='bottom'><img src='${urlPath}/images/gauge-${confidenceGroup.confidence}-white.png' loading='lazy'/></span>`;
            }
            return companyUrlContent;
        } else {
            return "Unavailable";
        }
    }

    companyUrlVisited() {
        if (this.parent) {
            this.parent.addToCompanyUrlsVisited(this.companyURL);
        }
    }

    checkCompanyUrlVisited() {
        return (this.parent && this.parent.companyUrlsVisited.includes(this.companyURL)) ? 1 : 0;
    }

    // Return a formatted string of all available social details
    formatSocialDetails() {
        const companyDetailsClass = this;
        let socialFieldsContent;
        const socialFields = ["facebook", "twitter", "instagram", "youtube", "linkedin"];
        const socialNames = ["Facebook", "Twitter", "Instagram", "Youtube", "LinkedIn"]

        socialFieldsContent = "";
        // Is particular field is availble format sting with link
        socialFields.forEach((field, index) => (this.isFieldAvailable(this[field])) ? socialFieldsContent += `${socialNames[index]}: <a href="${this[field]}" target="_blank">${(field == "twitter") ? companyDetailsClass.formatTwitterLink(this[field]) : this[field]}</a><br>` : "");
        if (socialFieldsContent.length == 0) {
            socialFieldsContent = "Unknown";
        }
        return socialFieldsContent;
    }

    createSocialDetailsCompact() {
        let socialDetailsHolder;
        const socialFields = ["facebook", "twitter", "instagram", "youtube", "linkedin"];
        socialFields.forEach(field => {
            if (this.isFieldAvailable(this[field])) {
                if (!socialDetailsHolder) {
                    socialDetailsHolder = coreFunctions.createElementHelper("div", {class: "social-links-holder"});
                }
                socialDetailsHolder.appendChild(coreFunctions.createElementHelper("a", {innerHTML: "&nbsp;", href: coreFunctions.formatUrl(this[field]), class: "social-link", "data-socialfield": field}));
            }
        });
        return socialDetailsHolder;
    }

    formatTwitterLink(link) {
       return `@${link.slice(link.lastIndexOf('/') + 1)}`;
    }

    // Return formatted details of SIC code
    formatSicField(values) {
        const sicEntries = [];
        values.forEach((sic) => {
            // From SIC code, add description
            const sicDescription = sicCodes.find(entry => entry.code == sic);
            sicEntries.push(`<div class="classification-item">`);
            sicEntries.push(sicDescription ? `<span class="classification-code">${sic}: </span><span class="classification-desc">${sicDescription.description}</span>` : `<span class="classification-code">${sic}</span>`);
            sicEntries.push(`</div>`);
        });
        return sicEntries.join("");
    }

    formatTradeDataCodeDescription(code) {
        return tradeDataLookupCodes[code] ? tradeDataLookupCodes[code].description : "No description available";
    }

    createTradeDataTomSelectSearch(id, data, field) {
        const options = data.map(element => this.createTradeDataTomSelectOption(element, field));
        new TomSelect(`#${id}`, {
            options: options,
            labelField: "name",
            searchField: ["name", "value"],
            openOnFocus: true,
            closeAfterSelect: true,
            onItemAdd: (value, $item) => this.tradeDataOnItemAdd(value, $item.dataset.field),
            placeholder: "Search company trade codes...",
            hidePlaceholder: false,
            plugins: {
                remove_button:{
                    title:'Reset search',
                }
            },
            render: {
                option: function(data, escape) {
                    return `<div class="trade-code-option" data-code="${escape(data.value)}">
                                <div class="trade-code-value">${escape(data.value)}</div>
                                <div class="trade-code-description">${escape(data.name)}</div>
                            </div>`
                },
                item: function(data, escape) {
                    return `<div class="trade-code-item" data-field="${escape(data.field)}">${escape(data.value)}</div>`;
                }
            },
            onDelete: () => this.tradeDataSearchReset(field)
        });
        return this;
    }

    tradeDataOnItemAdd(value, field) {
        const holder = document.querySelector(`tbody[data-field='${field}']`);
        holder.querySelectorAll("tr").forEach(row => row.hidden = !(row.dataset.code == value));
    }

    tradeDataSearchReset(field) {
        const holder = document.querySelector(`tbody[data-field='${field}']`);
        holder.querySelectorAll("tr").forEach(row => row.hidden = false);
    }

    createTradeDataTomSelectOption(element, field) {
        return {
            value: element.HS6,
            field: field,
            name: this.formatTradeDataCodeDescription(element.HS6)
        }
    }

    // Reduce locations object to a single field
    getCompanyLocationsByField(field) {
        let matchingLocations, registeredLocation;
        matchingLocations = Array.from(new Set(this.locations.map(location => location.getLocationField(field)))).sort(); // Remove duplicates and sort alphabetically
        registeredLocation = this.getRegisteredLocation(field); // Get details of registered location
        if (matchingLocations.length > 0) {
            // Remove registered location from the main list
            if (registeredLocation) {
                matchingLocations.splice(matchingLocations.findIndex(element => element == registeredLocation), 1);
            }
            // Add registered location first, then sort other locations sorted alphabetically.
            return [registeredLocation].concat(matchingLocations).filter(el => el != undefined && el.length > 0).join(", ");
        } else {
            return "Unknown";
        }
    }

    getRegisteredLocation(field) {
        let registeredLocation, registeredLocationObject;
        if (registeredLocationObject = this.locations.find(element => element.registeredPostcode === true)) {
            registeredLocation = registeredLocationObject[field];
        }
        return registeredLocation;
    }


    // Sum occupation counts for each location by given geography field 
    getOccupationCountsByLocation({field = "laName", topLocationsToShowCount = 5} = {}) {
        // Group locations by selected field
        const locationsGroupedByField = Object.groupBy(this.locations, (location) => location[field]);

        // Default settitngs
        const emptyOccupationCountsObject = Object.assign({total: 0}, Object.keys(OccupationGroup).reduce((acc, el) => {acc[el] = 0; return acc}, {}));
        
        for (let location in locationsGroupedByField) {
            // Sum locations in geography
            locationsGroupedByField[location] = locationsGroupedByField[location].reduce((accumulator, site) => {
                if (!Object.hasOwn(accumulator, 'location')) {
                    accumulator = {...emptyOccupationCountsObject};
                }
                let siteOccupationGroupsCount;
                if (site.occupationCounts.countsAvailable()) {
                    siteOccupationGroupsCount = site.occupationCounts.getSiteOccupationGroupsCount();
                    for (let field in siteOccupationGroupsCount) {
                        accumulator[field] += siteOccupationGroupsCount[field];
                    }
                    accumulator.location = location;
                }
                return accumulator;
            }, {});
        };

        const processedOccupationGroupsByLocation = Object.values(locationsGroupedByField).sort((a, b) => b.total - a.total).filter(element => element.total > 0); // Sort descending and filter out locations with no occupation data

        // If more than threshold, return top locations plus remaining locations grouped into an 'others' entry
        if (processedOccupationGroupsByLocation.length < topLocationsToShowCount) {
            return processedOccupationGroupsByLocation;
        } else {
            return processedOccupationGroupsByLocation.slice(0, topLocationsToShowCount).concat(processedOccupationGroupsByLocation.slice(topLocationsToShowCount, Infinity).reduce((accumulator, element) => {
                // Sum occupations groups for all remaining locations
                for (let field in accumulator) {
                    accumulator[field] += element[field];
                }
                accumulator.location = "Other";
                return accumulator;
            }, {...emptyOccupationCountsObject}));
        }

    }

    // Remove extra parts of location in brackets
    simplifyLocation(location) {
        if (location) location = location.replace(/\(.*\)/, "").trim();
        return location;
    }

    // Return array of all company postcodes. Can be registered postcode or all others
    getCompanyPostcodes(registeredAddressRequested) {
        return this.locations.filter(location => location.filterByRegisteredPostcode(registeredAddressRequested)).map(filteredLocation => filteredLocation.getLocationField("postcodeWithSpaces"));
    }

    // Returns financials object with the highest employee count
    getEmployeeCountDetails() {
        const max = this.financials.reduce((prev, current) => (prev.numberOfEmployee > current.numberOfEmployee) ? prev : current, false);
        return max;
    }

    // Add company website image if available
    addWebsiteImage() {
        let screenshot;
        const screenshotHolder = coreFunctions.createElementHelper("div", { class: "screenshotHolder" });
        if (this.companyURL && this.companyURL.length) {
            const companyUrlParsed = this.companyURL.replace(/^www\./, "");
            const imageSource = `https://tdcwebscreenshots.z35.web.core.windows.net/domainscreenshots/${companyUrlParsed[0]}/${companyUrlParsed}.png`;
            screenshot = screenshotHolder.appendChild(coreFunctions.createElementHelper("img", { class: "screenshot", loading: "lazy", src: imageSource }, { error: function () { screenshotHolder.classList.add("hidden"); screenshotHolder.removeChild(screenshot); }, load: { function() { screenshotHolder.classList.remove("hidden"); screenshotHolder.classList.add("screenshotAvailable"); } } }));
        } else {
            screenshotHolder.classList.add("hidden");
        }

        return screenshotHolder;
    }

    // Add UI control to either report missing URL or mismatched URL
    addMismatchedUrlReportControl() {
        const self = this;
        let reportMismatchedUrlButton;
        reportMismatchedUrlButton = coreFunctions.createElementHelper("button", { class: "report_mismatched_url actionControl", innerHTML: "&nbsp;", "data-tooltip": `${(this.isFieldAvailable(this.companyURL)) ? "Report invalid website" : "Request website update"}`, "data-tooltip_position": "top", "data-companynumber": this.companyNumber }, { "click": function (e) { self.updateMismatchedUrlReportPopup() } });
        if (this.hasUrlMismatchReport) {
            reportMismatchedUrlButton.disabled = true;
        }
        return reportMismatchedUrlButton;
    }

    // Update URL mismatched content
    updateMismatchedUrlReportPopup() {
        document.querySelector("#reportMismatchedUrlForm input[name='companyName']").value = this.companyName;
        document.querySelector("#reportMismatchedUrlForm input[name='companyNumber']").value = this.companyNumber;
        document.querySelector("#reportMismatchedUrlForm input[name='currentURL']").value = this.companyURL;
        document.getElementById("reportMismatchedUrlCompanyName").querySelector("span").innerHTML = this.companyName;

        // URL is available, update content
        if (this.isFieldAvailable(this.companyURL)) {
            document.querySelector("#reportMismatchedUrlOverlay .overlayTitle").innerHTML = "Report invalid website";
            document.querySelector("#reportMismatchedUrlOverlay .overlayText").innerHTML = "If you think this link is incorrect, please report it.";
            document.querySelector("#reportMismatchedUrlForm input[name='reportBackToUser']").parentElement.classList.add("hidden");
            document.querySelector("#reportMismatchedUrlForm input[name='reportBackToUser']").checked = false;
            document.getElementById("reportMismatchedUrlButton").innerHTML = "Report";
            this.resetUrlMatchStatsContent().addUrlMatchStatsContent();
        } else {
            document.querySelector("#reportMismatchedUrlOverlay .overlayTitle").innerHTML = "Request addition to ML platform";
            document.querySelector("#reportMismatchedUrlOverlay .overlayText").innerHTML = "For us to analyse this company we need to match it to a website. This is a manual process and will be actioned within a few weeks.";
            document.querySelector("#reportMismatchedUrlForm input[name='reportBackToUser']").parentElement.classList.remove("hidden");
            document.getElementById("reportMismatchedUrlButton").innerHTML = "Request update";
            this.resetUrlMatchStatsContent(); // No url match details
        }

        coreFunctions.showOverlay("reportmismatchedurl");
    }

    addUrlMatchStatsContent() {
        if (this.isFieldAvailable(this.urlMatchStats) && this.urlMatchStats.matchedWebsiteReasoning) {
            this.urlMatchStats.addUrlMatchDetails();
        }
        return this;
    }

    resetUrlMatchStatsContent() {
        let reportMismatchedUrlMatchStats = document.getElementById("reportMismatchedUrlMatchStats");
        while (reportMismatchedUrlMatchStats.firstChild) {
            reportMismatchedUrlMatchStats.removeChild(reportMismatchedUrlMatchStats.firstChild);
        }
        return this;
    }

    // Sort directors, active directors are first, then sorted by appointment date
    sortCompanyDirectors() {
        this.directors.sort((a, b) => {
            let aActive, bActive, aDate, bDate;

            aActive = a.isDirectorActive();
            bActive = b.isDirectorActive();
            aDate = Number(a.appointedOn);
            bDate = Number(b.appointedOn);

            if (aActive && bActive) {
                return bDate - aDate;
            } else if (aActive) {
                return -1;
            } else if (bActive) {
                return 1;
            } else {
                return bDate - aDate;
            }
        });
        return this;
    }

    // Add directors to UI
    createDirectorsHolder() {
        let directorsHolder = coreFunctions.createElementHelper("div", { class: "officersHolder" });
        this.directors.forEach(director => { if (director.isDirectorActive()) { directorsHolder.appendChild(director.addDirectorToHolder()) } });
        return directorsHolder;
    }

    // Add RTIC detial to page
    addCompanyRticsToPage(cic) {
        const rticContent = [];
        const listType = (cic === true) ? "cics" : "rtics";

        // Return unique RTIC sectors
        const rtics = this[listType].filter((value, index, self) => self.map(x => x.rticSectorName).indexOf(value.rticSectorName) == index).map(element => element.rticSectorName);

        // Add available verticals for each RTIC
        rtics.forEach((rtic, index) => {
            rticContent.push(`<div class="classification-item">`);
            rticContent.push(`<span class="classification-code">&#x25A0; ${rtic} <ul class="classification-list"><li> ${this[listType].filter(element => element.rticSectorName == rtic).map(element => element.rticName).join("</li><li> ")}</li></ul></span>`);
            rticContent.push(`</div>`);
        });

        // Only show "Report" button if overlay is present
        if (!document.getElementById("reportCompanyRticsHolder"))
            return rticContent.join("");

        if (rtics.length === 0)
            rticContent.push(`<a class="reportMismatchedRticButton" href="#">Suggest one...</a>`);

        // Add button to report mismatched/missing RTICs
        rticContent.push(`<span class="tooltip report-mismatched-rtic-tooltip" data-tooltip="Report mismatched ${(cic === true) ? "CICs" : "RTICs"}" data-tooltip_position="left"><button class="actionControl reportMismatchedRticButton" data-companynumber="${this.companyNumber}">&nbsp;</button></span>`);

        return rticContent.join("");
    }

    reportMismatchedRticButtonClick(element) {
        const listType = element.dataset.list_type;
        const verticals = this[(listType == "rtic") ? "rtics" : "cics"];

        // Update elements with company details
        document.getElementById("reportCompanyRticsHolder").dataset.list_type = listType;
        document.getElementById("reportRticsCompanyName").innerHTML = this.companyName;
        document.getElementById("reportRticsCompanyNumber").innerHTML = this.companyNumber;
        document.getElementById("reportRticsCompanyNumber").href= `${urlPath}/companypage?company_id=${this.companyNumber}`;
        document.getElementById("reportRticsCompanyUrl").innerHTML = this.companyURL ? `<a class="report-company-url" target="_blank" href="${coreFunctions.formatUrl(this.companyURL)}">${this.companyURL}</a>` : "Unavailable";

        // Update form with company details
        document.getElementById("reportMismatchedRticForm").elements.companyName.value = this.companyName;
        document.getElementById("reportMismatchedRticForm").elements.companyNumber.value = this.companyNumber;
        document.getElementById("reportMismatchedRticForm").elements.companyURL.value = this.companyURL;
        document.getElementById("reportMismatchedRticForm").elements.listType.value = listType;

        // Reset repoort mismatched RTICs dropdown with active company details
        if (document.getElementById("currentCompanyRticsSelect").tomselect) document.getElementById("currentCompanyRticsSelect").tomselect.destroy();
        if (!coreFunctions.arrayIsEmpty(verticals)) {
            this.mismatchedRticTomSelect = this.newReportMismatchedRticDropdown(verticals);
        }
        document.getElementById("currentCompanyRticsHolder").classList.toggle("hidden", coreFunctions.arrayIsEmpty(verticals));

        // const filters = this.parent.filters ?? this.parent.parent.filters;
        // if (filters) {
        //     filters.rticFilters.populateSuggestedRticsDropdown();
        // }

        // Reset suggested RTICs dropdowns with active company details
        this.updateSuggestedRticsDropdown("rtic", listType == "rtic" ? verticals : []);
        this.updateSuggestedRticsDropdown("cic", listType == "cic" ? verticals : []);

        coreFunctions.showOverlay("report_rtic");
    }

    updateSuggestedRticsDropdown(type, verticals) {
        let dropdown = document.querySelector(`.rtics-report-suggest-select[data-list_type='${type}']`).tomselect;
        if (dropdown) {

            dropdown.clear(); // clear all selected items from the control
            let companyRticVerticalCodes = verticals.map(element => element.rticCode);
            Object.values(dropdown.options).forEach(element => element.disabled = element.$option.disabled = companyRticVerticalCodes.includes(element.value)); // Disable companies current RTIC verticals
            dropdown.clearCache(); // clear cached option renderings
            dropdown.sync(); // synchronizes the Tom Select instance with the underlying select DOM elemnt
        }
        return this;
    }

    newReportMismatchedRticDropdown(verticals) {
        return new TomSelect('#currentCompanyRticsSelect', {
            options: verticals.map(element => element.getRticTomSelectOption()),
            optgroupField: 'class',
            labelField: 'name',
            searchField: ['name'],
            sortField: [
                {
                    field: "optgroup",
                    direction: "asc"
                }, {
                    field: "name",
                    direction: "asc"
                }
            ],
            openOnFocus: true,
            optgroups: [...new Map(verticals.map(element => element.getRticTomSelectOptionGroup())).values()],
            closeAfterSelect: false,
            plugins: {
                'checkbox_options': {
                    'checkedClassNames':   ['ts-checked'],
                    'uncheckedClassNames': ['ts-unchecked'],
                },
                'no_backspace_delete': {},
                'remove_button': {}
            },
            onItemAdd: (value) => coreFunctions.refreshFormFieldErrors(document.getElementById("reportMismatchedRticForm")),
            render: {
                optgroup_header: function(data, escape) {
                    return '<div class="optgroup-header">' + escape(data.label) + '</div>';
                },
                option: function(data, escape) {
                    return '<div class="mismatched-rtic-option">' + escape(data.name) + '</div>';
                },
                item: function(data, escape) {
                    return '<div class="report-rtic-item mismatched-rtic-item"><div>' + escape(data.name) + '<br><div class="sector">'+ escape(data.sectorName) + '</div></div>';
                }
            }
        });
    }

    // TODO might be able to remove this
    addSuggestRticsButtonToPage() {
        return `<button class="actionControl suggestRticButton" title="Suggest RTIC(s)" data-companynumber="${this.companyNumber}">&nbsp;</button>` // Add suggest RTIC control button
    }

    suggestRticsButtonClick(element) {
        let companyName, suggestCompanyRticsHolder;

        companyName = element.dataset.companyname;
        suggestCompanyRticsHolder = document.getElementById("suggestCompanyRticsHolder");

        document.getElementById("suggestRticCompanyName").innerHTML = this.companyName;
        document.getElementById("suggestRticForm").querySelector("input[name='companyname']").value = companyName;
        document.getElementById("suggestRticForm").querySelector("input[name='companynumber']").value = this.companyNumber;

        if(typeof mlListPage !== 'undefined') {
            mlListPage.clearSuggestedRticsDropdown();
        }

        if(typeof explore !== 'undefined') {
            explore.clearSuggestedRticsDropdown();
        }
        coreFunctions.showOverlay("suggest_rtic");
    }

    addSuggestCicsButtonToPage() {
        return `<button class="actionControl suggestCicButton" title="Suggest CIC(s)" data-companynumber="${this.companyNumber}">&nbsp;</button>` // Add suggest CIC control button
    }

    suggestCicsButtonClick(element) {
        let companyName, suggestCompanyCicsHolder;

        companyName = element.dataset.companyname;
        suggestCompanyCicsHolder = document.getElementById("suggestCompanyCicsHolder");

        document.getElementById("suggestCicCompanyName").innerHTML = this.companyName;
        document.getElementById("suggestCicForm").querySelector("input[name='companyname']").value = companyName;
        document.getElementById("suggestCicForm").querySelector("input[name='companynumber']").value = this.companyNumber;

        if(typeof mlListPage !== 'undefined') {
            mlListPage.clearSuggestedCicsDropdown();
        }

        if(typeof explore !== 'undefined') {
            explore.clearSuggestedCicsDropdown();
        }

        coreFunctions.showOverlay("suggest_cic");
    }

    addCompanyFinancialsToElement() {
        if (this.isFieldAvailable(this.financials)) {
            return this.createCompanyFinancialsTable();
        } else {
            return this.createDataFieldHolder("Financials", "Financials", "Unavailable", null, null);
        }
    }

    totalYearsWithFinancials() {
        return this.financials.length;
    }

    createCompanyFinancialsTable() {
        let totalYears, yearIndex, financialsTable, financialsTableColgroup, mainFinancialsTableSection, extraFinancialsTableSection;

        totalYears = this.totalYearsWithFinancials();
        yearIndex = 0;

        financialsTable = coreFunctions.createElementHelper("table", { "data-companynumber": this.companyNumber, class: "financialDetailsTable" });

        financialsTableColgroup = financialsTable.appendChild(coreFunctions.createElementHelper("colgroup", { class: "financialsTableColgroup" }));
        financialsTableColgroup.appendChild(coreFunctions.createElementHelper("col", { class: "labelsCol" }));
        while (yearIndex < totalYears) {
            financialsTableColgroup.appendChild(coreFunctions.createElementHelper("col", { class: `financesCol${(yearIndex == 0) ? " columnMobileVisible" : ""}` }));
            yearIndex++;
        }

        if(this.financials.filter(item => item.anomaliesDetected).length > 0) {
            document.getElementById("financialsDataHolder").appendChild(coreFunctions.createElementHelper("p", { class: "title__anomalies-detected",  innerHTML: "<sup>*</sup> We've identified the shaded columns as accounts that are likely outliers, using a model trained on confirmed outliers." }));
        }

        mainFinancialsTableSection = financialsTable.appendChild(coreFunctions.createElementHelper("tbody", { class: "mainFinancialsTableSection" }));
        //extraFinancialsTableSection = financialsTable.appendChild(coreFunctions.createElementHelper("tbody", { class: "extraFinancialsTableSection" }));

        this.createFinancialsTableRow(mainFinancialsTableSection, 'year', 'Financials', true, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'turnover', 'Turnover', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'preTaxProfit', 'Pre tax profit', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'profitAfterTax', 'Profit after tax', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'cash', 'Cash', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'totalCurrentAssets', 'Total current assets', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'totalAssets', 'Total assets', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'totalLiabilities', 'Total liabilities', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'netAssets', 'Net assets', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'netWorth', 'Net worth', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'numberOfEmployees', 'Employees', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'debtorsDueAfterOneYear', 'Debtors due', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'debtors', 'Debtors', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'tradeDebtors', 'Trade debtors', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'groupDebtors', 'Group debtors', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'creditors', 'Creditors', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'export', 'Export', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'wagesAndSalaries', 'Wages and salaries', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'depreciation', 'Depreciation', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'bankOverdraft', 'Bank overdraft', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'ebitda', 'EBITDA', false, totalYears);
        this.createFinancialsTableRow(mainFinancialsTableSection, 'auditors', 'Auditors', false, totalYears);

        //financialsTable.appendChild(this.createFinancialsTableShowExtraDetailsRow(totalYears));
        return financialsTable;
    }

    createFinancialsTableShowExtraDetailsRow(totalYears) {
        const companyObject = this;
        let extraDetailsRow, extraDetailsRowContent;
        extraDetailsRow = coreFunctions.createElementHelper("tr", { class: "extraDetailsRow" });
        extraDetailsRowContent = extraDetailsRow.appendChild(coreFunctions.createElementHelper("td", { colspan: totalYears + 1 }));
        extraDetailsRowContent.appendChild(coreFunctions.createElementHelper("button", { class: "actionControl extra-fields", innerHTML: '<span class="iconRotate">&nbsp;</span><span class="iconMessage"></span>' }, { click: function () { companyObject.toggleFinancialsTableExtraDetails(); } }));
        return extraDetailsRow;
    }

    toggleFinancialsTableExtraDetails() {
        let financialTable = document.querySelector(`.financialDetailsTable[data-companynumber='${this.companyNumber}']`);
        if (financialTable.classList.contains("extraFinancialFieldsVisible")) {
            financialTable.classList.remove("extraFinancialFieldsVisible");
        } else {
            financialTable.classList.add("extraFinancialFieldsVisible");
        }
    }


    createFinancialsTableRow(element, field, label, header, totalYears) {
        let row = document.createElement("tr");
        row.appendChild(coreFunctions.createElementHelper(`${(header) ? "th" : "td"}`, { innerHTML: label }));
        this.financials.forEach((yearFinancials, financialsIndex) => {
            if (header) {
                row.appendChild(yearFinancials.createFinancialsTableHeading(field, financialsIndex, totalYears, yearFinancials.anomaliesDetected));
            } else {
                row.appendChild(yearFinancials.createFinancialsTableCell(field, financialsIndex, (field === 'auditors' ? false : true), yearFinancials.anomaliesDetected));
            }
        });
        element.appendChild(row);
    }

    // Add financial details to page
    addCompanyFinancialsToPage() {
        let companyObject = this;
        companyObject.financials.forEach((financials, index) => financials.addFinancialsToTable(index, companyObject.financials.length));
        document.querySelector(".extraDetailsRow td").setAttribute("colspan", (this.financials.length + 1));
        return this;
    }

    // Filters out employment estimates with no data available
    filterEstimateData(data) {
        let filteredData = [];
        if (data != undefined && Array.isArray(data)) {
            filteredData = data.filter(element => {return (element.Measure != undefined || element.Estimated != undefined);});
        }
        return filteredData;
    }

    employmentEstimatesDataAvailable() {
        return this.employmentEstimates.length > 0;
    }

    createEmploymentEstimatesLineChartHolder() {
        let lineChartHolder =  coreFunctions.createElementHelper("div", { class: "employeeEstimatesLinechartHolder"});
        lineChartHolder.appendChild(this.addLinechartKey());
        lineChartHolder.appendChild(coreFunctions.createElementHelper("canvas", {id: `${this.companyNumber}_employmentEstimatesLineChartCanvas`}));
        return lineChartHolder;
    }

    createGrowthLineChartHolder(field) {
        let lineChartHolder =  coreFunctions.createElementHelper("div", { class: "growthLinechartHolder"});
        lineChartHolder.appendChild(this.addGrowthLinechartKey());
        lineChartHolder.appendChild(coreFunctions.createElementHelper("canvas", {id: `${field}_growthLineChartCanvas`}));
        return lineChartHolder;
    }

    createJobPostingOverTimeChartHolder(field) {
        let lineChartHolder =  coreFunctions.createElementHelper("div", { class: "jobPostingsOverTimeLinechartHolder"});
        lineChartHolder.appendChild(this.addJobPostingOverTimeLinechartKey());
        lineChartHolder.appendChild(coreFunctions.createElementHelper("canvas", {id: `${field}_jobPostingsOverTimeLineChartCanvas`}));
        return lineChartHolder;
    }

    addGrowthLinechartKey() {
        let linechartKey;
        linechartKey = coreFunctions.createElementHelper("div", { class: "linechartKey", innerHTML: "<span class='keyElement'><span class='keySquare measured line'>&nbsp;</span><span class='keyLabel'>Measured</span></span><span class='keyElement'><span class='keySquare projected line'>&nbsp;</span><span class='keyLabel'>Estimated</span></span>" });
        return linechartKey;
    }

    addJobPostingOverTimeLinechartKey() {
        let linechartKey;
        linechartKey = coreFunctions.createElementHelper("div", { class: "linechartKey", innerHTML: "<span class='keyElement'><span class='keySquare measured line'>&nbsp;</span><span class='keyLabel'>Postings</span></span>" });
        return linechartKey;
    }

    drawGrowthLineChart(field, growthValues, dataType) {
        const self = this;
            const ctx = document.getElementById(`${field}_growthLineChartCanvas`).getContext('2d');
            const data = {
                datasets: [{
                    label: 'Measured',
                    data: growthValues.map(element => ({ x: element.Year, y: element.Measure})),
                    fill: false,
                    showLine: true,
                    borderColor: '#D10956',
                    borderWidth: 3,
                    pointRadius: 2,
                    tension:0.2,
                    display: true
                }, {
                    label: 'Estimated',
                    data: growthValues.map(element => ({ x: element.Year, y: element.Estimated})),
                    fill: false,
                    showLine: true,
                    borderColor: 'rgba(209, 9, 86, 0.6)',
                    borderWidth: 3,
                    borderDash: [10,5],
                    pointRadius: 2,
                    tension: 0.2,
                    display: true
                }]
            };
            const scatterChart = new Chart(ctx, {
                type: 'scatter',
                data: data,
                options: {
                    animation: false,
                    aspectRatio: 1.7,
                    scales: {
                        x: {
                            beginAtZero: false,
                            title: {
                                display: false,
                                text: "Year",
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular'
                                }
                            },
                            ticks: {
                                callback: function (value, index, values) {
                                    if (field == "MonthlyJobs") {
                                        return  moment(value).format("YYYY-MM");
                                    } else {
                                        return (Number.isInteger(value)) ? value.toString() : null;
                                    }
                                },
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular',
                                    size: 13
                                }
                            },
                            grid: {
                                borderColor: "rgba(0, 0, 0, 0)",
                                color: "#4f5251",
                                display: false,
                            }
                        },
                        y: {
                            beginAtZero: true,
                            title: {
                                display: false,
                                text: "Count",
                                color: '#FFFFFF',
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular'
                                }
                            },
                            ticks: {
                                callback: function (value, index, values) {
                                    return self.formatGrowthLinechartLabel(value, false, dataType);
                                },
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular',
                                    size: 13
                                },
                                maxTicksLimit: 7
                            },
                            grid: {
                                borderColor: "rgba(0, 0, 0, 0)",
                                color: "#4f5251",
                                drawTicks: false
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: false,
                        }, title: {
                            display: false,
                            text: `Usage statistics`,
                            color: '#FFFFFF'
                        },
                        tooltip: {
                            titleFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            bodyFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            footerFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            displayColors: false,
                            callbacks: {
                                label: function (context) {
                                    let label = context.dataset.label || '';

                                    if (label) {
                                        label += ': ';
                                    }
                                    if (context.parsed.y !== null) {
                                        label += Number(context.parsed.y.toFixed(1)).toLocaleString();
                                    }
                                    if (context.parsed.x !== null) {
                                        label += ` (${context.parsed.x})`;
                                    }
                                    return label;
                                }
                            }
                        },
                        datalabels: {
                            anchor: 'center',
                            align: 'right',
                            offset: 5,
                            clamp: true,
                            font: {
                                weight: 'bold'
                            },
                            formatter: function (value, context) {
                                return context.dataset.label;
                            },
                            color: function (context) {
                                return context.dataset.borderColor;
                            },
                            display: function (context) { return (context.dataIndex === 0); }
                        }
                    }
                }
            });
            return this;
    }

    formatGrowthLinechartLabel(value, formatFree, dataType) {
        let formattedValue = Math.abs(value);

        const si = [
            {
                value: 1,
                symbol: ""
            },
            {
                value: 1E3,
                symbol: "k"
            },
            {
                value: 1E6,
                symbol: "M"
            },
            {
                value: 1E9,
                symbol: "bn"
            },
            {
                value: 1E12,
                symbol: "tn"
            },
            {
                value: 1E15,
                symbol: "P"
            },
            {
                value: 1E18,
                symbol: "E"
            }
        ];
        var i;
        for (i = si.length - 1; i > 0; i--) {
            if (formattedValue >= si[i].value) {
                break;
            }
        }

        if (dataType == "currency") {
            if (value < 0) {
                formattedValue = Number(Math.abs((value / si[i].value).toFixed(1))).toLocaleString() + si[i].symbol;
                if (formatFree) {
                    formattedValue = "-£" + formattedValue;
                } else {
                    formattedValue = "<span class='orange'>-£" + formattedValue + "</span>";
                }
            } else {
                formattedValue = Number((value / si[i].value).toFixed(1)).toLocaleString() + si[i].symbol;
                formattedValue = "£" + formattedValue;
            }
        } else {
            formattedValue = Number((value / si[i].value).toFixed(1)).toLocaleString() + si[i].symbol;
        }

        return formattedValue;
    }


    // TODO check this
    addLinechartKey() {
        let financialsKey;
        financialsKey = coreFunctions.createElementHelper("div", { class: "linechartKey", innerHTML: "<span class='keyElement'><span class='keySquare measured line'>&nbsp;</span><span class='keyLabel'>Measured</span></span><span class='keyElement'><span class='keySquare projected line'>&nbsp;</span><span class='keyLabel'>Estimated</span></span>" });
        return financialsKey;
    }

    // TODO check this
    drawEmploymentEstimatesLineChart() {
        const self = this;
            const ctx = document.getElementById(`${this.companyNumber}_employmentEstimatesLineChartCanvas`).getContext('2d');
            const data = {
                datasets: [{
                    label: 'Measured',
                    data: this.employmentEstimates.map(element => ({ x: element.Year, y: element.Measure})),
                    fill: false,
                    showLine: true,
                    borderColor: '#D10956',
                    borderWidth: 3,
                    pointRadius: 2,
                    tension: 0.1,
                    display: true
                }, {
                    label: 'Estimated',
                    data: this.employmentEstimates.map(element => ({ x: element.Year, y: element.Estimated})),
                    fill: false,
                    showLine: true,
                    borderColor: 'rgba(209, 9, 86, 0.6)',
                    borderWidth: 3,
                    borderDash: [10,5],
                    pointRadius: 2,
                    tension: 0.1,
                    display: true
                }]
            };
            const scatterChart = new Chart(ctx, {
                type: 'scatter',
                data: data,
                options: {
                    animation: false,
                    aspectRatio: 1.7,
                    scales: {
                        x: {
                            beginAtZero: false,
                            title: {
                                display: false,
                                text: "Year",
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular'
                                }
                            },
                            ticks: {
                                callback: function (value, index, values) {
                                  return (Number.isInteger(value)) ? value.toString() : null;
                                },
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular',
                                    size: 13
                                }
                            },
                            grid: {
                                borderColor: "rgba(0, 0, 0, 0)",
                                color: "#4f5251",
                                display: false,
                            }
                        },
                        y: {
                            beginAtZero: true,
                            title: {
                                display: false,
                                text: "Count",
                                color: '#FFFFFF',
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular'
                                }
                            },
                            ticks: {
                                callback: function (value, index, values) {
                                    return self.formatLinechartLabel(value, false);
                                },
                                color: "#FFFFFF",
                                font: {
                                    family: 'Gilmer-Regular, Poppins-Regular',
                                    size: 13
                                },
                                maxTicksLimit: 7
                            },
                            grid: {
                                borderColor: "rgba(0, 0, 0, 0)",
                                color: "#4f5251",
                                drawTicks: false
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: false,
                        }, title: {
                            display: false,
                            text: `Usage statistics`,
                            color: '#FFFFFF'
                        },
                        tooltip: {
                            titleFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            bodyFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            footerFont: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                            },
                            displayColors: false,
                            callbacks: {
                                label: function (context) {
                                    let label = context.dataset.label || '';

                                    if (label) {
                                        label += ': ';
                                    }
                                    if (context.parsed.y !== null) {
                                        label += Number(context.parsed.y.toFixed(1)).toLocaleString();
                                    }
                                    if (context.parsed.x !== null) {
                                        label += ` (${context.parsed.x})`;
                                    }
                                    return label;
                                }
                            }
                        },
                        datalabels: {
                            anchor: 'center',
                            align: 'right',
                            offset: 5,
                            clamp: true,
                            font: {
                                weight: 'bold'
                            },
                            formatter: function (value, context) {
                                return context.dataset.label;
                            },
                            color: function (context) {
                                return context.dataset.borderColor;
                            },
                            display: function (context) { return (context.dataIndex === 0); }
                        }
                    }
                }
            });
            return this;
    }

    formatLinechartLabel(value, formatFree) {
        let formattedValue = Math.abs(value);

        const si = [
            {
                value: 1,
                symbol: ""
            },
            {
                value: 1E3,
                symbol: "k"
            },
            {
                value: 1E6,
                symbol: "M"
            },
            {
                value: 1E9,
                symbol: "bn"
            },
            {
                value: 1E12,
                symbol: "tn"
            },
            {
                value: 1E15,
                symbol: "P"
            },
            {
                value: 1E18,
                symbol: "E"
            }
        ];
        var i;
        for (i = si.length - 1; i > 0; i--) {
            if (formattedValue >= si[i].value) {
                break;
            }
        }

        if (this.dataType == "currency") {
            if (value < 0) {
                formattedValue = Number(Math.abs((value / si[i].value).toFixed(1))).toLocaleString() + si[i].symbol;
                if (formatFree) {
                    formattedValue = "-£" + formattedValue;
                } else {
                    formattedValue = "<span class='orange'>-£" + formattedValue + "</span>";
                }
            } else {
                formattedValue = Number((value / si[i].value).toFixed(1)).toLocaleString() + si[i].symbol;
                formattedValue = "£" + formattedValue;
            }
        } else {
            formattedValue = Number((value / si[i].value).toFixed(1)).toLocaleString() + si[i].symbol;
        }

        return formattedValue;
    }

    drawJobPostingOverTimeBarChart(field, values) {
        const self = this;
        const ctx = document.getElementById(`${field}_jobPostingsOverTimeLineChartCanvas`).getContext('2d');
        const data = {
            datasets: [{
                label: 'Postings',
                data: values.map(element => ({ x: moment(element.Timestamp, 'X'), y: element.Count})),
                fill: false,
                showLine: true,
                borderColor: '#D10956',
                backgroundColor: '#D10956',
                borderWidth: 3,
                pointRadius: 2,
                tension:0.2,
                display: true,
                maxBarThickness: 100
            }]
        };
        const barChart = new Chart(ctx, {
            type: 'bar',
            data: data,
            options: {
                animation: false,
                aspectRatio: 1.7,
                scales: {
                    x: {
                        beginAtZero: false,
                        type: 'time',
                        time: {
                            unit: "month"
                        },
                        title: {
                            display: false,
                            text: "Year",
                            color: "#FFFFFF",
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular'
                            }
                        },
                        ticks: {
                            color: "#FFFFFF",
                            callback: function (value, index, values) {
                                return value;
                            },
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                                size: 13
                            }
                        },
                        grid: {
                            borderColor: "rgba(0, 0, 0, 0)",
                            color: "#4f5251",
                            display: false,
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: false,
                            text: "Count",
                            color: '#FFFFFF',
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular'
                            }
                        },
                        ticks: {
                            callback: function (value, index, values) {
                                let tickValue;
                                if (Number.isInteger(value)) {
                                    tickValue = self.formatJobPostingOverTimeLinechartLabel(value);
                                }
                                return tickValue;
                            },
                            color: "#FFFFFF",
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                                size: 13
                            },
                            maxTicksLimit: 7
                        },
                        grid: {
                            borderColor: "rgba(0, 0, 0, 0)",
                            color: "#4f5251",
                            drawTicks: false
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    title: {
                        display: false,
                        color: '#FFFFFF'
                    },
                    tooltip: {
                        titleFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        bodyFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        footerFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        displayColors: false,
                        callbacks: {
                            title: function (context) {
                                return moment(context[0].parsed.x, 'x').format("YYYY-MM");
                            },
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += Number(context.parsed.y.toFixed(1)).toLocaleString();
                                }
                                return label;
                            }
                        }
                    },
                    datalabels: {
                        anchor: 'center',
                        align: 'right',
                        offset: 5,
                        clamp: true,
                        font: {
                            weight: 'bold'
                        },
                        formatter: function (value, context) {
                            return context.dataset.label;
                        },
                        color: function (context) {
                            return context.dataset.borderColor;
                        },
                        display: function (context) { return (context.dataIndex === 0); }
                    }
                }
            }
        });
        return this;
    }

    drawJobPostingOverTimeLineChart(field, values) {
        const self = this;
        const ctx = document.getElementById(`${field}_jobPostingsOverTimeLineChartCanvas`).getContext('2d');
        const data = {
            datasets: [{
                label: 'Postings',
                data: values.map(element => ({ x: element.Timestamp, y: element.Count})),
                fill: false,
                showLine: true,
                borderColor: '#D10956',
                borderWidth: 3,
                pointRadius: 2,
                tension:0.2,
                display: true
            }]
        };
        const scatterChart = new Chart(ctx, {
            type: 'scatter',
            data: data,
            options: {
                animation: false,
                aspectRatio: 1.7,
                scales: {
                    x: {
                        beginAtZero: false,
                        title: {
                            display: false,
                            text: "Year",
                            color: "#FFFFFF",
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular'
                            }
                        },
                        ticks: {
                            callback: function (value, index, values) {
                                return moment(value, 'X').format("YYYY-MM");
                            },
                            color: "#FFFFFF",
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                                size: 13
                            }
                        },
                        grid: {
                            borderColor: "rgba(0, 0, 0, 0)",
                            color: "#4f5251",
                            display: false,
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: false,
                            text: "Count",
                            color: '#FFFFFF',
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular'
                            }
                        },
                        ticks: {
                            callback: function (value, index, values) {
                                return self.formatJobPostingOverTimeLinechartLabel(value);
                            },
                            color: "#FFFFFF",
                            font: {
                                family: 'Gilmer-Regular, Poppins-Regular',
                                size: 13
                            },
                            maxTicksLimit: 7
                        },
                        grid: {
                            borderColor: "rgba(0, 0, 0, 0)",
                            color: "#4f5251",
                            drawTicks: false
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    title: {
                        display: false,
                        color: '#FFFFFF'
                    },
                    tooltip: {
                        titleFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        bodyFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        footerFont: {
                            family: 'Gilmer-Regular, Poppins-Regular',
                        },
                        displayColors: false,
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += Number(context.parsed.y.toFixed(1)).toLocaleString();
                                }
                                if (context.parsed.x !== null) {
                                    label += ` (${moment(context.parsed.x, "X").format("YYYY-MM")})`;
                                }
                                return label;
                            }
                        }
                    },
                    datalabels: {
                        anchor: 'center',
                        align: 'right',
                        offset: 5,
                        clamp: true,
                        font: {
                            weight: 'bold'
                        },
                        formatter: function (value, context) {
                            return context.dataset.label;
                        },
                        color: function (context) {
                            return context.dataset.borderColor;
                        },
                        display: function (context) { return (context.dataIndex === 0); }
                    }
                }
            }
        });
        return this;
    }

    formatJobPostingOverTimeLinechartLabel(value) {
        let formattedValue = Math.abs(value);

        const si = [
            {
                value: 1,
                symbol: ""
            },
            {
                value: 1E3,
                symbol: "k"
            },
            {
                value: 1E6,
                symbol: "M"
            },
            {
                value: 1E9,
                symbol: "bn"
            },
            {
                value: 1E12,
                symbol: "tn"
            },
            {
                value: 1E15,
                symbol: "P"
            },
            {
                value: 1E18,
                symbol: "E"
            }
        ];
        var i;
        for (i = si.length - 1; i > 0; i--) {
            if (formattedValue >= si[i].value) {
                break;
            }
        }

        formattedValue = Number((value / si[i].value).toFixed(1)).toLocaleString() + si[i].symbol;

        return formattedValue;
    }

    // Creates a searchable table with TomSelect search
    createSearchableTable(field, object, elementsField, headingsField, placeholder) {
        // Elements to be used as rows in table
        const elements = elementsField ? object[elementsField] : object.elements;

        const holder = coreFunctions.createElementHelper("div", {class: "searchable-table-holder"});
        const search = holder.appendChild(coreFunctions.createElementHelper("select", {class: "searchable-table-select"}));

        const options = elements.map(element => element.createTomSelectOption(field));
        new TomSelect(search, {
            options: options,
            labelField: "name",
            searchField: ["name", "value"],
            openOnFocus: true,
            closeAfterSelect: true,
            onItemAdd: (value, $item) => this.searchableTableOnItemAdd(value, $item.dataset.field),
            placeholder: placeholder,
            hidePlaceholder: false,
            plugins: {
                remove_button:{
                    title:'Reset search',
                }
            },
            render: {
                option: function(data, escape) {
                    return `<div class="searchable-table-option" data-code="${escape(data.value)}">
                                <div class="searchable-table-value">${escape(data.value)}</div>${(data.name == data.value) ? "" : `<div class="searchable-table-description">${escape(data.name)}</div>`}
                            </div>`
                },
                item: function(data, escape) {
                    return `<div class="searchable-table-item" data-field="${escape(data.field)}">${escape(data.value)}</div>`;
                }
            },
            onDelete: () => this.searchableTableOnReset(field)
        });

        const tableWrapper = holder.appendChild(coreFunctions.createElementHelper("div", {class: "searchable-table-wrapper"}));
        const table = tableWrapper.appendChild(coreFunctions.createElementHelper("table", {class: "searchable-table", "data-field": field}));
        const tableHeadings = headingsField ? object[headingsField] : object.tableHeadings;
        table.appendChild(this.createTableHeader(tableHeadings));

        // Add rows to table body
        const tbody = table.appendChild(coreFunctions.createElementHelper("tbody", {"data-field": field}));
        elements.forEach(element => tbody.appendChild(element.createTableRow()));

        return holder;

    }

    // Hide rows not matching search results
    searchableTableOnItemAdd(value, field) {
        const holder = document.querySelector(`.searchable-table tbody[data-field='${field}']`);
        holder.querySelectorAll("tr").forEach(row => row.hidden = !(row.dataset.code == value));
    }

    // Show all rows in searchable table
    searchableTableOnReset(field) {
        const holder = document.querySelector(`.searchable-table tbody[data-field='${field}']`);
        holder.querySelectorAll("tr").forEach(row => row.hidden = false);
    }

    createExpandableTable(object, elementsField, headingsField) {

        let tableHolder, table;
        let elements = (elementsField) ? object[elementsField] : object.elements; // Set array field to populate table rows from
        let tableHeadings = (headingsField) ? object[headingsField] : object.tableHeadings;
        tableHolder = coreFunctions.createElementHelper("div", {class: "expandableTableHolder"});
        table = tableHolder.appendChild(coreFunctions.createElementHelper("table", {class: "expandableTable", "data-companynumber": this.companyNumber}));
        table.appendChild(this.createTableHeader(tableHeadings));
        table.appendChild(coreFunctions.createElementHelper("tbody", {class: "expandableTableBody primary"})); // Main table body initially visible
        this.addElementsToExpandableTable(tableHolder, object, elements);
        if (object.addCountToTable) {
            tableHolder.appendChild(object.addTotalCount());
        }
        return tableHolder;
    }

    addElementsToExpandableTable(tableHolder, object, elements) {
        let tableBody, expandableTableBody;
        tableBody = tableHolder.querySelector("tbody");

        if (this.expandableTableBodyRequired(object, elements)) {
            expandableTableBody = tableHolder.querySelector("table").appendChild(coreFunctions.createElementHelper("tbody", {class: "expandableTableBody extra", "data-visible": 0}));
            tableHolder.appendChild(this.createExpandTableControl());
        }
        coreFunctions.clearElementChildren(tableBody);
        elements.forEach((element, index) => {
            let target = (index < object.tableElementsPerPage) ? tableBody : expandableTableBody;
            target.appendChild(element.createTableRow());
        });
        return this;
    }

    // Check element needed to contain extra rows
    expandableTableBodyRequired(object, elements) {
        return (elements.length > object.tableElementsPerPage);
    }

    // Create element to control display of extra table rowsa
    createExpandTableControl() {
        // Create random ID for element
        let controlId = `expandTableControl_${Math.random().toString(36).substr(2, 8)}`
        let holder = coreFunctions.createElementHelper("div", {class: "expandableTableControlHolder"});
        holder.appendChild(coreFunctions.createElementHelper("input", {type: "checkbox", id: controlId}, {change: (event) => {this.expandTableControlOnChange(event)}}));
        holder.appendChild(coreFunctions.createElementHelper("label", {innerHTML: "<span class='iconRotate'>&nbsp;</span><span class='iconMessage'>Show all</span>", class: "actionControl", for: controlId}));
        return holder;
    }

    // Display or hide extra table rows
    expandTableControlOnChange(event) {
        let expandableTableBody;
        let control = event.target;
        if (expandableTableBody = control.closest(".expandableTableHolder").querySelector("tbody.extra")) {
            expandableTableBody.dataset.visible = (control.checked) ? 1 : 0;
        }
        // When extra results hidden move view back to control
        if (!control.checked) {
            control.scrollIntoView({ block: "center" });
        }
    }

    createPaginatedTable(object) {
        let tableHolder, table;
        tableHolder = coreFunctions.createElementHelper("div", {class: "paginatedTableHolder"});
        table = tableHolder.appendChild(coreFunctions.createElementHelper("table", {class: "paginatedTable", "data-companynumber": this.companyNumber}));
        table.appendChild(this.createTableHeader(object.tableHeadings));
        table.appendChild(coreFunctions.createElementHelper("tbody", {class: "paginatedTableBody"}));
        tableHolder.appendChild(this.createPaginatedTablePageNumbers(object, tableHolder));
        if (object.addCountToTable) {
            tableHolder.appendChild(object.addTotalCount());
        }
        this.addElementsToPaginatedTable(tableHolder, object);
        return tableHolder;
    }

    createTableHeader(tableHeadings) {
        let tableHeader, tableHeaderRow
        tableHeader = coreFunctions.createElementHelper("thead")
        tableHeaderRow = tableHeader.appendChild(coreFunctions.createElementHelper("tr"));
        tableHeadings.forEach(heading => tableHeaderRow.appendChild(coreFunctions.createElementHelper("th", {innerHTML: heading})));
        return tableHeader;
    }

    createPaginatedTablePageNumbers(object, holder) {
        const self = this;
        let pageNumber, existingPageNumbers, pageLinksHolder, pageNumbersStructure, totalElementsInPageNumberStructure;

        if (existingPageNumbers = holder.querySelector(".paginatedTablePageLinksHolder")) {
            holder.removeChild(existingPageNumbers);
        }

        pageLinksHolder = coreFunctions.createElementHelper("div", {class: "paginatedTablePageLinksHolder"});
        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl previous-page"},{click: function() {self.paginatedTableShiftPage(this, object, -1)}}));

        pageNumbersStructure = this.getPaginatedTablePageNumbersStructure(object);
        totalElementsInPageNumberStructure = pageNumbersStructure.length;
        for (var i = 0; i < totalElementsInPageNumberStructure; i++) {
            pageNumber = pageNumbersStructure[i];
            if (pageNumbersStructure[i - 1] && pageNumber != (pageNumbersStructure[i - 1] + 1)) {
                pageLinksHolder.appendChild(coreFunctions.createElementHelper("span", { innerHTML: ". .", class: "pageLink" }));
            }
            pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", { innerHTML: pageNumber, class: `actionControl pageLink${(pageNumber == object.pageNumber) ? " selected" : ""}`, "data-pagenumber": pageNumber }, { click: function () { self.paginatedTablePageSelect(this, object) } }));
        }

        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl next-page"}, {click: function() {self.paginatedTableShiftPage(this, object, 1)}}));

        return pageLinksHolder;
    }

    paginatedTablePageSelect(element, object) {
        let tableHolder, table;
        tableHolder = element.closest(".paginatedTableHolder");
        table = tableHolder.querySelector("table");
        object.pageNumber = parseInt(element.dataset.pagenumber);
        tableHolder.appendChild(this.createPaginatedTablePageNumbers(object, tableHolder));
        this.addElementsToPaginatedTable(table, object);
    }

    paginatedTableShiftPage(element, object, change) {
        let pageNumber, pageSelectElement;
        pageNumber = object.pageNumber + change;
        if (pageSelectElement = element.closest(".paginatedTablePageLinksHolder").querySelector(`.pageLink[data-pagenumber='${pageNumber}']`)) {
            this.paginatedTablePageSelect(pageSelectElement, object);
        }
    }

    getPaginatedTablePageNumbersStructure(object) {
        let pageNumber, totalPages, pageStructure;

        pageNumber = object.pageNumber;
        totalPages = Math.ceil(object.elements.length / object.tableElementsPerPage);

        if (totalPages <= 10) {
            pageStructure = [];
            for (var i = 0; i < totalPages; i++) {
                pageStructure.push(i + 1);
            }
            return pageStructure;
        } else if (pageNumber < 4) {
            return [1, 2, 3, 4, totalPages];
        } else if (pageNumber >= (totalPages - 2)) {
            return [1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            return [1, pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2, totalPages];
        }
    }

    addElementsToPaginatedTable(tableHolder, object) {
        let tableBody = tableHolder.querySelector("tbody");
        coreFunctions.clearElementChildren(tableBody);
        this.getElementsOnPage(object).forEach(element => tableBody.appendChild(element.createTableRow()));
        return this;
    }

    getElementsOnPage(object) {
        return object.elements.slice((object.pageNumber - 1) * object.tableElementsPerPage, object.pageNumber * object.tableElementsPerPage);
    }

    getSimilarCompaniesDetails(resultsHolder) {
        if (coreFunctions.arrayIsEmpty(this.similarCompanyDetails)) {
            const method = "POST";
            const input = new FormData();
            input.append("similarCompanies", this.similarCompanies ? JSON.stringify(this.similarCompanies) : "[]");
            const url = `../api/similar_companies/similar_companies_actions.php?action=get_similar_companies_full&company_number=${this.companyNumber}`;
            const options = {responseType: "json"};
            const success = (response) => this.processSimilarCompaniesDetails(response, resultsHolder).addSimilarCompanyDetailsToPage(resultsHolder);
            const error = () => {};
            coreFunctions.xhrHelper(method, url, success, error, input, options);
        }
        return this;
    }

    processSimilarCompaniesDetails(response, resultsHolder) {
        if (!coreFunctions.arrayIsEmpty(response)) {
            this.similarCompanyDetails = response.map(element => new SimilarCompany(element, this.parent));
        }
        return this;
    }

    addSimilarCompanyDetailsToPage(resultsHolder) {
        coreFunctions.clearElementChildren(resultsHolder);
        resultsHolder.classList.add("similar-companies-processed");
        const holder = resultsHolder.appendChild(coreFunctions.createElementHelper("ul"));
        if (!coreFunctions.arrayIsEmpty(this.similarCompanyDetails)) {
            this.similarCompanyDetails.forEach(element => holder.appendChild(element.createCompanyDetailsCompactView()));
        } else {
            holder.appendChild(coreFunctions.createElementHelper("li", {innerHTML: "No similar companies found."}));
        }
        return this;
    }

    addCompanyIcon() {
        const iconHolder = coreFunctions.createElementHelper("div", { class: "company-icon-holder" });
        if (this.companyURL && this.companyURL.length) {
            const companyUrlParsed = this.companyURL.replace(/^www\./, "");
            const iconSource = `https://tdcwebscreenshots.z35.web.core.windows.net/domainicons/${companyUrlParsed[0]}/${companyUrlParsed}.png`;
            const icon = iconHolder.appendChild(coreFunctions.createElementHelper("img", { class: "company-icon", loading: "lazy", src: iconSource }, {
                error: () => {
                    iconHolder.removeChild(icon);
                    iconHolder.appendChild(this.createIconPlaceholder())
                },
                load: () => {}
            }));
        } else {
            iconHolder.appendChild(this.createIconPlaceholder());
        }

        return iconHolder;

    }

    createIconPlaceholder() {
        const companyInitial = this.companyName[0];
        const iconPlaceholder = coreFunctions.createElementHelper("div", {innerHTML: companyInitial, class: "company-icon-placeholder"});
        return iconPlaceholder;
    }

    createCompanyDetailsCompactView(tooltipPosition = "bottom") {
        const currencyFormat = new Intl.NumberFormat('en-EN', { style: 'currency', maximumFractionDigits: 0, currency: 'GBP', notation: 'compact'  });
        const holder = coreFunctions.createElementHelper("li", {"data-companynumber": this.companyNumber, class: "company-compact-view"});

        const companyNameHolder = holder.appendChild(coreFunctions.createElementHelper("div", {class: "company-name-holder"}));

        const companyNameFlex = companyNameHolder.appendChild(coreFunctions.createElementHelper("div", {class: "flex-row-holder"}));
        companyNameFlex.appendChild(this.addCompanyIcon());

        const companyNameDetails = companyNameFlex.appendChild(coreFunctions.createElementHelper("div", {class: "flex-grow"}));
        companyNameDetails.appendChild(coreFunctions.createElementHelper("div", {innerHTML: this.companyName, class: `company-name include-icon ${this.addCompanyStatusClass()}`, "data-tooltip": `Company status: ${this.companyStatus}`, "data-tooltip_position": "left"})).appendChild(coreFunctions.createElementHelper("a", {innerHTML: this.companyNumber, href: `https://find-and-update.company-information.service.gov.uk/company/${this.companyNumber}`, target: "_blank", class: "trainingset-review-company-detail company-number"}));

        if (this.isFieldAvailable(this.companyURL)) {
            companyNameDetails.appendChild(coreFunctions.createElementHelper("a", {innerHTML: this.companyURL, href: coreFunctions.formatUrl(this.companyURL), target: "_blank", class: "company-url include-icon"}));
        } else {
            companyNameDetails.appendChild(coreFunctions.createElementHelper("span", {innerHTML: "Unknown", class: "company-url include-icon"}));
        }

        let socialDetailsHolder = this.createSocialDetailsCompact();
        if (socialDetailsHolder) {
            companyNameDetails.appendChild(socialDetailsHolder)
        }

        const turnoverString = this.isFieldAvailable(this.bestEstimateTurnover) ? currencyFormat.format(this.bestEstimateTurnover) : "Unknown";
        holder.appendChild(coreFunctions.createElementHelper("div", {class: "company-compact-view__field-holder", "data-field": "Turnover"})).appendChild(coreFunctions.createElementHelper("div", {innerHTML: turnoverString, "class": "data-field turnover include-icon", "data-tooltip": "Estimated turnover", "data-tooltip_position": tooltipPosition}));

        const employeesString = this.isFieldAvailable(this.bestEstimateEmployees) ? this.bestEstimateEmployees.toLocaleString() : "Unknown";
        holder.appendChild(coreFunctions.createElementHelper("div", {class: "company-compact-view__field-holder", "data-field": "Employees"})).appendChild(coreFunctions.createElementHelper("div", {innerHTML: employeesString, "class": "data-field employees include-icon", "data-tooltip": "Estimated employee count", "data-tooltip_position": tooltipPosition}));

        const locationString = this.isFieldAvailable(this.locations) ? this.simplifyLocation(this.getRegisteredLocation("nuts1Name")) : "Unknown";
        holder.appendChild(coreFunctions.createElementHelper("div", {class: "company-compact-view__field-holder", "data-field": "location"})).appendChild(coreFunctions.createElementHelper("div", {innerHTML: locationString, "class": "data-field location include-icon", "data-tooltip": "Registered address location", "data-tooltip_position": tooltipPosition}))

        holder.appendChild(coreFunctions.createElementHelper("div", {class: "company-link-holder"})).appendChild(coreFunctions.createElementHelper("a", { innerHTML: "<span>Full info</span>", class: "actionControl chevron-right companyPageLink", href: `${urlPath}/companypage?company_id=${this.companyNumber}`, target: tooltipPosition }));

        return holder;
    }

    createCircleScoreElement(percentage) {
        // Circle parameters
        const width = 60;
        const height = 60;
        const strokeWidth = 5;
        const radius = (width / 2) - strokeWidth;
        const circumference = Math.PI * radius * 2;

        // Set element colors
        const cs = getComputedStyle(document.body);
        const innerColor = cs.getPropertyValue('--colour-bg-opaque');
        const circleColor = cs.getPropertyValue('--colour-brand-pink');

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("width", width);
        svg.setAttribute("height", height);
        svg.classList.add("circle-score");

        const inner = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        inner.setAttribute("r", radius);
        inner.setAttribute("cx", width / 2);
        inner.setAttribute("cy", height / 2);
        inner.setAttribute("stroke", innerColor);
        inner.setAttribute("stroke-width", strokeWidth);
        inner.setAttribute("fill", "transparent");
        inner.setAttribute("stroke-dashoffset", 0);
        inner.setAttribute("stroke-dasharray", `${circumference}px`);
        svg.appendChild(inner);

        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("r", radius);
        circle.setAttribute("cx", width / 2);
        circle.setAttribute("cy", height / 2);
        circle.setAttribute("stroke", circleColor);
        circle.setAttribute("stroke-width", strokeWidth);
        circle.setAttribute("stroke-linecap", "round");
        circle.setAttribute("fill", "transparent");
        circle.setAttribute("stroke-dashoffset", `${circumference * (1 - percentage)}px`);
        circle.setAttribute("stroke-dasharray", `${circumference}px`);
        svg.appendChild(circle);

        return svg;
    }
}

class Director {
    constructor(personNumber, forenames, surname, role, appointedOn, resignationDate) {
        this.personNumber = personNumber;
        this.forenames = forenames;
        this.surname = surname;
        this.role = role;
        this.appointedOn = appointedOn;
        this.resignationDate = resignationDate;
    }

    // Formatted version of director active
    formatDirectorStatus() {
        return (this.isDirectorActive()) ? "Active" : `Resigned: ${moment(this.resignationDate, "YYYYMMDD").format("Do MMMM YYYY")}`;
    }

    // Check whether the director is current active for this company
    isDirectorActive() {
        return !this.resignationDate || this.resignationDate.length == 0;
    }

    // Add director details to page
    addDirectorToHolder() {
        let createElementHelper, personHolder, portraitHolder, personDetails;

        createElementHelper = coreFunctions.createElementHelper;

        personHolder = createElementHelper("div", { class: "personHolder" });

        portraitHolder = createElementHelper("div", { class: "portraitHolder" });
        personHolder.appendChild(portraitHolder);
        portraitHolder.appendChild(createElementHelper("img", { class: "portrait", src: `${urlPath}/images/director.svg` })); // Portrait image

        personDetails = createElementHelper("div", { class: "personDetails" }); // Holder for direcrtor details
        personHolder.appendChild(personDetails);
        personDetails.appendChild(createElementHelper("div", { class: "personName", innerHTML: `${this.forenames ? this.forenames : ''} ${this.surname ? this.surname : ''}` })); // Directors name
        personDetails.appendChild(createElementHelper("div", { class: "personPosition", innerHTML: this.role })); // Director role
        // personDetails.appendChild(createElementHelper("div", { innerHTML: `Appointed: ${moment(this.appointedOn, "YYYYMMDD").format("Do MMMM YYYY")}` })); // Appointment date
        // personDetails.appendChild(createElementHelper("div", { class: `personStatus ${(this.isDirectorActive()) ? "directorStatusActive" : "directorStatusResigned"}`, innerHTML: this.formatDirectorStatus() })); // Director status, with resignation date if applicable
        personDetails.appendChild(createElementHelper("a", { href: `${urlPath}/directors?person_number=${this.personNumber}`, innerHTML: "Details", target: "_blank", class: "actionControl" })); // Link to directors page for more detail

        return personHolder;
    }
}

class CompanyLocation {
    constructor(postcodeWithSpaces, laName, constituency, lepName, nuts1Name, registeredPostcode, standardisedCity, latitude, longitude, oecdfuaCorePlusCommuting, occupationCounts, maxLocationTotalOccupationCount) {
        this.postcodeWithSpaces = postcodeWithSpaces;
        this.laName = laName;
        this.constituency = constituency;
        this.lepName = lepName;
        this.nuts1Name = nuts1Name;
        this.registeredPostcode = registeredPostcode;
        this.standardisedCity = standardisedCity;
        this.latitude = latitude;
        this.longitude = longitude;
        this.oecdfuaCorePlusCommuting = oecdfuaCorePlusCommuting;
        this.occupationCounts = new OccupationCounts(occupationCounts, maxLocationTotalOccupationCount, this);
    }

    hasValidCoordinates() {
        return isFinite(this.latitude) && Math.abs(this.latitude) <= 90 && isFinite(this.longitude) && Math.abs(this.longitude) <= 180;
    }

    createLocationsTableRow(keys) {
        const row = coreFunctions.createElementHelper("tr", {class: "locations-table__table-row"});
        row.appendChild(coreFunctions.createElementHelper("td", {innerHTML: `<span class='locations-table__badge locations-table__badge--uk'>UK</span>${this.registeredPostcode ? "<span class='locations-table__badge locations-table__badge--registered'>Registered</span>" : ''}`, class: "locations-table__table-data", "data-field": "location"}));
        keys.forEach(key => row.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this[key] && this[key].length ? this[key] : "-", class: "locations-table__table-data", "data-field": key})));
        if (this.occupationCounts.countsAvailable()) {
            row.appendChild(this.occupationCounts.createLocationTableCells({allOccupationGroups: Object.keys(OccupationGroup)}));
            const chartCell = row.appendChild(coreFunctions.createElementHelper("td", {class: "locations-table__table-data locations-table__table-data--numeric", "data-field": "occupationCounts"}));
            setTimeout(() => {
                const barchart = this.occupationCounts.createOccupationsStackedBarchart({displayLegend: false, displayTooltips: false, minimised: true});
                barchart.classList.add("locations-table__barchart-holder");
                chartCell.appendChild(barchart);
            }, 100);

        } else {
            row.lastChild.colSpan = Object.keys(OccupationGroup).length + 3; // Fill space that would have contained with occupation details
        }
        return row;
    }

    // Return a specific location field
    getLocationField(field) {
        return this[field];
    }

    // Filter locations by whether they represent the registered address or not.
    filterByRegisteredPostcode(registeredAddressRequested) {
        return this.registeredPostcode == registeredAddressRequested;
    }

    createLocationMapMarker(companyName) {
        let mapIcon, mapMarker;
        if (this.hasValidCoordinates()) {
            if (this.occupationCounts.countsAvailable()) {
                mapIcon = this.occupationCounts.createOccupationCountsMapIcon();
            } else {
                const CompanyIcon = L.Icon.extend({
                    options: {
                        iconSize:     [42, 32],
                        iconAnchor:   [21, 31],
                        popupAnchor:  [0, -30]
                    }
                });
                mapIcon = new CompanyIcon({iconUrl: this.registeredPostcode ? `${urlPath}/images/marker-company-hq.svg` : `${urlPath}/images/marker-company.svg`});
            }
            mapMarker = L.marker([this.latitude, this.longitude], {icon: mapIcon}).bindPopup(this.createCompanyLocationMarkerPopup(companyName));
        }
        return mapMarker;
    }

    createCompanyLocationMarkerPopup(companyName) {
        const popup = L.popup({className: "locations-map__popup"});
        popup.setContent(this.createCompanyLocationMarkerPopupContent(companyName));
        return popup;
    }

    createCompanyLocationMarkerPopupContent(companyName) {
        const popupHolder = coreFunctions.createElementHelper("div", {class: "locations-map__popup-content"});
        popupHolder.appendChild(coreFunctions.createElementHelper("h4", {innerHTML: `<strong>${(this.registeredPostcode) ? "Registered location" : "Operating location"}</strong>`, class: "locations-map__popup-title"}));

        const fieldHolder = popupHolder.appendChild(coreFunctions.createElementHelper("div", {class: "locations-map__popup-field-holder"}));

        fieldHolder.appendChild(coreFunctions.createElementHelper("p", {innerHTML: `<strong>Postcode:</strong> ${this.postcodeWithSpaces}`, class: "locations-map__popup-field"}));
        fieldHolder.appendChild(coreFunctions.createElementHelper("p", {innerHTML: `<strong>Local authority:</strong> ${this.laName}`, class: "locations-map__popup-field"}));
        fieldHolder.appendChild(coreFunctions.createElementHelper("p", {innerHTML: `<strong>Constituency:</strong> ${this.constituency}`, class: "locations-map__popup-field"}));
        fieldHolder.appendChild(coreFunctions.createElementHelper("p", {innerHTML: `<strong>Region:</strong> ${this.nuts1Name}`, class: "locations-map__popup-field"}));

        if (this.occupationCounts.countsAvailable()) {
            fieldHolder.appendChild(coreFunctions.createElementHelper("p", {innerHTML: `<strong>Site employees:</strong> ${this.occupationCounts.totalCount.toLocaleString()}`, class: "locations-map__popup-field"}));
            popupHolder.appendChild(this.occupationCounts.createOccupationsStackedBarchart());
        }
        return popupHolder;
    }
}

class CompanyRtic {
    constructor(companyNumber, rticCode, rticName, rticSectorCode, rticSectorName, cic) {
        this.rticCode = rticCode;
        this.rticName = rticName;
        this.rticSectorCode = rticSectorCode;
        this.rticSectorName = rticSectorName;
        this.companyNumber = companyNumber;
        this.cic = cic;
    }

    getRticTomSelectOption() {
        return {
            class: this.rticSectorCode,
            value: this.rticCode,
            name: this.rticName,
            sectorName: this.rticSectorName
        };
    }

    getRticTomSelectOptionGroup() {
        return [this.rticSectorCode, {
            value: this.rticSectorCode,
            label: this.rticSectorName
        }];
    }

    getRefreshedRticTomSelectOptionGroup() {
        return [this.rticSectorCode, this.rticSectorName];
    }
}

class Financials {
    constructor(year, turnover, preTaxProfit, profitAfterTax, cash, totalCurrentAssets, totalAssets, totalLiabilities, shareholderFunds, netWorth, numberOfEmployees, debtorsDueAfterOneYear, debtors, tradeDebtors, groupDebtors, creditors, exportTotal, wagesAndSalaries, depreciation, bankOverdraft, ebitda, auditors, anomaliesDetected, employeesAnomalousProbability, netAssets) {
        this.year = year;
        this.turnover = turnover;
        this.preTaxProfit = preTaxProfit;
        this.profitAfterTax = profitAfterTax;
        this.cash = cash;
        this.totalCurrentAssets = totalCurrentAssets;
        this.totalAssets = totalAssets;
        this.totalLiabilities = totalLiabilities;
        this.shareholderFunds = shareholderFunds;
        this.netWorth = netWorth;
        this.numberOfEmployees = numberOfEmployees;
        this.debtorsDueAfterOneYear = debtorsDueAfterOneYear;
        this.debtors = debtors;
        this.tradeDebtors = tradeDebtors;
        this.groupDebtors = groupDebtors;
        this.creditors = creditors;
        this.export = exportTotal;
        this.wagesAndSalaries = wagesAndSalaries;
        this.depreciation = depreciation;
        this.bankOverdraft = bankOverdraft;
        this.ebitda = ebitda;
        this.auditors = auditors;
        this.anomaliesDetected = anomaliesDetected;
        this.employeesAnomalousProbability = employeesAnomalousProbability;
        this.netAssets = netAssets;
    }

    // Check whether the financial field has data
    isFieldAvailable(value) {
        return value != undefined;
    }

    // Add financial data cell to table
    createFinancialsTableCell(field, yearsIndex, formatFinancialField = true) {
        let tdClass = `${(yearsIndex == 0) ? "cellMobileVisible" : ""}`;
        if(this.anomaliesDetected === true) {
            tdClass += ' warn__anomalies-detected';
        }
        return coreFunctions.createElementHelper("td", { class: tdClass, innerHTML: `${(this.isFieldAvailable(this[field])) ? (formatFinancialField ? this.formatFinancialValue(this[field], field) : this[field]) : "Unavailable"}` });
    }

    // Heading cell for financials table
    createFinancialsTableHeading(key, index, totalYears) {
        let dateHeadingCell, dateHeadingCellContent;
        const financialsObject = this;
        const createElementHelper = coreFunctions.createElementHelper;

        // By default first column is visible on mobile
        dateHeadingCell = createElementHelper("th", { class: `${(index == 0) ? "cellMobileVisible" : ""}` });
        dateHeadingCellContent = createElementHelper("span", { class: "dateHeadingCell" });
        dateHeadingCell.appendChild(dateHeadingCellContent);

        // Add go back to previous year control on mobile
        if (index > 0) {
            dateHeadingCellContent.appendChild(createElementHelper("button", { class: "actionControl yearArrow left", innerHTML: "&nbsp;" }, { "click": function () { financialsObject.toggleFinancialYearsMobile(this, -1); } }));
        }
        // Add go to next year on mobile
        if (index < (totalYears - 1)) {
            dateHeadingCellContent.appendChild(createElementHelper("button", { class: "actionControl yearArrow right", innerHTML: "&nbsp;" }, { "click": function () { financialsObject.toggleFinancialYearsMobile(this, 1); } }));
        }

        // Add year end to year heading, take the last two digits and add one
        const text = (key.toLowerCase() == 'year' && this[key]) ? `${this[key]}/${(this[key] % 100) + 1}` : this[key];
        dateHeadingCellContent.appendChild(createElementHelper("span", { class: "date", innerHTML: text + ( this.anomaliesDetected ? '<sup>*</sup>' : '' ) }));
        dateHeadingCellContent.appendChild(createElementHelper("span", { class: "date mobileDate", innerHTML: text + ( this.anomaliesDetected ? '<sup>*</sup>' : '' ) }));

        return dateHeadingCell;
    }

    // Format value
    formatFinancialValue(value, field) {
        // Employees is numerical value
        if (field == "numberOfEmployees") {
            return value.toLocaleString();
        }
        // Format negative currency data
        else if (value < 0) {
            return `<span class='orange'>-£${Math.abs(value).toLocaleString()}</span>`;
        } else {
            return `£${value.toLocaleString()}`;
        }
    }

    // Add financials to table
    addFinancialsToTable(yearsIndex, yearsTotal) {
        let financialsObject = this;

        // Data fields
        const financialElementFields = ["YEAR", "TURNOVER", "PRE_TAX_PROFIT", "PROFIT_AFTER_TAX", "CASH", "TOTAL_CURRENT_ASSETS", "TOTAL_ASSETS", "TOTAL_LIABILITIES", "SHAREHOLDER_FUNDS", "NET_WORTH", "NUMBER_OF_EMPLOYEES", "DEBTORS_DUE_AFTER_ONE_YEAR", "DEBTORS", "TRADE_DEBTORS", "GROUP_DEBTORS", "CREDITORS", "EXPORT", "WAGES_AND_SALARIES", "DEPRECIATION", "BANK_OVERDRAFT", "EBITDA", "AUDITORS"];

        // Col details for each financial year
        document.getElementById("financialsTableColgroup").appendChild(coreFunctions.createElementHelper("col", { class: `financesCol${(yearsIndex == 0) ? " columnMobileVisible" : ""}` }));

        // Loop through each field in object and add content to the page
        Object.keys(financialsObject).forEach(function (financialsKey, i) {
            console.log(financialsKey);
            if (i == 0) {
                document.querySelector(`#financialsTable tr[field='${financialElementFields[i]}']`).appendChild(financialsObject.createFinancialsTableHeading(financialsKey, yearsIndex, yearsTotal))
            } else {
                document.querySelector(`#financialsTable tr[field='${financialElementFields[i]}']`).innerHTML += financialsObject.createFinancialsTableCell(financialsKey, yearsIndex);
            }
        });
    }

    // Move through different financial years in mobile view
    toggleFinancialYearsMobile(element, change) {
        let cell, financialsTable, newColumnIndex;
        cell = element.closest("th");
        financialsTable = element.closest(".financialDetailsTable");
        newColumnIndex = Array.from(cell.parentNode.children).indexOf(cell) + change; // Calculate new position in column array, from position of cell in row
        Array.from(financialsTable.querySelectorAll(".cellMobileVisible")).forEach(cell => cell.classList.remove("cellMobileVisible")); // Hide the currently visible column
        Array.from(financialsTable.querySelectorAll("tr")).forEach(row => row.children[newColumnIndex].classList.add("cellMobileVisible")); // Show the next column by looping through each row
    }
}

class PersonsWithSignificantControl {
    constructor () {
        this.persons = [];
    }

    personsWithSignificantControlAvailable() {
        return this.persons.length > 0;
    }

    createPersonsWithSignificantControlElement() {
        let fieldHolder, fieldContentWrapper;
        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "personsWithSignificantControl"});
        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldContentWrapper.appendChild(coreFunctions.createElementHelper("h4", { innerHTML: "Persons with significant control" }));
        fieldContentWrapper.appendChild(this.createPersonsWithSignificantHolder())
        return fieldHolder;
    }

    createPersonsWithSignificantHolder() {
        let personsWithSignificantControlHolder = coreFunctions.createElementHelper("div", {class: "personsWithSignificantControlHolder"});
        this.persons.forEach(person => personsWithSignificantControlHolder.appendChild(person.createPersonWithSignificantControlElement()));
        return personsWithSignificantControlHolder;
    }

    processPersonWithSignificantControl(companyNumber, name, dateOfBirth, countryOfResidence, type, natureOfControl) {
        let personWithSignificantControl;
        if (personWithSignificantControl = this.findPersonWithSignificantControl(name)) {
            personWithSignificantControl.addNatureOfControl(natureOfControl);
        } else {
            this.addNewPersonWithSignificantControl(companyNumber, name, dateOfBirth, countryOfResidence, type, natureOfControl);
        }
    }

    findPersonWithSignificantControl(name) {
        let personWithSignificantControl;
        if (personWithSignificantControl = this.persons.find(person => person.name == name)) {
            return personWithSignificantControl;
        } else {
            return false;
        }
    }

    addNewPersonWithSignificantControl(companyNumber, name, dateOfBirth, countryOfResidence, type, natureOfControl) {
        let newPersonWithSignificantControl = new PersonWithSignificantControl(companyNumber, name, dateOfBirth, countryOfResidence, type, natureOfControl);
        this.persons.push(newPersonWithSignificantControl);
        return newPersonWithSignificantControl;
    }

}

class PersonWithSignificantControl {
    constructor (companyNumber, name, dateOfBirth, countryOfResidence, type, natureOfControl) {
        this.companyNumber = companyNumber;
        this.name = name;
        this.dateOfBirth = dateOfBirth;
        this.countryOfOrigin = countryOfResidence;
        this.type = type;
        this.naturesOfControl = [natureOfControl];
    }

    addNatureOfControl(natureOfControl) {
        if (natureOfControl != undefined) {
            this.naturesOfControl.push(coreFunctions.capitaliseFirstLetter(natureOfControl.replace(/-/g, " ")));
        }
    }

    createPersonWithSignificantControlElement() {
        let personHolder, personDetails;
        personHolder = coreFunctions.createElementHelper("div", {class: "personHolder"});
        personHolder.appendChild(coreFunctions.createElementHelper("div", {class: "portraitImageHolder"})).appendChild(coreFunctions.createElementHelper("img", {src: `${urlPath}/images/director.svg`, class: "portrait"})); // Portrait image
        personDetails = personHolder.appendChild(coreFunctions.createElementHelper("div", {class: "personDetails"}));
        personDetails.appendChild(coreFunctions.createElementHelper("div", {class: "personName", innerHTML: this.name})); // Name
        personDetails.appendChild(coreFunctions.createElementHelper("div", {class: "personPosition", innerHTML: this.type})); // Type of control
        if (this.dateOfBirth) {
            personDetails.appendChild(coreFunctions.createElementHelper("div", {innerHTML: `<span class='personsWithControlField'>Born: </span> <span class='personsWithControlValue'>${moment(this.dateOfBirth, "MM/YYYY").format("MMMM YYYY")}</span>`})); // Date of birth
        }
        if (this.countryOfOrigin) {
            personDetails.appendChild(coreFunctions.createElementHelper("div", {innerHTML: `<span class='personsWithControlField'>Country of origin: </span> <span class='personsWithControlValue'>${this.countryOfOrigin}</span>`})); // Country of origin
        }
        personDetails.appendChild(coreFunctions.createElementHelper("div", {class: "natureOfControlHolder", innerHTML: `<span class='personsWithControlField'>Nature of control: </span> <span class='personsWithControlValue'><span class='natureOfControl'>${this.naturesOfControl.join("</span><span class='natureOfControl'>")}</span></span>`}));
        return personHolder;
    }

}

class ShareHolders {
    constructor (companyNumber) {
        this.elements = [];
        this.companyNumber = companyNumber;
        this.pageNumber = 1;
        this.tableHeadings = ["Name", "Currency", "Share type", "Value", "Percent"];
        this.tableElementsPerPage = 20;
        this.addCountToTable = true;
    }

    addNewShareHolder(companyNumber, shareType, shareHolderName, allInfo, totalShares, currency, totalShareValue, sharePercent) {
        let newShareHolder = new Shareholder(companyNumber, shareType, shareHolderName, allInfo, totalShares, currency, totalShareValue, sharePercent);
        this.elements.push(newShareHolder);
        return newShareHolder;
    }

    shareHoldersAvailable() {
        return this.elements.length > 0;
    }

    getTotalShareCount() {
        return (this.shareHoldersAvailable) ? this.elements[0].totalShares : 0;
    }

    addTotalCount() {
        return coreFunctions.createElementHelper("div", {class:"tableCount", innerHTML: `Total shares: ${this.getTotalShareCount().toLocaleString()}`});
    }

    getShareHoldersCount() {
        return this.shareHolders.length;
    }
}

class Shareholder {
    constructor (companyNumber, shareType, shareHolderName, allInfo, totalShares, currency, totalShareValue, sharePercent) {
        this.companyNumber = companyNumber;
        this.shareType = shareType;
        this.shareHolderName = shareHolderName;
        this.allInfo = allInfo;
        this.totalShares = totalShares;
        this.currency = currency;
        this.totalShareValue = totalShareValue;
        this.sharePercent = sharePercent;
    }

    createTableRow() {
        let tableRow;
        tableRow = document.createElement("tr");
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.shareHolderName, "data-shareholderfield": "shareHolderName"}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.currency}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.shareType}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: (this.totalShareValue == undefined || isNaN(this.totalShareValue)) ? this.totalShareValue : Number(this.totalShareValue.toFixed(2)).toLocaleString()}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.formatSharePercent()}));
        return tableRow;
    }

    formatSharePercent() {
        if (!this.sharePercent)
            return;
        let sharePercentFormatted = "N/A";
        if (!isNaN(this.sharePercent)) {
            sharePercentFormatted = this.sharePercent.toFixed(2);
        }
        return sharePercentFormatted;
    }
}

class UrlMatchStats {
    constructor (matchedWebsite, matchedWebsiteScore, matchedWebsiteReasoning, matchedWebsiteSources) {
        this.matchedWebsite = matchedWebsite;
        this.matchedWebsiteScore = matchedWebsiteScore;
        this.matchedWebsiteReasoning = matchedWebsiteReasoning;
        this.matchedWebsiteSources = matchedWebsiteSources;
    }

    addUrlMatchDetails() {
        let urlMatchStatsFragment, urlMatchStats;
        urlMatchStatsFragment = document.createDocumentFragment();
        urlMatchStatsFragment.appendChild(coreFunctions.createElementHelper("h3", {innerHTML: "Website match stats"}));
        urlMatchStats =  urlMatchStatsFragment.appendChild(coreFunctions.createElementHelper("ul",{class: "urlMatchStats"}));
        urlMatchStats.appendChild(coreFunctions.createElementHelper("li", {innerHTML: `<span class="urlMatchStatsField">Website: </span><a target="_blank" href="${coreFunctions.formatUrl(this.matchedWebsite)}">${this.matchedWebsite}</a>`}));
        urlMatchStats.appendChild(coreFunctions.createElementHelper("li", {innerHTML: `<span class="urlMatchStatsField">Score: </span>${this.matchedWebsiteScore}`}));
        urlMatchStats.appendChild(coreFunctions.createElementHelper("li", {innerHTML: `<span class="urlMatchStatsField">Reasoning: </span><br>${this.matchedWebsiteReasoning.join("<br>").replace("RedFlag", "Previous")}`}));
        urlMatchStats.appendChild(coreFunctions.createElementHelper("li", {innerHTML: `<span class="urlMatchStatsField">Sources: </span>${this.matchedWebsiteSources.join(", ")}`}));
        document.getElementById("reportMismatchedUrlMatchStats").appendChild(urlMatchStatsFragment);
    }
}

class _360GivingFunding {
    constructor(companyNumber) {
        this.companyNumber = companyNumber;
        this.elements = [];
        this.tableHeadings = ["Title", "Description", "Award date", "Currency", "Amount awarded", "Funder name", "Grant programme title"];
        this.pageNumber = 1;
        this.tableElementsPerPage = 20;
    }

    grantFundingAvailable() {
        return this.elements.length > 0;
    }

    getElementCount() {
        return this._360GivingFunding.length;
    }

    add360GivingFunding(_360GivingFunding) {
        const _360GivingFundingObject = this;
        _360GivingFunding.forEach(grant => {
            _360GivingFundingObject.new360GivingGrant(grant.CompanyNumber, grant.id, grant.title, grant.currency, grant.awardDate, grant.description, grant.amountAwarded, grant.grantProgrammeTitle, grant.funderName, grant.recipientName);
        });
        return this;
    }

    new360GivingGrant(companyNumber, id, title, currency, awardDate, description, amountAwarded, grantProgrammeTitle, funderName, recipientName) {
        let new360GivingGrant = new _360GivingFundingGrant(companyNumber, id, title, currency, awardDate, description, amountAwarded, grantProgrammeTitle, funderName, recipientName);
        this.elements.push(new360GivingGrant);
        return new360GivingGrant;
    }

    checkEventInPeriod(startDateParsed) {
        return (this.grantFundingAvailable() && this.elements.find(el => moment(el.awardDate).isSameOrAfter(startDateParsed)) != undefined);
    }
}

class _360GivingFundingGrant {
    constructor(companyNumber, id, title, currency, awardDate, description, amountAwarded, grantProgrammeTitle, funderName, recipientName) {
        this.companyNumber = companyNumber;
        this.id = id;
        this.title = title;
        this.currency = currency;
        this.awardDate = awardDate;
        this.description = description;
        this.amountAwarded = amountAwarded;
        this.grantProgrammeTitle = grantProgrammeTitle;
        this.funderName = funderName;
        this.recipientName = recipientName;
    }

    createTableRow() {
        let tableRow;
        tableRow = document.createElement("tr", {"data_360givinggrantid": this.id});
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.title}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.description}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: moment(this.awardDate).format("YYYY-MM-DD")}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.currency}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.amountAwarded.toLocaleString()}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.funderName}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.grantProgrammeTitle}));
        return tableRow;
    }
}

class InnovateUkFunding {
    constructor (companyNumber) {
        this.companyNumber = companyNumber;
        this.tableHeadings = ["Project title", "Competition title", "Award offered", "Start date", "End date", "Sector", "Enterprise size"];
        this.elements = [];
        this.pageNumber = 1;
        this.tableElementsPerPage = 20;
    }

    addInnovateUkFundingGrants(innovateUkFunding) {
        const innovateUkFundingObject = this;
        innovateUkFunding.forEach(grant => innovateUkFundingObject.newInnovateUkFundingGrant(grant.CompanyNumber, grant.CompetitionYear, grant.ProjectStartDate, grant.ProjectEndDate, grant.AwardOffered, grant.TotalCosts, grant.ActualSpendtoDate, grant.CompetitionReference, grant.CompetitionTitle, grant.ProgrammeTitle, grant.Sector, grant.ApplicationNumber, grant.ProjectNumber, grant.ProjectTitle, grant.PublicDescription, grant.InnovateUKProductType, grant.ParticipantName, grant.IsLeadParticipant, grant.ParticipantWithdrawnFromProject, grant.ProjectStatus, grant.EnterpriseSize, grant.Postcode, grant.IndustrialStrategyChallengeFundISCF));
        return this;
    }

    newInnovateUkFundingGrant(companyNumber, competitionYear, projectStartDate, projectEndDate, awardOffered, totalCosts, actualSpendtoDate, competitionReference, competitionTitle, programmeTitle, sector, applicationNumber, projectNumber, projectTitle, publicDescription, innovateUKProductType, participantName, isLeadParticipant, participantWithdrawnFromProject, projectStatus, enterpriseSize, postcode, industrialStrategyChallengeFundISCF) {
        let newInnovateUkGrant = new InnovateUkFundingGrant(companyNumber, competitionYear, projectStartDate, projectEndDate, awardOffered, totalCosts, actualSpendtoDate, competitionReference, competitionTitle, programmeTitle, sector, applicationNumber, projectNumber, projectTitle, publicDescription, innovateUKProductType, participantName, isLeadParticipant, participantWithdrawnFromProject, projectStatus, enterpriseSize, postcode, industrialStrategyChallengeFundISCF);
        this.elements.push(newInnovateUkGrant);
        return newInnovateUkGrant;
    }

    innovateUkFundingGrantsAvailable() {
        return this.elements.length > 0;
    }

    checkEventInPeriod(startDateParsed) {
        return (this.innovateUkFundingGrantsAvailable() && this.elements.find(el => moment(el.projectStartDate).isSameOrAfter(startDateParsed)) != undefined);
    }
}

class InnovateUkFundingGrant {
    constructor (companyNumber, competitionYear, projectStartDate, projectEndDate, awardOffered, totalCosts, actualSpendtoDate, competitionReference, competitionTitle, programmeTitle, sector, applicationNumber, projectNumber, projectTitle, publicDescription, innovateUKProductType, participantName, isLeadParticipant, participantWithdrawnFromProject, projectStatus, enterpriseSize, postcode, industrialStrategyChallengeFundISCF) {
        this.companyNumber = companyNumber;
        this.competitionYear = competitionYear;
        this.projectStartDate = projectStartDate;
        this.projectEndDate = projectEndDate;
        this.awardOffered = awardOffered;
        this.totalCosts = totalCosts;
        this.actualSpendtoDate = actualSpendtoDate;
        this.competitionReference = competitionReference;
        this.competitionTitle = competitionTitle;
        this.programmeTitle = programmeTitle;
        this.sector = sector;
        this.applicationNumber = applicationNumber;
        this.projectNumber = projectNumber;
        this.projectTitle = projectTitle;
        this.publicDescription = publicDescription;
        this.innovateUKProductType = innovateUKProductType;
        this.participantName = participantName;
        this.isLeadParticipant = isLeadParticipant;
        this.participantWithdrawnFromProject = participantWithdrawnFromProject;
        this.projectStatus = projectStatus;
        this.enterpriseSize = enterpriseSize;
        this.postcode = postcode;
        this.industrialStrategyChallengeFundISCF = industrialStrategyChallengeFundISCF;
    }

    createTableRow() {
        let documentFragment, overviewTableRow;
        documentFragment = document.createDocumentFragment();
        overviewTableRow = this.createGrantOverviewTableRow();
        documentFragment.appendChild(overviewTableRow);
        documentFragment.appendChild(this.createInnovateUkFundingGrantExtraDetailsTableRow(overviewTableRow.children.length));
        return documentFragment;
    }

    createGrantOverviewTableRow() {
        const innovateUkFundingGrant = this;
        let tableRow = coreFunctions.createElementHelper("tr", {"data-projectnumber": this.projectNumber});

        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.projectTitle}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.competitionTitle}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: `£${this.awardOffered.toLocaleString()}`}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: moment(this.projectStartDate).format("YYYY-MM-DD")}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: moment(this.projectEndDate).format("YYYY-MM-DD")}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.sector}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.enterpriseSize}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {class: "innovateUkExtraDetailsCell"})).appendChild(document.createElement("div")).appendChild(coreFunctions.createElementHelper("button", {"data-projectnumber": this.projectNumber, "data-companynumber": this.companyNumber, class: "actionControl extra-fields", innerHTML: "<span class='iconRotate'>&nbsp;</span>Details" }, { click: function () {  innovateUkFundingGrant.toggleExtraDetailsVisibility(this) } }));

        return tableRow;
    }

    toggleExtraDetailsVisibility(element) {
        if (element.classList.contains("extraFieldsVisible")) {
            element.classList.remove("extraFieldsVisible");
            document.querySelector(`.innovateUkFundingExtraDetailsRow[data-companynumber='${this.companyNumber}'][data-projectnumber='${this.projectNumber}']`).classList.add("hidden");
        } else {
            element.classList.add("extraFieldsVisible");
            document.querySelector(`.innovateUkFundingExtraDetailsRow[data-companynumber='${this.companyNumber}'][data-projectnumber='${this.projectNumber}']`).classList.remove("hidden");
        }
    }

    createInnovateUkFundingGrantExtraDetailsTableRow(colSpan) {
        let tableRow, tableCell, innovateUkFundingGrantExtraDetailsHolder;
        tableRow = coreFunctions.createElementHelper("tr", {"data-projectnumber": this.projectNumber, "data-companynumber": this.companyNumber, class: "innovateUkFundingExtraDetailsRow hidden"});
        tableCell = tableRow.appendChild(coreFunctions.createElementHelper("td", {colspan: colSpan}));
        innovateUkFundingGrantExtraDetailsHolder = tableCell.appendChild(coreFunctions.createElementHelper("div", {class: "fieldHolderFlexContainer"}));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("competitionReference", "Competition reference", this.competitionReference));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("competitionYear", "Competition year", this.competitionYear));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("applicationNumber", "Application number", this.applicationNumber));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("totalCosts", "Total costs", `£${this.totalCosts.toLocaleString()}`));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("actualSpendtoDate", "Spend to date", `£${this.actualSpendtoDate.toLocaleString()}`));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("projectStatus", "Project status", this.projectStatus));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("participantWithdrawnFromProject", "Withdrawn from project?", this.participantWithdrawnFromProject));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("isLeadParticipant", "Is lead participant?", this.isLeadParticipant));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("industrialStrategyChallengeFundISCF", "Industrial strategy challenge fund?", this.industrialStrategyChallengeFundISCF));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("publicDescription", "Description", this.publicDescription));
        innovateUkFundingGrantExtraDetailsHolder.appendChild(this.createInnovateUkFundingGrantExtraDetail("postcode", "Postcode", this.postcode));
        return tableRow;
    }

    createInnovateUkFundingGrantExtraDetail(field, title, content) {
        let extraDetailHolder, extraDetailContentWrapper;
        extraDetailHolder = coreFunctions.createElementHelper("div", {class:"fieldHolder", "data-name": field});
        extraDetailContentWrapper = extraDetailHolder.appendChild(coreFunctions.createElementHelper("div", {class: "fieldContentWrapper"}));
        extraDetailContentWrapper.appendChild(coreFunctions.createElementHelper("h4", {innerHTML: title}));
        extraDetailContentWrapper.appendChild(coreFunctions.createElementHelper("div", {class: "fieldDataHolder", innerHTML: content, "data-field": field}));
        return extraDetailHolder;
    }
}

class CompanyJobPostings {
    constructor(companyNumber) {
        this.companyNumber = companyNumber;
        this.companyJobPostingsBySOC4 = [];
        this.companyJobPostingsPerPage = 10;
        this.pageNumber = 1;
        this.companyJobPostingsTableHeadings = ["SOC4 code", "SOC4 name", "Postings", "Median salary"];
        this.companyJobPostingsBySkill = [];
        this.companyJobBySkillPostingsPerPage = 10;
        this.companyJobPostingsBySkillPageNumber = 1;
        this.companyJobBySkillTableHeadings = ["Skill name", "Unique postings"];
        this.companyJobPostingsByLot = [];
        this.companyJobByLotPostingsPerPage = 10;
        this.companyJobPostingsByLotPageNumber = 1;
        this.companyJobPostingsByLotTableHeadings = ["LOT code", "LOT name", "Unique postings", "Median salary"];
        this.tableElementsPerPage = 30;
    }

    processCompanyJobPostings(companyJobPostings) {
        const companyJobPostingsClass = this;
        companyJobPostings.forEach(posting => companyJobPostingsClass.newCompanyJobPosting(posting.CompanyNumber, posting.SOC4Code, posting.SOC4Name, posting.Postings, posting.MedianSalary));
    }

    newCompanyJobPosting(companyNumber, SOC4Code, SOC4Name, postings, medianSalary) {
        let newCompanyJobPosting = new CompanyJobPosting(companyNumber, SOC4Code, SOC4Name, postings, medianSalary);
        this.companyJobPostingsBySOC4.push(newCompanyJobPosting);
        return newCompanyJobPosting;
    }

    companyJobPostingsAvailable() {
        return this.companyJobPostingsBySOC4.length > 0;
    }

    createCompanyJobPostingsElement() {
        let fieldHolder, fieldContentWrapper, tableHolder, titleElement, lightcastDataExplainer;
        lightcastDataExplainer = `<span class='lightcast-tooltip'>
          Job postings data is sourced from <a href='https://lightcast.io' target='_blank'>Lightcast</a>.<br><br>
          Lightcast classifies jobs into <a href='https://www.ons.gov.uk/methodology/classificationsandstandards/standardoccupationalclassificationsoc' target='_blank'>SOC4</a> and <a href='https://lightcast.io/resources/blog/new-occupation-taxonomy' target='_blank'>LOT</a> codes.<br><br>
          The Lightcast Occupation Taxonomy (formerly the “Global Occupation Taxonomy”) is a proprietary classification system composed of four different levels (Career Area, Occupation Group, <strong>Occupation</strong>, and Specialized Occupation).
        </span>`;

        titleElement = coreFunctions.createElementHelper("h4", { innerHTML: `Job postings (SOC4) <a class='lightcast' target='_blank' href='https://lightcast.io/'><img src='${urlPath}/images/lightcast-logo.svg'/></a>` });
        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "companyJobPostings", "data-companynumber": this.companyNumber });
        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldContentWrapper.appendChild(titleElement);
        titleElement.appendChild(this.createWidgetExplanationElement('companyJobPostings', lightcastDataExplainer));

        // tableHolder = fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", {class: "companyJobPostingsTableHolder"}));
        // tableHolder.appendChild(this.createCompanyJobPostingsTable());
        // if (this.getCompanyJobPostingsCount() > this.companyJobPostingsPerPage) {
        //     tableHolder.appendChild(this.createCompanyJobPostingsPageNumbers(tableHolder));
        // }
        return fieldHolder;
    }

    createCompanyJobPostingsTable() {
        let companyJobPostingsTable;
        companyJobPostingsTable = coreFunctions.createElementHelper("table", {class: "shareHoldersTable", "data-companynumber": this.companyNumber});
        companyJobPostingsTable.appendChild(coreFunctions.createElementHelper("thead")).appendChild(this.createCompanyJobPostingsTableHeader());
        companyJobPostingsTable.appendChild(this.addCompanyJobPostingsToTable(companyJobPostingsTable));
        return companyJobPostingsTable;
    }

    createCompanyJobPostingsTableHeader() {
        let tableHeaderRow;
        const tableHeaders = ["SOC4 code", "SOC4 name", "Postings", "Median salary"];
        tableHeaderRow = coreFunctions.createElementHelper("tr", {innerHTML: tableHeaders.reduce((previousContent, currentElement) => previousContent + `<th data-companyjobpostingstitle="${currentElement}">${currentElement}</th>`, "")});
        return tableHeaderRow;
    }

    addCompanyJobPostingsToTable(holder) {
        let existingTbody,  companyJobPostingsTbody;
        if (existingTbody = holder.querySelector("tbody")) {
            holder.removeChild(existingTbody);
        }
        companyJobPostingsTbody = document.createElement("tbody");

        this.getCompanyJobPostingsOnPage().forEach(companyJobPosting => companyJobPostingsTbody.appendChild(companyJobPosting.createTableRow()));

        return companyJobPostingsTbody;
    }

    getCompanyJobPostingsCount() {
        return this.companyJobPostingsBySOC4.length;
    }

    getCompanyJobPostingsPageCount() {
        return Math.ceil(this.getCompanyJobPostingsCount() / this.companyJobPostingsPerPage);
    }

    getCompanyJobPostingsOnPage() {
        return this.companyJobPostingsBySOC4.slice((this.pageNumber - 1) * this.companyJobPostingsPerPage, this.pageNumber * this.companyJobPostingsPerPage);
    }

    createCompanyJobPostingsPageNumbers(holder) {
        const companyJobPostingsObject = this;
        let pageNumber, existingPageNumbers, pageLinksHolder, pageNumbersStructure, totalElementsInPageNumberStructure;

        if (existingPageNumbers = holder.querySelector(".companyJobPostingsPageLinksHolder")) {
            holder.removeChild(existingPageNumbers);
        }

        pageLinksHolder = coreFunctions.createElementHelper("div", {class: "companyJobPostingsPageLinksHolder"});
        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl previous-page"},{click: function() {companyJobPostingsObject.companyJobPostingsShiftPage(-1)}}));

        pageNumbersStructure = this.getCompanyJobPostingsPageNumbersStructure();
        totalElementsInPageNumberStructure = pageNumbersStructure.length;

        for (var i = 0; i < totalElementsInPageNumberStructure; i++) {
            pageNumber = pageNumbersStructure[i];
            if (pageNumbersStructure[i - 1] && pageNumber != (pageNumbersStructure[i - 1] + 1)) {
                pageLinksHolder.appendChild(coreFunctions.createElementHelper("span", { innerHTML: ". .", class: "pageLink" }));
            }
            pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", { innerHTML: pageNumber, class: `actionControl pageLink${(pageNumber == this.pageNumber) ? " selected" : ""}`, "data-pagenumber": pageNumber }, { click: function () {  companyJobPostingsObject.companyJobPostingsPageSelect(this) } }));
        }

        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl next-page"}, {click: function() {companyJobPostingsObject.companyJobPostingsShiftPage(1)}}));
        return pageLinksHolder;
    }

    companyJobPostingsPageSelect(element) {
        let companyJobPostingsTableHolder, companyJobPostingsTable;
        companyJobPostingsTableHolder = element.closest(".companyJobPostingsTableHolder");
        companyJobPostingsTable = companyJobPostingsTableHolder.querySelector("table");
        this.pageNumber = Number(element.dataset.pagenumber);
        companyJobPostingsTableHolder.appendChild(this.createCompanyJobPostingsPageNumbers(companyJobPostingsTableHolder));
        companyJobPostingsTable.appendChild(this.addCompanyJobPostingsToTable(companyJobPostingsTable));
    }

    companyJobPostingsShiftPage(change) {
        let pageNumber, pageSelectElement;
        pageNumber = this.pageNumber + change;
        if (pageSelectElement = document.querySelector(`.fieldHolder[data-name='companyJobPostings'][data-companynumber='${this.companyNumber}'] .pageLink[data-pagenumber='${pageNumber}']`)) {
            this.companyJobPostingsPageSelect(pageSelectElement);
        }
    }

    getCompanyJobPostingsPageNumbersStructure() {
        let pageNumber, totalPages, pageStructure;

        pageNumber = this.pageNumber;
        totalPages = Math.ceil(this.getCompanyJobPostingsCount() / this.companyJobPostingsPerPage);

        if (totalPages <= 10) {
            pageStructure = [];
            for (var i = 0; i < totalPages; i++) {
                pageStructure.push(i + 1);
            }
            return pageStructure;
        } else if (pageNumber < 4) {
            return [1, 2, 3, 4, totalPages];
        } else if (pageNumber >= (totalPages - 2)) {
            return [1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            return [1, pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2, totalPages];
        }
    }

    processCompanyJobPostingsBySkill(companyJobPostingsBySkill) {
        const companyJobPostingsClass = this;
        companyJobPostingsBySkill.forEach(posting => companyJobPostingsClass.newCompanyJobBySkillPosting(posting.CompanyNumber, posting.SkillName, posting.TotalPostings, posting.UniquePostings, posting.MedianPostingDuration));
    }

    newCompanyJobBySkillPosting(companyNumber, skillName, totalPostings, uniquePostings, medianPostingDuration) {
        let newCompanyJobPostingBySkill = new CompanyJobPostingBySkill(companyNumber, skillName, totalPostings, uniquePostings, medianPostingDuration);
        this.companyJobPostingsBySkill.push(newCompanyJobPostingBySkill);
        return newCompanyJobPostingBySkill;
    }

    companyJobPostingsBySkillAvailable() {
        return this.companyJobPostingsBySkill.length > 0;
    }
    createCompanyJobPostingsBySkillElement() {
        let fieldHolder, fieldContentWrapper, tableHolder;
        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "companyJobPostingsBySkill", "data-companynumber": this.companyNumber });
        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldContentWrapper.appendChild(coreFunctions.createElementHelper("h4", { innerHTML: "Job postings by skill" }));
        // tableHolder = fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", {class: "companyJobPostingsTableHolder"}));
        // tableHolder.appendChild(this.createCompanyJobPostingsBySkillTable());
        // if (this.getCompanyJobPostingsBySkillCount() > this.companyJobBySkillPostingsPerPage) {
        //     tableHolder.appendChild(this.createCompanyJobPostingsBySkillPageNumbers(tableHolder));
        // }
        return fieldHolder;
    }

    createWidgetExplanationElement(field, explanation) {
        let widgetExplanationHolder = coreFunctions.createElementHelper("span", { class: "widgetExplanationHolder" });

        widgetExplanationHolder.appendChild(coreFunctions.createElementHelper("input", { type: "checkbox", id: `widgetExplanationCheckbox${field}` })); // Checkbox

        widgetExplanationHolder.appendChild(coreFunctions.createElementHelper("label", { for: `widgetExplanationCheckbox${field}`, innerHTML: `<span class="questionMark">?</span><span class='widgetExplanation'>${explanation}</span>` })); // Label with explanation element can be toggled on and off

        return widgetExplanationHolder;
    }

    createCompanyJobPostingsBySkillTable() {
        let companyJobPostingsBySkillTable;
        companyJobPostingsBySkillTable = coreFunctions.createElementHelper("table", {class: "shareHoldersTable", "data-companynumber": this.companyNumber});
        companyJobPostingsBySkillTable.appendChild(coreFunctions.createElementHelper("thead")).appendChild(this.createCompanyJobPostingsBySkillTableHeader());
        companyJobPostingsBySkillTable.appendChild(this.addCompanyJobPostingsBySkillToTable(companyJobPostingsBySkillTable));
        return companyJobPostingsBySkillTable;
    }

    createCompanyJobPostingsBySkillTableHeader() {
        let tableHeaderRow;
        const tableHeaders = ["Skill name", "Unique postings"];
        tableHeaderRow = coreFunctions.createElementHelper("tr", {innerHTML: tableHeaders.reduce((previousContent, currentElement) => previousContent + `<th data-companyjobpostingstitle="${currentElement}">${currentElement}</th>`, "")});
        return tableHeaderRow;
    }

    addCompanyJobPostingsBySkillToTable(holder) {
        let existingTbody, companyJobPostingsBySkillTbody;
        if (existingTbody = holder.querySelector("tbody")) {
            holder.removeChild(existingTbody);
        }
        companyJobPostingsBySkillTbody = document.createElement("tbody");

        this.getCompanyJobPostingsBySkillOnPage().forEach(companyJobPosting => companyJobPostingsBySkillTbody.appendChild(companyJobPosting.createCompanyJobPostingBySkillTableRow()));

        return companyJobPostingsBySkillTbody;
    }

    getCompanyJobPostingsBySkillCount() {
        return this.companyJobPostingsBySkill.length;
    }

    getCompanyJobPostingsBySkillPageCount() {
        return Math.ceil(this.getCompanyJobPostingsBySkillCount() / this.companyJobPostingsBySkillPerPage);
    }

    getCompanyJobPostingsBySkillOnPage() {
        return this.companyJobPostingsBySkill.slice((this.companyJobPostingsBySkillPageNumber - 1) * this.companyJobBySkillPostingsPerPage, this.companyJobPostingsBySkillPageNumber * this.companyJobBySkillPostingsPerPage);
    }

    createCompanyJobPostingsBySkillPageNumbers(holder) {
        const companyJobPostingsObject = this;
        let pageNumber, existingPageNumbers, pageLinksHolder, pageNumbersStructure, totalElementsInPageNumberStructure;

        if (existingPageNumbers = holder.querySelector(".companyJobPostingsPageLinksHolder")) {
            holder.removeChild(existingPageNumbers);
        }

        pageLinksHolder = coreFunctions.createElementHelper("div", {class: "companyJobPostingsPageLinksHolder"});
        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl previous-page"},{click: function() {companyJobPostingsObject.companyJobPostingsBySkillShiftPage(-1)}}));

        pageNumbersStructure = this.getCompanyJobPostingsBySkillPageNumbersStructure();
        totalElementsInPageNumberStructure = pageNumbersStructure.length;

        for (var i = 0; i < totalElementsInPageNumberStructure; i++) {
            pageNumber = pageNumbersStructure[i];
            if (pageNumbersStructure[i - 1] && pageNumber != (pageNumbersStructure[i - 1] + 1)) {
                pageLinksHolder.appendChild(coreFunctions.createElementHelper("span", { innerHTML: ". .", class: "pageLink" }));
            }
            pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", { innerHTML: pageNumber, class: `actionControl pageLink${(pageNumber == this.companyJobPostingsBySkillPageNumber) ? " selected" : ""}`, "data-pagenumber": pageNumber }, { click: function () {  companyJobPostingsObject.companyJobPostingsBySkillPageSelect(this) } }));
        }

        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl next-page"}, {click: function() {companyJobPostingsObject.companyJobPostingsBySkillShiftPage(1)}}));
        return pageLinksHolder;
    }

    companyJobPostingsBySkillPageSelect(element) {
        let companyJobPostingsTableHolder, companyJobPostingsTable;
        companyJobPostingsTableHolder = element.closest(".companyJobPostingsTableHolder");
        companyJobPostingsTable = companyJobPostingsTableHolder.querySelector("table");
        this.companyJobPostingsBySkillPageNumber = Number(element.dataset.pagenumber);
        companyJobPostingsTableHolder.appendChild(this.createCompanyJobPostingsBySkillPageNumbers(companyJobPostingsTableHolder));
        companyJobPostingsTable.appendChild(this.addCompanyJobPostingsBySkillToTable(companyJobPostingsTable));
    }

    companyJobPostingsBySkillShiftPage(change) {
        let pageNumber, pageSelectElement;
        pageNumber = this.companyJobPostingsBySkillPageNumber + change;
        if (pageSelectElement = document.querySelector(`.fieldHolder[data-name='companyJobPostingsBySkill'][data-companynumber='${this.companyNumber}'] .pageLink[data-pagenumber='${pageNumber}']`)) {
            this.companyJobPostingsBySkillPageSelect(pageSelectElement);
        }
    }

    getCompanyJobPostingsBySkillPageNumbersStructure() {
        let pageNumber, totalPages, pageStructure;

        pageNumber = this.companyJobPostingsBySkillPageNumber;
        totalPages = Math.ceil(this.getCompanyJobPostingsBySkillCount() / this.companyJobBySkillPostingsPerPage);

        if (totalPages <= 10) {
            pageStructure = [];
            for (var i = 0; i < totalPages; i++) {
                pageStructure.push(i + 1);
            }
            return pageStructure;
        } else if (pageNumber < 4) {
            return [1, 2, 3, 4, totalPages];
        } else if (pageNumber >= (totalPages - 2)) {
            return [1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            return [1, pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2, totalPages];
        }
    }

    processCompanyJobPostingsByLot(companyJobPostingsByLot) {
        const companyJobPostingsClass = this;
        companyJobPostingsByLot.forEach(posting => companyJobPostingsClass.newCompanyJobByLotPosting(posting.CompanyNumber, posting.LotCode, posting.LotName, posting.Postings, posting.MedianSalary));
    }

    newCompanyJobByLotPosting(companyNumber, lotCode, lotName, postings, medianSalary) {
        let newCompanyJobPostingByLot = new CompanyJobPostingByLot(companyNumber, lotCode, lotName, postings, medianSalary);
        this.companyJobPostingsByLot.push(newCompanyJobPostingByLot);
        return newCompanyJobPostingByLot;
    }

    companyJobPostingsByLotAvailable() {
        return this.companyJobPostingsByLot.length > 0;
    }
    createCompanyJobPostingsByLotElement() {
        let fieldHolder, fieldContentWrapper, tableHolder, lightcastDataExplainer, titleElement;

        lightcastDataExplainer = "Job postings data is sourced from <a href='https://help.thedatacity.com/knowledge/lightcast-data' target='_blank'>Lightcast</a>.</br></br>The LOT data shown here is Occupation, Level 3, you can find out more about this <a href='https://lightcast.io/our-taxonomies#Using%20Skills%20To%20Understand%20Occupations' target='_blank' rel='noopener'>here</a>.";
        titleElement = coreFunctions.createElementHelper("h4", { innerHTML: `Job postings (LOT)<a class='lightcast' target='_blank' href='https://lightcast.io/our-taxonomies#Using%20Skills%20To%20Understand%20Occupations'><img src='${urlPath}/images/lightcast-logo.svg'/></a>` });

        fieldHolder = coreFunctions.createElementHelper("div", { class: "fieldHolder", "data-name": "companyJobPostingsByLot", "data-companynumber": this.companyNumber });
        fieldContentWrapper = fieldHolder.appendChild(coreFunctions.createElementHelper("div", { class: "fieldContentWrapper" }));
        fieldContentWrapper.appendChild(titleElement);
        titleElement.appendChild(this.createWidgetExplanationElement('companyJobPostingsByLot', lightcastDataExplainer));

        // tableHolder = fieldContentWrapper.appendChild(coreFunctions.createElementHelper("div", {class: "companyJobPostingsTableHolder"}));
        // tableHolder.appendChild(this.createCompanyJobPostingsByLotTable());
        // if (this.getCompanyJobPostingsByLotCount() > this.companyJobByLotPostingsPerPage) {
        //     tableHolder.appendChild(this.createCompanyJobPostingsByLotPageNumbers(tableHolder));
        // }
        return fieldHolder;
    }

    createCompanyJobPostingsByLotTable() {
        let companyJobPostingsByLotTable;
        companyJobPostingsByLotTable = coreFunctions.createElementHelper("table", {class: "shareHoldersTable", "data-companynumber": this.companyNumber});
        companyJobPostingsByLotTable.appendChild(coreFunctions.createElementHelper("thead")).appendChild(this.createCompanyJobPostingsByLotTableHeader());
        companyJobPostingsByLotTable.appendChild(this.addCompanyJobPostingsByLotToTable(companyJobPostingsByLotTable));
        return companyJobPostingsByLotTable;
    }

    createCompanyJobPostingsByLotTableHeader() {
        let tableHeaderRow;
        const tableHeaders = ["LOT code", "LOT name", "Unique postings", "Median salary"];
        tableHeaderRow = coreFunctions.createElementHelper("tr", {innerHTML: tableHeaders.reduce((previousContent, currentElement) => previousContent + `<th data-companyjobpostingstitle="${currentElement}">${currentElement}</th>`, "")});
        return tableHeaderRow;
    }

    addCompanyJobPostingsByLotToTable(holder) {
        let existingTbody, companyJobPostingsByLotTbody;
        if (existingTbody = holder.querySelector("tbody")) {
            holder.removeChild(existingTbody);
        }
        companyJobPostingsByLotTbody = document.createElement("tbody");

        this.getCompanyJobPostingsByLotOnPage().forEach(companyJobPosting => companyJobPostingsByLotTbody.appendChild(companyJobPosting.createCompanyJobPostingByLotTableRow()));

        return companyJobPostingsByLotTbody;
    }

    getCompanyJobPostingsByLotCount() {
        return this.companyJobPostingsByLot.length;
    }

    getCompanyJobPostingsByLotPageCount() {
        return Math.ceil(this.getCompanyJobPostingsByLotCount() / this.companyJobPostingsByLotPerPage);
    }

    getCompanyJobPostingsByLotOnPage() {
        return this.companyJobPostingsByLot.slice((this.companyJobPostingsByLotPageNumber - 1) * this.companyJobByLotPostingsPerPage, this.companyJobPostingsByLotPageNumber * this.companyJobByLotPostingsPerPage);
    }

    createCompanyJobPostingsByLotPageNumbers(holder) {
        const companyJobPostingsObject = this;
        let pageNumber, existingPageNumbers, pageLinksHolder, pageNumbersStructure, totalElementsInPageNumberStructure;

        if (existingPageNumbers = holder.querySelector(".companyJobPostingsPageLinksHolder")) {
            holder.removeChild(existingPageNumbers);
        }

        pageLinksHolder = coreFunctions.createElementHelper("div", {class: "companyJobPostingsPageLinksHolder"});
        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl previous-page"},{click: function() {companyJobPostingsObject.companyJobPostingsByLotShiftPage(-1)}}));

        pageNumbersStructure = this.getCompanyJobPostingsByLotPageNumbersStructure();
        totalElementsInPageNumberStructure = pageNumbersStructure.length;

        for (var i = 0; i < totalElementsInPageNumberStructure; i++) {
            pageNumber = pageNumbersStructure[i];
            if (pageNumbersStructure[i - 1] && pageNumber != (pageNumbersStructure[i - 1] + 1)) {
                pageLinksHolder.appendChild(coreFunctions.createElementHelper("span", { innerHTML: ". .", class: "pageLink" }));
            }
            pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", { innerHTML: pageNumber, class: `actionControl pageLink${(pageNumber == this.companyJobPostingsByLotPageNumber) ? " selected" : ""}`, "data-pagenumber": pageNumber }, { click: function () {  companyJobPostingsObject.companyJobPostingsByLotPageSelect(this) } }));
        }

        pageLinksHolder.appendChild(coreFunctions.createElementHelper("button", {innerHTML: "&nbsp;", class: "arrow actionControl next-page"}, {click: function() {companyJobPostingsObject.companyJobPostingsByLotShiftPage(1)}}));
        return pageLinksHolder;
    }

    companyJobPostingsByLotPageSelect(element) {
        let companyJobPostingsTableHolder, companyJobPostingsTable;
        companyJobPostingsTableHolder = element.closest(".companyJobPostingsTableHolder");
        companyJobPostingsTable = companyJobPostingsTableHolder.querySelector("table");
        this.companyJobPostingsByLotPageNumber = Number(element.dataset.pagenumber);
        companyJobPostingsTableHolder.appendChild(this.createCompanyJobPostingsByLotPageNumbers(companyJobPostingsTableHolder));
        companyJobPostingsTable.appendChild(this.addCompanyJobPostingsByLotToTable(companyJobPostingsTable));
    }

    companyJobPostingsByLotShiftPage(change) {
        let pageNumber, pageSelectElement;
        pageNumber = this.companyJobPostingsByLotPageNumber + change;
        if (pageSelectElement = document.querySelector(`.fieldHolder[data-name='companyJobPostingsByLot'][data-companynumber='${this.companyNumber}'] .pageLink[data-pagenumber='${pageNumber}']`)) {
            this.companyJobPostingsByLotPageSelect(pageSelectElement);
        }
    }

    getCompanyJobPostingsByLotPageNumbersStructure() {
        let pageNumber, totalPages, pageStructure;

        pageNumber = this.companyJobPostingsByLotPageNumber;
        totalPages = Math.ceil(this.getCompanyJobPostingsByLotCount() / this.companyJobByLotPostingsPerPage);

        if (totalPages <= 10) {
            pageStructure = [];
            for (var i = 0; i < totalPages; i++) {
                pageStructure.push(i + 1);
            }
            return pageStructure;
        } else if (pageNumber < 4) {
            return [1, 2, 3, 4, totalPages];
        } else if (pageNumber >= (totalPages - 2)) {
            return [1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            return [1, pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2, totalPages];
        }
    }
}

class CompanyJobPostingBySkill {
    constructor(companyNumber, skillName, totalPostings, uniquePostings, medianPostingDuration) {
        this.companyNumber = companyNumber;
        this.skillName = skillName;
        this.totalPostings = totalPostings;
        this.uniquePostings = uniquePostings;
        this.medianPostingDuration = medianPostingDuration;
    }

    createTableRow() {
        let tableRow;
        tableRow = coreFunctions.createElementHelper("tr", {"data-code": this.skillName});
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.skillName}));
        // tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: ((this.totalPostings) ? this.totalPostings.toLocaleString() : 'N/A')}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: ((this.uniquePostings) ? this.uniquePostings.toLocaleString() : 'N/A')}));
        // tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: ((this.medianPostingDuration) ? this.medianPostingDuration.toLocaleString() : 'N/A')}));
        return tableRow;
    }

    createTomSelectOption(field) {
        return {
            field: field,
            value: this.skillName,
            name: this.skillName
        }
    }
}

class CompanyJobPosting {
    constructor(companyNumber, SOC4Code, SOC4Name, postings, medianSalary) {
        this.companyNumber = companyNumber;
        this.SOC4Code = SOC4Code;
        this.SOC4Name = SOC4Name;
        this.postings = postings;
        this.medianSalary = medianSalary;
    }

    createTableRow() {
        let tableRow;
        tableRow = coreFunctions.createElementHelper("tr", {"data-code": this.SOC4Code});
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.SOC4Code}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.SOC4Name}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.postings.toLocaleString()}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: ((this.medianSalary) ? `£${this.medianSalary.toLocaleString()}` : 'N/A')}));
        return tableRow;
    }

    createTomSelectOption(field) {
        return {
            field: field,
            value: this.SOC4Code,
            name: this.SOC4Name
        }
    }
}

class CompanyJobPostingByLot {
    constructor(companyNumber, lotCode, lotName, postings, medianSalary) {
        this.companyNumber = companyNumber;
        this.lotCode = lotCode;
        this.lotName = lotName;
        this.postings = postings;
        this.medianSalary = medianSalary;
    }

    createTableRow() {
        let tableRow;
        tableRow = coreFunctions.createElementHelper("tr", {"data-code": this.lotCode});
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.lotCode}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.lotName}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.postings.toLocaleString()}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: ((this.medianSalary) ? `£${this.medianSalary.toLocaleString()}` : 'N/A')}));
        return tableRow;
    }

    createTomSelectOption(field) {
        return {
            field: field,
            value: this.lotCode,
            name: this.lotName
        }
    }
}

class DealroomFunding {
    constructor(companyNumber, roundAmount, roundDate, roundType, roundVerified, spinout, roundInvestorName, contributeToTotalFundingAmount) {
        this.companyNumber = companyNumber;
        this.roundAmount = roundAmount;
        this.roundDate = roundDate;
        this.roundType = roundType;
        this.roundVerified = roundVerified;
        this.spinout = spinout;
        this.tableHeadings = ["Type", "Investor name", "Date", "Estimated amount", "Round contributes to total funding?"];
        this.roundInvestorName = roundInvestorName;
        this.contributeToTotalFundingAmount = contributeToTotalFundingAmount;
    }

    addTableHeadings() {
        let tHead, tableHeaderRow
        tHead = document.createElement("thead");
        tableHeaderRow = tHead.appendChild(coreFunctions.createElementHelper("tr"));
        this.tableHeadings.forEach(heading => tableHeaderRow.appendChild(coreFunctions.createElementHelper("th", {innerHTML: heading})));
        return tHead;
    }

    createFundingTableRow() {
        let tableRow = document.createElement("tr");
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.roundType}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: (this.roundInvestorName) ? this.roundInvestorName.replace(/\"/g, '') : "Unknown"})); // Remove quotes from name
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: (this.roundDate) ? moment(this.roundDate, "YYYY-MM-DD").format("MMMM YYYY") : "Unavailable"}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: (this.roundAmount === 0) ? "Unknown" : this.roundAmount.toLocaleString('en-EN', {style: 'currency', currency: 'GBP' })}));
        tableRow.appendChild(coreFunctions.createElementHelper("td")).appendChild(this.createFundingContributionElement());
        // tableRow.appendChild(coreFunctions.createElementHelper("td")).appendChild(coreFunctions.createElementHelper("img", {src: (this.roundVerified == true) ? `${urlPath}/images/tick-lightgray.svg` : `${urlPath}/images/cross-lightgray.svg`}));
        // tableRow.appendChild(coreFunctions.createElementHelper("td")).appendChild(coreFunctions.createElementHelper("img", {src: (this.spinout == true) ? `${urlPath}/images/tick-lightgray.svg` : `${urlPath}/images/cross-lightgray.svg`}));
        return tableRow;
    }

    createFundingContributionElement() {
        const holder = document.createElement("span");
        holder.classList.add("dealroom-funding-contributor");

        const element = document.createElement("img");
        const icon = this.contributeToTotalFundingAmount ? "tick-green.svg" : "cross-red.svg";
        element.src = `${window.urlPath}/images/${icon}`;
        holder.appendChild(element);

        const text = document.createElement("span");
        text.innerHTML = this.contributeToTotalFundingAmount ? "Yes" : "No";
        text.classList.add(this.contributeToTotalFundingAmount ? "green" : "orange");
        holder.appendChild(text);

        return holder;
    }

}

class EsgStatementAnchor {
    constructor(domain,anchor,word) {
        this.domain = domain;
        this.anchor = anchor;
        this.word = word;
    }

    createTableRow() {
        let tableRow;
        tableRow = document.createElement("tr");
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.getEsgDescription(), class: "capitalize"}));
        tableRow.appendChild(coreFunctions.createElementHelper("td")).appendChild(coreFunctions.createElementHelper("a", {innerHTML: this.anchor, href: this.anchor}));
        tableRow.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.word}));
        return tableRow;
    }

    getEsgDescription() {
        let path;
        const anchorSplit = this.anchor.split(`${this.domain}/`);
        if (path = anchorSplit[1]) {
            return path.split("?")[0].replace(/esg/ig, "ESG").replace(/-/g, " ").replace(/\/$/, "").replace(/\//g, " | ");
        } else {
            return "Unavailable";
        }
    }
}

class Rsic {
    constructor(id, sicCode, score) {
        this.id = id;
        this.sicCode = sicCode;
        this.score = score;
    }
}

class HistoricRtics {
    constructor(data) {
        this.historicRticsByYear = this.processHistoricRticData(data);
    }

    processHistoricRticData(data) {
        return data.reduce((acc, curr) => {
            if (!acc[curr.Year]) acc[curr.Year] = [];
            acc[curr.Year].push(new HistoricRtic(curr));
            return acc;
        }, {});
    }

    companyHasHistoricRtics() {
        return Object.keys(this.historicRticsByYear).length != 0;
    }

    createHistoricRticsChart(holder) {
        const rticsByYearFlat = this.flattenHistoricRticsByYear();

        // Set variable height for chart based on number of unique verticals (40 px per row, with extra 20 for x-axis label)
        const verticalsCount = new Set(rticsByYearFlat.map(element => element.y)).size;
        const height = (verticalsCount == 1) ? 110 : (40 * verticalsCount) + 30;
        const chartHolder =  holder.appendChild(coreFunctions.createElementHelper("div", { class: "bubble-chart-holder", style: "position: relative;", height:  height + "px"}));
        const chartCanvas = chartHolder.appendChild(coreFunctions.createElementHelper("canvas", {height: height, width: "600"}));

        const data = {
            datasets: [{
              label: 'RTICs by year',
              data: rticsByYearFlat,
              backgroundColor: '#D10956',
              borderColor: '#D10956',
            }]
        };
        const options = {
            layout: {
                padding: 20,

            },
            animation: false,
            responsive: false,
            maintainAspectRatio: false,
            scales: {
                y: {
                    type: 'category',
                    grid: {
                        borderColor: "#4f5251",
                        color: "#4f5251"
                    },
                    ticks: {
                        autoSkip: false,
                        callback: function (value, index, values) {
                            const label = this.getLabelForValue(value).split(";").map(element => (element.length > 30) ? element.substring(0,30) : element);
                            return label;
                        },
                        color: "#C9C9C9",
                        font: {
                            family: 'Gilmer-Regular, Poppins-Regular'
                        }
                    }
                },
                x: {
                    type: 'linear',
                    suggestedMin: (rticsByYearFlat.length == 1) ? (rticsByYearFlat[0].x - 1) : null,
                    suggestedMax: (rticsByYearFlat.length == 1) ? (rticsByYearFlat[0].x + 1) : null,
                    grid: {
                        borderColor: "#4f5251",
                        color: "#4f5251"
                    },
                    ticks: {
                        callback: function (value, index, values) {

                            return value;
                        },
                        color: "#C9C9C9",
                        font: {
                            family: 'Gilmer-Regular, Poppins-Regular'
                        },
                        autoSkip: false,
                        stepSize: 1
                    },
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    displayColors: false,
                    titleFont: {
                        family: 'Gilmer-Regular, Poppins-Regular',
                    },
                    bodyFont: {
                        family: 'Gilmer-Regular, Poppins-Regular',
                    },
                    footerFont: {
                        family: 'Gilmer-Regular, Poppins-Regular',
                    },
                    callbacks: {
                        label: function (context) {
                            const data = context.dataset.data[context.dataIndex];
                            return [`Year: ${data.x}`, `RTIC vertical: ${data.y.replace(";", " ")}`, `Score: ${data.score}`];
                        },
                        title: () => ''
                    }
                }
            }
        }

        const config = {
            type: 'bubble',
            data: data,
            options: options
        };

        const bubbleChart = new Chart(chartCanvas.getContext('2d'), config);
        return this;
    }

    flattenHistoricRticsByYear() {
        return Object.values(this.historicRticsByYear).flatMap(element => element.map(vertical => vertical.formatHistoricRticForChart()));
    }

}

class HistoricRtic {
    constructor(data) {
        this.year = data.Year;
        this.verticalCode = data.VerticalCode;
        this.verticalName = data.VerticalName;
        this.sectorCode = data.SectorCode;
        this.sectorName = data.SectorName;
        this.score = data.Score;
    }

    formatHistoricRticForChart() {
        return {
            x: this.year,
            y: `${this.verticalName};(${this.sectorName})`,
            // r: Math.ceil(vertical.score),
            r: 5,
            score: this.score,
            code: this.verticalCode
        }
    }
}

class IndustrialStrategySector {
    constructor({code, name}) {
        this.code = code;
        this.name = name;
    }

    formatIndustrialStrategySectorDetails() {
        return `<div class="classification-item"><span class="classification-code">${this.code}: </span><span class="classification-desc">${this.name}</span></div>`;
    }
}

class OccupationCountByNation {
    constructor ({code, name, count, occupationCounts}) {
        this.code = code;
        this.name = name;
        this.count = count;
        this.occupationCounts = new OccupationCounts(occupationCounts);
    }

    createOccupationCountsByNationDetails() {
        const holder = coreFunctions.createElementHelper("div", {class: "locations-map__nation-occupation-counts"});
        holder.appendChild(coreFunctions.createElementHelper("h4", {class: "locations-map__popup-title", innerHTML: this.name}));
        holder.appendChild(coreFunctions.createElementHelper("p", {class: "locations-map__popup-field", innerHTML: `Total employees: ${this.occupationCounts.totalCount.toLocaleString()}`}));
        holder.appendChild(this.occupationCounts.createOccupationsPiechart(false));
        holder.querySelector(".locations-map__piechart-holder").classList.add("locations-map__piechart-holder--nation");
        holder.querySelector(".locations-map__piechart-canvas").classList.add("locations-map__piechart-canvas--nation");
        return holder;
    }

    createLocationsTableContent(locationFieldKeys) {
        const tableBody = coreFunctions.createElementHelper("tbody", {class: `locations-table__table-body locations-table__table-body--${this.code.toLowerCase()}`});
        const row = tableBody.appendChild(coreFunctions.createElementHelper("tr", {class: `locations-table__table-row locations-table__table-row--${this.code.toLowerCase()}`}));
        row.appendChild(coreFunctions.createElementHelper("td", {innerHTML: `<span class='locations-table__badge locations-table__badge--${this.code.toLowerCase()}'>${this.name}</span>`, class: "locations-table__table-data"}));
        locationFieldKeys.forEach(key => row.appendChild(coreFunctions.createElementHelper("td", {class: "locations-table__table-data", innerHTML: "-"})));
        if (this.occupationCounts.countsAvailable()) {
            row.appendChild(this.occupationCounts.createLocationTableCells({allOccupationGroups: Object.keys(OccupationGroup)}));
            const chartCell = row.appendChild(coreFunctions.createElementHelper("td", {class: "locations-table__table-data locations-table__table-data--numeric", "data-field": "occupationCounts"}));
            setTimeout(() => {
                const barchart = this.occupationCounts.createOccupationsStackedBarchart({displayLegend: false, displayTooltips: false, minimised: true});
                barchart.classList.add("locations-table__barchart-holder");
                chartCell.appendChild(barchart);
            }, 100);
        }
        return tableBody;
    }
}

class OccupationCounts {
    minIconSize = 20;
    maxIconSize = 40;

    constructor(occupationCounts, maxLocationTotalOccupationCount, parent) {
        this.maxLocationTotalOccupationCount = maxLocationTotalOccupationCount;
        this.groups = occupationCounts ? occupationCounts.flatMap(el => OccupationGroup[el.Thing.replace(/ /g, '')] instanceof OccupationGroup ? [new OccupationCount({name: el.Thing, count: el.Count})] : []) : [];
        this.totalCount = this.getTotalCount();
        this.parent = parent;
    }

    getSiteOccupationGroupsCount() {
        return Object.assign({total: this.totalCount}, this.groups.reduce((acc, element) => {acc[element.group] = element.count; return acc}, {}));
    }

    getOccupationCountByField(field) {
        console.log(field);
        let count, matchingGroup;
        if (field == "occupationCounts") {
            count = this.totalCount;
        } else if (matchingGroup = this.groups.find(el => el.group == field)) {
            console.log(matchingGroup, field);
            count = matchingGroup.count;
        }
        return count;
    }

    createLocationTableCells({allOccupationGroups} = {}) {
        const fragment = document.createDocumentFragment();

        fragment.appendChild(coreFunctions.createElementHelper("td", {innerHTML: this.totalCount.toLocaleString(), class: "locations-table__table-data locations-table__table-data--numeric locations-table__table-data--highlight", "data-field": "occupationCounts"}))

        let groupIndex = 0;
        const groupedOccupationGroups = Object.groupBy(this.groups, ({ group }) => group);
        allOccupationGroups.forEach(group => {
            const locationContainsGroup = Object.hasOwn(groupedOccupationGroups, group);
            if (locationContainsGroup) {
                fragment.appendChild(groupedOccupationGroups[group][0].createLocationTableCell());
                groupIndex++;
            } else {
                fragment.appendChild(this.createEmptyLocationTableCell(group));
            }
        });
        return fragment;
    }

    createEmptyLocationTableCell(group) {
        return coreFunctions.createElementHelper("td", {innerHTML: 0, class: "locations-table__table-data locations-table__table-data--numeric", "data-field": group});
    }

    createOccupationsStackedBarchart({ displayLegend = true, displayTooltips = true, minimised = false } = {}) {
        const chartHolder = coreFunctions.createElementHelper("div", {class: "locations-map__barchart-holder"});
        const chartCanvas = chartHolder.appendChild(coreFunctions.createElementHelper("canvas", {class: "locations-map__barchart-canvas"}));
        if (minimised) {
            chartCanvas.classList.add("locations-map__barchart-canvas--minimised");
        }

        const cs = getComputedStyle(document.body);
        const lightGrey = cs.getPropertyValue('--colour-brand-lightgrey');
        const fontFamily = cs.getPropertyValue('--font-regular');

        // If looking at minimised table view, ensure bar segments are in same order for all locations
        const sourceData = minimised ? this.sortGroupsByOccupationGroupIndex() : this.sortGroupsByCount();

        const config = {
            type: 'bar',
            data: {
                labels: [this.parent ? this.parent.postcodeWithSpaces : "Rest of world"],
                datasets: sourceData.map(el => el.getStackedBarchartDataset())
            },
            options: {
                maintainAspectRatio: false,
                indexAxis: 'y',
                layout: {
                    padding: {
                        left: 0,
                        right: 0
                    }
                },
                scales: {
                    xAxes: {
                        stacked: true,
                        display: false,
                        max: this.totalCount
                    },
                    yAxes: {
                        stacked: true,
                        display: false
                    }
                },
                plugins: {
                    legend: {
                        display: displayLegend,
                        align: 'start',
                        labels: {
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 6,
                            color: lightGrey,
                            font: {
                                family: fontFamily,
                                size: 10
                            },
                        }
                    },
                    tooltip: {
                        enabled: displayTooltips,
                        color: lightGrey,
                        titleFont: {
                            family: fontFamily,
                            size: 10
                        },
                        bodyFont: {
                            family: fontFamily,
                            size: 10
                        },
                    }
                }
            }
        };

        const barChart = new Chart(chartCanvas.getContext('2d'), config);
        return chartHolder;
    }

    createOccupationsPiechart(displayLegend = true) {
        const chartHolder = coreFunctions.createElementHelper("div", {class: "locations-map__piechart-holder"});
        const chartCanvas = chartHolder.appendChild(coreFunctions.createElementHelper("canvas", {class: "locations-map__piechart-canvas"}));

        const cs = getComputedStyle(document.body);
        const lightGrey = cs.getPropertyValue('--colour-brand-lightgrey');
        const fontFamily = cs.getPropertyValue('--font-regular');

        const config = {
            type: 'pie',
            data: this.getPopupPiechartData(),
            options: {
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: displayLegend,
                        align: 'start',
                        labels: {
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 6,
                            color: lightGrey,
                            font: {
                                family: fontFamily,
                                size: 10
                            },
                        }
                    },
                    tooltip: {
                        color: lightGrey,
                        titleFont: {
                            family: fontFamily,
                            size: 10
                        },
                        bodyFont: {
                            family: fontFamily,
                            size: 10
                        },
                    }
                }
            }
        };

        const pieChart = new Chart(chartCanvas.getContext('2d'), config);
        return chartHolder;
    }

    // Based on this article: https://david-gilbertson.medium.com/a-simple-pie-chart-in-svg-dbdd653b6936
    createOccupationCountsSVGPiechart() {
        const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svgEl.setAttribute('viewBox', '-1 -1 2 2');
        svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgEl.style.transform = "rotate(-0.25turn)"; // Aligns the SVG coordinate system with the cartesian coordinate system

        const slices = this.getPiechartSlices();
        let cumulativePercent = 0;
        let pathEl;

        slices.forEach(slice => {
            const [startX, startY] = this.getCoordinatesForPercent(cumulativePercent);

            // Each slice starts where the last slice ended, so keep a cumulative percent
            cumulativePercent += slice.percent;

            const [endX, endY] = this.getCoordinatesForPercent(cumulativePercent);

            // If the slice is more than 50%, take the large arc (the long way around)
            const largeArcFlag = slice.percent > .5 ? 1 : 0;

            const pathData = [
                `M ${startX} ${startY}`, // Move
                `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
                `L 0 0`, // Line
            ].join(' ');

            pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pathEl.setAttribute('d', pathData);
            pathEl.setAttribute('fill', slice.color);
            svgEl.appendChild(pathEl);
        });

        return svgEl;
    }

    createOccupationCountsMapIcon() {
        const svgPiechart = this.createOccupationCountsSVGPiechart();
        let iconURL = "data:image/svg+xml," + encodeURIComponent(svgPiechart.outerHTML);
        const iconSize = this.getIconSize();
        return L.icon({
            iconUrl: iconURL,
            iconSize: [iconSize, iconSize],
            className: "locations-map__piechart-marker"
        });
    }

    getIconSize() {
       return Math.max(Math.sqrt(this.totalCount / this.maxLocationTotalOccupationCount) * this.maxIconSize, this.minIconSize);
    }

    getTotalCount() {
        let totalCount = 0;
        if (this.groups.length > 0) {
            totalCount = this.groups.reduce((acc,el) => {acc += el.count; return acc}, 0);
        }
        return totalCount;
    }

    getCoordinatesForPercent(percent) {
        const x = Math.cos(2 * Math.PI * percent);
        const y = Math.sin(2 * Math.PI * percent);
        return [x, y];
    }

    countsAvailable() {
        return this.groups.length > 0;
    }

    getPiechartSlices() {
        return this.sortGroupsByCount().map(el => el.getPiechartSlice(this.totalCount));
    }

    sortGroupsByCount() {
        return this.groups.sort((a, b) => b.count - a.count);
    }

    // Will sort groups in the order as defined in OccupationGroup class, allows direct comparison
    sortGroupsByOccupationGroupIndex() {
        const keys = Object.keys(OccupationGroup);
        return this.groups.sort((a, b) => keys.indexOf(a.group) - keys.indexOf(b.group));
    }

    

    getPopupPiechartData() {
        const sortedGroups = this.sortGroupsByCount();
        const labels = sortedGroups.map(el => el.name);
        const counts = sortedGroups.map(el => el.count);
        const colors = sortedGroups.map(el => el.color);

        const cs = getComputedStyle(document.body);
        const grey = cs.getPropertyValue('--colour-brand-grey');

        return {
            labels: labels,
            datasets: [{
                borderWidth: 1,
                borderColor: grey,
                label: "Occupation groups",
                data: counts,
                backgroundColor: colors,
                hoverOffset: 0,

            }]
        }

    }

    createOccupationCountsKey() {
        const keyHolder = coreFunctions.createElementHelper("div", {class: "locations-map__occupations-key-holder"});
        keyHolder.appendChild(coreFunctions.createElementHelper("div", {class: "locations-map__occupations-key-label", innerHTML: "Occupation group"}));
        const key = keyHolder.appendChild(coreFunctions.createElementHelper("div", {class: "locations-map__occupations-key"}));
        for (let group in OccupationGroup) {
            key.appendChild(OccupationGroup[group].createOccupationGroupKeyElement());
        }
        return keyHolder;
    }
}

class OccupationCount {
    constructor ({name, count}) {
        this.name = name;
        this.group = name.replace(/ /g, "");
        this.count = count;
        this.color = OccupationGroup[this.group].color;
    }

    getPiechartSlice(totalCount) {
        return {
            percent: this.count / totalCount,
            color: this.color
        };
    }

    getStackedBarchartDataset() {
        return {
            data: [this.count],
            label: this.name,
            backgroundColor: this.color
        }
    }

    createLocationTableCell() {
        return coreFunctions.createElementHelper("td", {innerHTML: this.count.toLocaleString(), class: "locations-table__table-data locations-table__table-data--numeric", "data-field": this.group});
    }

}

class OccupationGroup {
    static STEM = new OccupationGroup({ key: "STEM", name: "STEM", color: "#489b75" });
    static BusinessServices = new OccupationGroup({ key: "BusinessServices", name: "Business Services", color: "#C56621" });
    static SHAPE = new OccupationGroup({ key: "SHAPE", name: "SHAPE", color: "#70B2E5" });
    static Trades = new OccupationGroup({ key: "Trades", name: "Trades", color: "#DAA230" });
    static Military = new OccupationGroup({ key: "Military", name: "Military", color: "#EEE45A" });

    constructor({ key, name, color }) {
        this.key = key;
        this.name = name;
        this.color = color;
    }

    getColor() {
        return this.color;
    }

    createOccupationGroupKeyElement() {
        return coreFunctions.createElementHelper("span", {class: "locations-map__occupations-key-element", style: `--key-color: ${this.color};`, innerHTML: this.name});
    }
}

var sicCodes = [
    {
        "code": "A",
        "description": "Agriculture, Forestry and Fishing"
    },
    {
        "code": 1110,
        "description": "Growing of cereals (except rice), leguminous crops and oil seeds"
    },
    {
        "code": 1120,
        "description": "Growing of rice"
    },
    {
        "code": 1130,
        "description": "Growing of vegetables and melons, roots and tubers"
    },
    {
        "code": 1140,
        "description": "Growing of sugar cane"
    },
    {
        "code": 1150,
        "description": "Growing of tobacco"
    },
    {
        "code": 1160,
        "description": "Growing of fibre crops"
    },
    {
        "code": 1190,
        "description": "Growing of other non-perennial crops"
    },
    {
        "code": 1210,
        "description": "Growing of grapes"
    },
    {
        "code": 1220,
        "description": "Growing of tropical and subtropical fruits"
    },
    {
        "code": 1230,
        "description": "Growing of citrus fruits"
    },
    {
        "code": 1240,
        "description": "Growing of pome fruits and stone fruits"
    },
    {
        "code": 1250,
        "description": "Growing of other tree and bush fruits and nuts"
    },
    {
        "code": 1260,
        "description": "Growing of oleaginous fruits"
    },
    {
        "code": 1270,
        "description": "Growing of beverage crops"
    },
    {
        "code": 1280,
        "description": "Growing of spices, aromatic, drug and pharmaceutical crops"
    },
    {
        "code": 1290,
        "description": "Growing of other perennial crops"
    },
    {
        "code": 1300,
        "description": "Plant propagation"
    },
    {
        "code": 1410,
        "description": "Raising of dairy cattle"
    },
    {
        "code": 1420,
        "description": "Raising of other cattle and buffaloes"
    },
    {
        "code": 1430,
        "description": "Raising of horses and other equines"
    },
    {
        "code": 1440,
        "description": "Raising of camels and camelids"
    },
    {
        "code": 1450,
        "description": "Raising of sheep and goats"
    },
    {
        "code": 1460,
        "description": "Raising of swine/pigs"
    },
    {
        "code": 1470,
        "description": "Raising of poultry"
    },
    {
        "code": 1490,
        "description": "Raising of other animals"
    },
    {
        "code": 1500,
        "description": "Mixed farming"
    },
    {
        "code": 1610,
        "description": "Support activities for crop production"
    },
    {
        "code": 1621,
        "description": "Farm animal boarding and care"
    },
    {
        "code": 1629,
        "description": "Support activities for animal production (other than farm animal boarding and care) n.e.c."
    },
    {
        "code": 1630,
        "description": "Post-harvest crop activities"
    },
    {
        "code": 1640,
        "description": "Seed processing for propagation"
    },
    {
        "code": 1700,
        "description": "Hunting, trapping and related service activities"
    },
    {
        "code": 2100,
        "description": "Silviculture and other forestry activities"
    },
    {
        "code": 2200,
        "description": "Logging"
    },
    {
        "code": 2300,
        "description": "Gathering of wild growing non-wood products"
    },
    {
        "code": 2400,
        "description": "Support services to forestry"
    },
    {
        "code": 3110,
        "description": "Marine fishing"
    },
    {
        "code": 3120,
        "description": "Freshwater fishing"
    },
    {
        "code": 3210,
        "description": "Marine aquaculture"
    },
    {
        "code": 3220,
        "description": "Freshwater aquaculture"
    },
    {
        "code": "B",
        "description": "Mining and Quarrying"
    },
    {
        "code": 5101,
        "description": "Deep coal mines"
    },
    {
        "code": 5102,
        "description": "Open cast coal working"
    },
    {
        "code": 5200,
        "description": "Mining of lignite"
    },
    {
        "code": 6100,
        "description": "Extraction of crude petroleum"
    },
    {
        "code": 6200,
        "description": "Extraction of natural gas"
    },
    {
        "code": 7100,
        "description": "Mining of iron ores"
    },
    {
        "code": 7210,
        "description": "Mining of uranium and thorium ores"
    },
    {
        "code": 7290,
        "description": "Mining of other non-ferrous metal ores"
    },
    {
        "code": 8110,
        "description": "Quarrying of ornamental and building stone, limestone, gypsum, chalk and slate"
    },
    {
        "code": 8120,
        "description": "Operation of gravel and sand pits; mining of clays and kaolin"
    },
    {
        "code": 8910,
        "description": "Mining of chemical and fertilizer minerals"
    },
    {
        "code": 8920,
        "description": "Extraction of peat"
    },
    {
        "code": 8930,
        "description": "Extraction of salt"
    },
    {
        "code": 8990,
        "description": "Other mining and quarrying n.e.c."
    },
    {
        "code": 9100,
        "description": "Support activities for petroleum and natural gas mining"
    },
    {
        "code": 9900,
        "description": "Support activities for other mining and quarrying"
    },
    {
        "code": "C",
        "description": "Manufacturing"
    },
    {
        "code": 10110,
        "description": "Processing and preserving of meat"
    },
    {
        "code": 10120,
        "description": "Processing and preserving of poultry meat"
    },
    {
        "code": 10130,
        "description": "Production of meat and poultry meat products"
    },
    {
        "code": 10200,
        "description": "Processing and preserving of fish, crustaceans and molluscs"
    },
    {
        "code": 10310,
        "description": "Processing and preserving of potatoes"
    },
    {
        "code": 10320,
        "description": "Manufacture of fruit and vegetable juice"
    },
    {
        "code": 10390,
        "description": "Other processing and preserving of fruit and vegetables"
    },
    {
        "code": 10410,
        "description": "Manufacture of oils and fats"
    },
    {
        "code": 10420,
        "description": "Manufacture of margarine and similar edible fats"
    },
    {
        "code": 10511,
        "description": "Liquid milk and cream production"
    },
    {
        "code": 10512,
        "description": "Butter and cheese production"
    },
    {
        "code": 10519,
        "description": "Manufacture of other milk products"
    },
    {
        "code": 10520,
        "description": "Manufacture of ice cream"
    },
    {
        "code": 10611,
        "description": "Grain milling"
    },
    {
        "code": 10612,
        "description": "Manufacture of breakfast cereals and cereals-based food"
    },
    {
        "code": 10620,
        "description": "Manufacture of starches and starch products"
    },
    {
        "code": 10710,
        "description": "Manufacture of bread; manufacture of fresh pastry goods and cakes"
    },
    {
        "code": 10720,
        "description": "Manufacture of rusks and biscuits; manufacture of preserved pastry goods and cakes"
    },
    {
        "code": 10730,
        "description": "Manufacture of macaroni, noodles, couscous and similar farinaceous products"
    },
    {
        "code": 10810,
        "description": "Manufacture of sugar"
    },
    {
        "code": 10821,
        "description": "Manufacture of cocoa and chocolate confectionery"
    },
    {
        "code": 10822,
        "description": "Manufacture of sugar confectionery"
    },
    {
        "code": 10831,
        "description": "Tea processing"
    },
    {
        "code": 10832,
        "description": "Production of coffee and coffee substitutes"
    },
    {
        "code": 10840,
        "description": "Manufacture of condiments and seasonings"
    },
    {
        "code": 10850,
        "description": "Manufacture of prepared meals and dishes"
    },
    {
        "code": 10860,
        "description": "Manufacture of homogenized food preparations and dietetic food"
    },
    {
        "code": 10890,
        "description": "Manufacture of other food products n.e.c."
    },
    {
        "code": 10910,
        "description": "Manufacture of prepared feeds for farm animals"
    },
    {
        "code": 10920,
        "description": "Manufacture of prepared pet foods"
    },
    {
        "code": 11010,
        "description": "Distilling, rectifying and blending of spirits"
    },
    {
        "code": 11020,
        "description": "Manufacture of wine from grape"
    },
    {
        "code": 11030,
        "description": "Manufacture of cider and other fruit wines"
    },
    {
        "code": 11040,
        "description": "Manufacture of other non-distilled fermented beverages"
    },
    {
        "code": 11050,
        "description": "Manufacture of beer"
    },
    {
        "code": 11060,
        "description": "Manufacture of malt"
    },
    {
        "code": 11070,
        "description": "Manufacture of soft drinks; production of mineral waters and other bottled waters"
    },
    {
        "code": 12000,
        "description": "Manufacture of tobacco products"
    },
    {
        "code": 13100,
        "description": "Preparation and spinning of textile fibres"
    },
    {
        "code": 13200,
        "description": "Weaving of textiles"
    },
    {
        "code": 13300,
        "description": "Finishing of textiles"
    },
    {
        "code": 13910,
        "description": "Manufacture of knitted and crocheted fabrics"
    },
    {
        "code": 13921,
        "description": "Manufacture of soft furnishings"
    },
    {
        "code": 13922,
        "description": "manufacture of canvas goods, sacks, etc."
    },
    {
        "code": 13923,
        "description": "manufacture of household textiles"
    },
    {
        "code": 13931,
        "description": "Manufacture of woven or tufted carpets and rugs"
    },
    {
        "code": 13939,
        "description": "Manufacture of other carpets and rugs"
    },
    {
        "code": 13940,
        "description": "Manufacture of cordage, rope, twine and netting"
    },
    {
        "code": 13950,
        "description": "Manufacture of non-wovens and articles made from non-wovens, except apparel"
    },
    {
        "code": 13960,
        "description": "Manufacture of other technical and industrial textiles"
    },
    {
        "code": 13990,
        "description": "Manufacture of other textiles n.e.c."
    },
    {
        "code": 14110,
        "description": "Manufacture of leather clothes"
    },
    {
        "code": 14120,
        "description": "Manufacture of workwear"
    },
    {
        "code": 14131,
        "description": "Manufacture of other men's outerwear"
    },
    {
        "code": 14132,
        "description": "Manufacture of other women's outerwear"
    },
    {
        "code": 14141,
        "description": "Manufacture of men's underwear"
    },
    {
        "code": 14142,
        "description": "Manufacture of women's underwear"
    },
    {
        "code": 14190,
        "description": "Manufacture of other wearing apparel and accessories n.e.c."
    },
    {
        "code": 14200,
        "description": "Manufacture of articles of fur"
    },
    {
        "code": 14310,
        "description": "Manufacture of knitted and crocheted hosiery"
    },
    {
        "code": 14390,
        "description": "Manufacture of other knitted and crocheted apparel"
    },
    {
        "code": 15110,
        "description": "Tanning and dressing of leather; dressing and dyeing of fur"
    },
    {
        "code": 15120,
        "description": "Manufacture of luggage, handbags and the like, saddlery and harness"
    },
    {
        "code": 15200,
        "description": "Manufacture of footwear"
    },
    {
        "code": 16100,
        "description": "Sawmilling and planing of wood"
    },
    {
        "code": 16210,
        "description": "Manufacture of veneer sheets and wood-based panels"
    },
    {
        "code": 16220,
        "description": "Manufacture of assembled parquet floors"
    },
    {
        "code": 16230,
        "description": "Manufacture of other builders' carpentry and joinery"
    },
    {
        "code": 16240,
        "description": "Manufacture of wooden containers"
    },
    {
        "code": 16290,
        "description": "Manufacture of other products of wood; manufacture of articles of cork, straw and plaiting materials"
    },
    {
        "code": 17110,
        "description": "Manufacture of pulp"
    },
    {
        "code": 17120,
        "description": "Manufacture of paper and paperboard"
    },
    {
        "code": 17211,
        "description": "Manufacture of corrugated paper and paperboard, sacks and bags"
    },
    {
        "code": 17219,
        "description": "Manufacture of other paper and paperboard containers"
    },
    {
        "code": 17220,
        "description": "Manufacture of household and sanitary goods and of toilet requisites"
    },
    {
        "code": 17230,
        "description": "Manufacture of paper stationery"
    },
    {
        "code": 17240,
        "description": "Manufacture of wallpaper"
    },
    {
        "code": 17290,
        "description": "Manufacture of other articles of paper and paperboard n.e.c."
    },
    {
        "code": 18110,
        "description": "Printing of newspapers"
    },
    {
        "code": 18121,
        "description": "Manufacture of printed labels"
    },
    {
        "code": 18129,
        "description": "Printing n.e.c."
    },
    {
        "code": 18130,
        "description": "Pre-press and pre-media services"
    },
    {
        "code": 18140,
        "description": "Binding and related services"
    },
    {
        "code": 18201,
        "description": "Reproduction of sound recording"
    },
    {
        "code": 18202,
        "description": "Reproduction of video recording"
    },
    {
        "code": 18203,
        "description": "Reproduction of computer media"
    },
    {
        "code": 19100,
        "description": "Manufacture of coke oven products"
    },
    {
        "code": 19201,
        "description": "Mineral oil refining"
    },
    {
        "code": 19209,
        "description": "Other treatment of petroleum products (excluding petrochemicals manufacture)"
    },
    {
        "code": 20110,
        "description": "Manufacture of industrial gases"
    },
    {
        "code": 20120,
        "description": "Manufacture of dyes and pigments"
    },
    {
        "code": 20130,
        "description": "Manufacture of other inorganic basic chemicals"
    },
    {
        "code": 20140,
        "description": "Manufacture of other organic basic chemicals"
    },
    {
        "code": 20150,
        "description": "Manufacture of fertilizers and nitrogen compounds"
    },
    {
        "code": 20160,
        "description": "Manufacture of plastics in primary forms"
    },
    {
        "code": 20170,
        "description": "Manufacture of synthetic rubber in primary forms"
    },
    {
        "code": 20200,
        "description": "Manufacture of pesticides and other agrochemical products"
    },
    {
        "code": 20301,
        "description": "Manufacture of paints, varnishes and similar coatings, mastics and sealants"
    },
    {
        "code": 20302,
        "description": "Manufacture of printing ink"
    },
    {
        "code": 20411,
        "description": "Manufacture of soap and detergents"
    },
    {
        "code": 20412,
        "description": "Manufacture of cleaning and polishing preparations"
    },
    {
        "code": 20420,
        "description": "Manufacture of perfumes and toilet preparations"
    },
    {
        "code": 20510,
        "description": "Manufacture of explosives"
    },
    {
        "code": 20520,
        "description": "Manufacture of glues"
    },
    {
        "code": 20530,
        "description": "Manufacture of essential oils"
    },
    {
        "code": 20590,
        "description": "Manufacture of other chemical products n.e.c."
    },
    {
        "code": 20600,
        "description": "Manufacture of man-made fibres"
    },
    {
        "code": 21100,
        "description": "Manufacture of basic pharmaceutical products"
    },
    {
        "code": 21200,
        "description": "Manufacture of pharmaceutical preparations"
    },
    {
        "code": 22110,
        "description": "Manufacture of rubber tyres and tubes; retreading and rebuilding of rubber tyres"
    },
    {
        "code": 22190,
        "description": "Manufacture of other rubber products"
    },
    {
        "code": 22210,
        "description": "Manufacture of plastic plates, sheets, tubes and profiles"
    },
    {
        "code": 22220,
        "description": "Manufacture of plastic packing goods"
    },
    {
        "code": 22230,
        "description": "Manufacture of builders ware of plastic"
    },
    {
        "code": 22290,
        "description": "Manufacture of other plastic products"
    },
    {
        "code": 23110,
        "description": "Manufacture of flat glass"
    },
    {
        "code": 23120,
        "description": "Shaping and processing of flat glass"
    },
    {
        "code": 23130,
        "description": "Manufacture of hollow glass"
    },
    {
        "code": 23140,
        "description": "Manufacture of glass fibres"
    },
    {
        "code": 23190,
        "description": "Manufacture and processing of other glass, including technical glassware"
    },
    {
        "code": 23200,
        "description": "Manufacture of refractory products"
    },
    {
        "code": 23310,
        "description": "Manufacture of ceramic tiles and flags"
    },
    {
        "code": 23320,
        "description": "Manufacture of bricks, tiles and construction products, in baked clay"
    },
    {
        "code": 23410,
        "description": "Manufacture of ceramic household and ornamental articles"
    },
    {
        "code": 23420,
        "description": "Manufacture of ceramic sanitary fixtures"
    },
    {
        "code": 23430,
        "description": "Manufacture of ceramic insulators and insulating fittings"
    },
    {
        "code": 23440,
        "description": "Manufacture of other technical ceramic products"
    },
    {
        "code": 23490,
        "description": "Manufacture of other ceramic products n.e.c."
    },
    {
        "code": 23510,
        "description": "Manufacture of cement"
    },
    {
        "code": 23520,
        "description": "Manufacture of lime and plaster"
    },
    {
        "code": 23610,
        "description": "Manufacture of concrete products for construction purposes"
    },
    {
        "code": 23620,
        "description": "Manufacture of plaster products for construction purposes"
    },
    {
        "code": 23630,
        "description": "Manufacture of ready-mixed concrete"
    },
    {
        "code": 23640,
        "description": "Manufacture of mortars"
    },
    {
        "code": 23650,
        "description": "Manufacture of fibre cement"
    },
    {
        "code": 23690,
        "description": "Manufacture of other articles of concrete, plaster and cement"
    },
    {
        "code": 23700,
        "description": "Cutting, shaping and finishing of stone"
    },
    {
        "code": 23910,
        "description": "Production of abrasive products"
    },
    {
        "code": 23990,
        "description": "Manufacture of other non-metallic mineral products n.e.c."
    },
    {
        "code": 24100,
        "description": "Manufacture of basic iron and steel and of ferro-alloys"
    },
    {
        "code": 24200,
        "description": "Manufacture of tubes, pipes, hollow profiles and related fittings, of steel"
    },
    {
        "code": 24310,
        "description": "Cold drawing of bars"
    },
    {
        "code": 24320,
        "description": "Cold rolling of narrow strip"
    },
    {
        "code": 24330,
        "description": "Cold forming or folding"
    },
    {
        "code": 24340,
        "description": "Cold drawing of wire"
    },
    {
        "code": 24410,
        "description": "Precious metals production"
    },
    {
        "code": 24420,
        "description": "Aluminium production"
    },
    {
        "code": 24430,
        "description": "Lead, zinc and tin production"
    },
    {
        "code": 24440,
        "description": "Copper production"
    },
    {
        "code": 24450,
        "description": "Other non-ferrous metal production"
    },
    {
        "code": 24460,
        "description": "Processing of nuclear fuel"
    },
    {
        "code": 24510,
        "description": "Casting of iron"
    },
    {
        "code": 24520,
        "description": "Casting of steel"
    },
    {
        "code": 24530,
        "description": "Casting of light metals"
    },
    {
        "code": 24540,
        "description": "Casting of other non-ferrous metals"
    },
    {
        "code": 25110,
        "description": "Manufacture of metal structures and parts of structures"
    },
    {
        "code": 25120,
        "description": "Manufacture of doors and windows of metal"
    },
    {
        "code": 25210,
        "description": "Manufacture of central heating radiators and boilers"
    },
    {
        "code": 25290,
        "description": "Manufacture of other tanks, reservoirs and containers of metal"
    },
    {
        "code": 25300,
        "description": "Manufacture of steam generators, except central heating hot water boilers"
    },
    {
        "code": 25400,
        "description": "Manufacture of weapons and ammunition"
    },
    {
        "code": 25500,
        "description": "Forging, pressing, stamping and roll-forming of metal; powder metallurgy"
    },
    {
        "code": 25610,
        "description": "Treatment and coating of metals"
    },
    {
        "code": 25620,
        "description": "Machining"
    },
    {
        "code": 25710,
        "description": "Manufacture of cutlery"
    },
    {
        "code": 25720,
        "description": "Manufacture of locks and hinges"
    },
    {
        "code": 25730,
        "description": "Manufacture of tools"
    },
    {
        "code": 25910,
        "description": "Manufacture of steel drums and similar containers"
    },
    {
        "code": 25920,
        "description": "Manufacture of light metal packaging"
    },
    {
        "code": 25930,
        "description": "Manufacture of wire products, chain and springs"
    },
    {
        "code": 25940,
        "description": "Manufacture of fasteners and screw machine products"
    },
    {
        "code": 25990,
        "description": "Manufacture of other fabricated metal products n.e.c."
    },
    {
        "code": 26110,
        "description": "Manufacture of electronic components"
    },
    {
        "code": 26120,
        "description": "Manufacture of loaded electronic boards"
    },
    {
        "code": 26200,
        "description": "Manufacture of computers and peripheral equipment"
    },
    {
        "code": 26301,
        "description": "Manufacture of telegraph and telephone apparatus and equipment"
    },
    {
        "code": 26309,
        "description": "Manufacture of communication equipment other than telegraph, and telephone apparatus and equipment"
    },
    {
        "code": 26400,
        "description": "Manufacture of consumer electronics"
    },
    {
        "code": 26511,
        "description": "Manufacture of electronic measuring, testing etc. equipment, not for industrial process control"
    },
    {
        "code": 26512,
        "description": "Manufacture of electronic industrial process control equipment"
    },
    {
        "code": 26513,
        "description": "Manufacture of non-electronic measuring, testing etc. equipment, not for industrial process control"
    },
    {
        "code": 26514,
        "description": "Manufacture of non-electronic industrial process control equipment"
    },
    {
        "code": 26520,
        "description": "Manufacture of watches and clocks"
    },
    {
        "code": 26600,
        "description": "Manufacture of irradiation, electromedical and electrotherapeutic equipment"
    },
    {
        "code": 26701,
        "description": "Manufacture of optical precision instruments"
    },
    {
        "code": 26702,
        "description": "Manufacture of photographic and cinematographic equipment"
    },
    {
        "code": 26800,
        "description": "Manufacture of magnetic and optical media"
    },
    {
        "code": 27110,
        "description": "Manufacture of electric motors, generators and transformers"
    },
    {
        "code": 27120,
        "description": "Manufacture of electricity distribution and control apparatus"
    },
    {
        "code": 27200,
        "description": "Manufacture of batteries and accumulators"
    },
    {
        "code": 27310,
        "description": "Manufacture of fibre optic cables"
    },
    {
        "code": 27320,
        "description": "Manufacture of other electronic and electric wires and cables"
    },
    {
        "code": 27330,
        "description": "Manufacture of wiring devices"
    },
    {
        "code": 27400,
        "description": "Manufacture of electric lighting equipment"
    },
    {
        "code": 27510,
        "description": "Manufacture of electric domestic appliances"
    },
    {
        "code": 27520,
        "description": "Manufacture of non-electric domestic appliances"
    },
    {
        "code": 27900,
        "description": "Manufacture of other electrical equipment"
    },
    {
        "code": 28110,
        "description": "Manufacture of engines and turbines, except aircraft, vehicle and cycle engines"
    },
    {
        "code": 28120,
        "description": "Manufacture of fluid power equipment"
    },
    {
        "code": 28131,
        "description": "Manufacture of pumps"
    },
    {
        "code": 28132,
        "description": "Manufacture of compressors"
    },
    {
        "code": 28140,
        "description": "Manufacture of taps and valves"
    },
    {
        "code": 28150,
        "description": "Manufacture of bearings, gears, gearing and driving elements"
    },
    {
        "code": 28210,
        "description": "Manufacture of ovens, furnaces and furnace burners"
    },
    {
        "code": 28220,
        "description": "Manufacture of lifting and handling equipment"
    },
    {
        "code": 28230,
        "description": "Manufacture of office machinery and equipment (except computers and peripheral equipment)"
    },
    {
        "code": 28240,
        "description": "Manufacture of power-driven hand tools"
    },
    {
        "code": 28250,
        "description": "Manufacture of non-domestic cooling and ventilation equipment"
    },
    {
        "code": 28290,
        "description": "Manufacture of other general-purpose machinery n.e.c."
    },
    {
        "code": 28301,
        "description": "Manufacture of agricultural tractors"
    },
    {
        "code": 28302,
        "description": "Manufacture of agricultural and forestry machinery other than tractors"
    },
    {
        "code": 28410,
        "description": "Manufacture of metal forming machinery"
    },
    {
        "code": 28490,
        "description": "Manufacture of other machine tools"
    },
    {
        "code": 28910,
        "description": "Manufacture of machinery for metallurgy"
    },
    {
        "code": 28921,
        "description": "Manufacture of machinery for mining"
    },
    {
        "code": 28922,
        "description": "Manufacture of earthmoving equipment"
    },
    {
        "code": 28923,
        "description": "Manufacture of equipment for concrete crushing and screening and roadworks"
    },
    {
        "code": 28930,
        "description": "Manufacture of machinery for food, beverage and tobacco processing"
    },
    {
        "code": 28940,
        "description": "Manufacture of machinery for textile, apparel and leather production"
    },
    {
        "code": 28950,
        "description": "Manufacture of machinery for paper and paperboard production"
    },
    {
        "code": 28960,
        "description": "Manufacture of plastics and rubber machinery"
    },
    {
        "code": 28990,
        "description": "Manufacture of other special-purpose machinery n.e.c."
    },
    {
        "code": 29100,
        "description": "Manufacture of motor vehicles"
    },
    {
        "code": 29201,
        "description": "Manufacture of bodies (coachwork) for motor vehicles (except caravans)"
    },
    {
        "code": 29202,
        "description": "Manufacture of trailers and semi-trailers"
    },
    {
        "code": 29203,
        "description": "Manufacture of caravans"
    },
    {
        "code": 29310,
        "description": "Manufacture of electrical and electronic equipment for motor vehicles and their engines"
    },
    {
        "code": 29320,
        "description": "Manufacture of other parts and accessories for motor vehicles"
    },
    {
        "code": 30110,
        "description": "Building of ships and floating structures"
    },
    {
        "code": 30120,
        "description": "Building of pleasure and sporting boats"
    },
    {
        "code": 30200,
        "description": "Manufacture of railway locomotives and rolling stock"
    },
    {
        "code": 30300,
        "description": "Manufacture of air and spacecraft and related machinery"
    },
    {
        "code": 30400,
        "description": "Manufacture of military fighting vehicles"
    },
    {
        "code": 30910,
        "description": "Manufacture of motorcycles"
    },
    {
        "code": 30920,
        "description": "Manufacture of bicycles and invalid carriages"
    },
    {
        "code": 30990,
        "description": "Manufacture of other transport equipment n.e.c."
    },
    {
        "code": 31010,
        "description": "Manufacture of office and shop furniture"
    },
    {
        "code": 31020,
        "description": "Manufacture of kitchen furniture"
    },
    {
        "code": 31030,
        "description": "Manufacture of mattresses"
    },
    {
        "code": 31090,
        "description": "Manufacture of other furniture"
    },
    {
        "code": 32110,
        "description": "Striking of coins"
    },
    {
        "code": 32120,
        "description": "Manufacture of jewellery and related articles"
    },
    {
        "code": 32130,
        "description": "Manufacture of imitation jewellery and related articles"
    },
    {
        "code": 32200,
        "description": "Manufacture of musical instruments"
    },
    {
        "code": 32300,
        "description": "Manufacture of sports goods"
    },
    {
        "code": 32401,
        "description": "Manufacture of professional and arcade games and toys"
    },
    {
        "code": 32409,
        "description": "Manufacture of other games and toys, n.e.c."
    },
    {
        "code": 32500,
        "description": "Manufacture of medical and dental instruments and supplies"
    },
    {
        "code": 32910,
        "description": "Manufacture of brooms and brushes"
    },
    {
        "code": 32990,
        "description": "Other manufacturing n.e.c."
    },
    {
        "code": 33110,
        "description": "Repair of fabricated metal products"
    },
    {
        "code": 33120,
        "description": "Repair of machinery"
    },
    {
        "code": 33130,
        "description": "Repair of electronic and optical equipment"
    },
    {
        "code": 33140,
        "description": "Repair of electrical equipment"
    },
    {
        "code": 33150,
        "description": "Repair and maintenance of ships and boats"
    },
    {
        "code": 33160,
        "description": "Repair and maintenance of aircraft and spacecraft"
    },
    {
        "code": 33170,
        "description": "Repair and maintenance of other transport equipment n.e.c."
    },
    {
        "code": 33190,
        "description": "Repair of other equipment"
    },
    {
        "code": 33200,
        "description": "Installation of industrial machinery and equipment"
    },
    {
        "code": "D",
        "description": "Electricity, gas, steam and air conditioning supply"
    },
    {
        "code": 35110,
        "description": "Production of electricity"
    },
    {
        "code": 35120,
        "description": "Transmission of electricity"
    },
    {
        "code": 35130,
        "description": "Distribution of electricity"
    },
    {
        "code": 35140,
        "description": "Trade of electricity"
    },
    {
        "code": 35210,
        "description": "Manufacture of gas"
    },
    {
        "code": 35220,
        "description": "Distribution of gaseous fuels through mains"
    },
    {
        "code": 35230,
        "description": "Trade of gas through mains"
    },
    {
        "code": 35300,
        "description": "Steam and air conditioning supply"
    },
    {
        "code": "E",
        "description": "Water supply, sewerage, waste management and remediation activities"
    },
    {
        "code": 36000,
        "description": "Water collection, treatment and supply"
    },
    {
        "code": 37000,
        "description": "Sewerage"
    },
    {
        "code": 38110,
        "description": "Collection of non-hazardous waste"
    },
    {
        "code": 38120,
        "description": "Collection of hazardous waste"
    },
    {
        "code": 38210,
        "description": "Treatment and disposal of non-hazardous waste"
    },
    {
        "code": 38220,
        "description": "Treatment and disposal of hazardous waste"
    },
    {
        "code": 38310,
        "description": "Dismantling of wrecks"
    },
    {
        "code": 38320,
        "description": "Recovery of sorted materials"
    },
    {
        "code": 39000,
        "description": "Remediation activities and other waste management services"
    },
    {
        "code": "F",
        "description": "Construction"
    },
    {
        "code": 41100,
        "description": "Development of building projects"
    },
    {
        "code": 41201,
        "description": "Construction of commercial buildings"
    },
    {
        "code": 41202,
        "description": "Construction of domestic buildings"
    },
    {
        "code": 42110,
        "description": "Construction of roads and motorways"
    },
    {
        "code": 42120,
        "description": "Construction of railways and underground railways"
    },
    {
        "code": 42130,
        "description": "Construction of bridges and tunnels"
    },
    {
        "code": 42210,
        "description": "Construction of utility projects for fluids"
    },
    {
        "code": 42220,
        "description": "Construction of utility projects for electricity and telecommunications"
    },
    {
        "code": 42910,
        "description": "Construction of water projects"
    },
    {
        "code": 42990,
        "description": "Construction of other civil engineering projects n.e.c."
    },
    {
        "code": 43110,
        "description": "Demolition"
    },
    {
        "code": 43120,
        "description": "Site preparation"
    },
    {
        "code": 43130,
        "description": "Test drilling and boring"
    },
    {
        "code": 43210,
        "description": "Electrical installation"
    },
    {
        "code": 43220,
        "description": "Plumbing, heat and air-conditioning installation"
    },
    {
        "code": 43290,
        "description": "Other construction installation"
    },
    {
        "code": 43310,
        "description": "Plastering"
    },
    {
        "code": 43320,
        "description": "Joinery installation"
    },
    {
        "code": 43330,
        "description": "Floor and wall covering"
    },
    {
        "code": 43341,
        "description": "Painting"
    },
    {
        "code": 43342,
        "description": "Glazing"
    },
    {
        "code": 43390,
        "description": "Other building completion and finishing"
    },
    {
        "code": 43910,
        "description": "Roofing activities"
    },
    {
        "code": 43991,
        "description": "Scaffold erection"
    },
    {
        "code": 43999,
        "description": "Other specialised construction activities n.e.c."
    },
    {
        "code": "G",
        "description": "Wholesale and retail trade; repair of motor vehicles and motorcycles"
    },
    {
        "code": 45111,
        "description": "Sale of new cars and light motor vehicles"
    },
    {
        "code": 45112,
        "description": "Sale of used cars and light motor vehicles"
    },
    {
        "code": 45190,
        "description": "Sale of other motor vehicles"
    },
    {
        "code": 45200,
        "description": "Maintenance and repair of motor vehicles"
    },
    {
        "code": 45310,
        "description": "Wholesale trade of motor vehicle parts and accessories"
    },
    {
        "code": 45320,
        "description": "Retail trade of motor vehicle parts and accessories"
    },
    {
        "code": 45400,
        "description": "Sale, maintenance and repair of motorcycles and related parts and accessories"
    },
    {
        "code": 46110,
        "description": "Agents selling agricultural raw materials, livestock, textile raw materials and semi-finished goods"
    },
    {
        "code": 46120,
        "description": "Agents involved in the sale of fuels, ores, metals and industrial chemicals"
    },
    {
        "code": 46130,
        "description": "Agents involved in the sale of timber and building materials"
    },
    {
        "code": 46140,
        "description": "Agents involved in the sale of machinery, industrial equipment, ships and aircraft"
    },
    {
        "code": 46150,
        "description": "Agents involved in the sale of furniture, household goods, hardware and ironmongery"
    },
    {
        "code": 46160,
        "description": "Agents involved in the sale of textiles, clothing, fur, footwear and leather goods"
    },
    {
        "code": 46170,
        "description": "Agents involved in the sale of food, beverages and tobacco"
    },
    {
        "code": 46180,
        "description": "Agents specialised in the sale of other particular products"
    },
    {
        "code": 46190,
        "description": "Agents involved in the sale of a variety of goods"
    },
    {
        "code": 46210,
        "description": "Wholesale of grain, unmanufactured tobacco, seeds and animal feeds"
    },
    {
        "code": 46220,
        "description": "Wholesale of flowers and plants"
    },
    {
        "code": 46230,
        "description": "Wholesale of live animals"
    },
    {
        "code": 46240,
        "description": "Wholesale of hides, skins and leather"
    },
    {
        "code": 46310,
        "description": "Wholesale of fruit and vegetables"
    },
    {
        "code": 46320,
        "description": "Wholesale of meat and meat products"
    },
    {
        "code": 46330,
        "description": "Wholesale of dairy products, eggs and edible oils and fats"
    },
    {
        "code": 46341,
        "description": "Wholesale of fruit and vegetable juices, mineral water and soft drinks"
    },
    {
        "code": 46342,
        "description": "Wholesale of wine, beer, spirits and other alcoholic beverages"
    },
    {
        "code": 46350,
        "description": "Wholesale of tobacco products"
    },
    {
        "code": 46360,
        "description": "Wholesale of sugar and chocolate and sugar confectionery"
    },
    {
        "code": 46370,
        "description": "Wholesale of coffee, tea, cocoa and spices"
    },
    {
        "code": 46380,
        "description": "Wholesale of other food, including fish, crustaceans and molluscs"
    },
    {
        "code": 46390,
        "description": "Non-specialised wholesale of food, beverages and tobacco"
    },
    {
        "code": 46410,
        "description": "Wholesale of textiles"
    },
    {
        "code": 46420,
        "description": "Wholesale of clothing and footwear"
    },
    {
        "code": 46431,
        "description": "Wholesale of audio tapes, records, CDs and video tapes and the equipment on which these are played"
    },
    {
        "code": 46439,
        "description": "Wholesale of radio, television goods & electrical household appliances (other than records, tapes, CD's & video tapes and the equipment used for playing them)"
    },
    {
        "code": 46440,
        "description": "Wholesale of china and glassware and cleaning materials"
    },
    {
        "code": 46450,
        "description": "Wholesale of perfume and cosmetics"
    },
    {
        "code": 46460,
        "description": "Wholesale of pharmaceutical goods"
    },
    {
        "code": 46470,
        "description": "Wholesale of furniture, carpets and lighting equipment"
    },
    {
        "code": 46480,
        "description": "Wholesale of watches and jewellery"
    },
    {
        "code": 46491,
        "description": "Wholesale of musical instruments"
    },
    {
        "code": 46499,
        "description": "Wholesale of household goods (other than musical instruments) n.e.c"
    },
    {
        "code": 46510,
        "description": "Wholesale of computers, computer peripheral equipment and software"
    },
    {
        "code": 46520,
        "description": "Wholesale of electronic and telecommunications equipment and parts"
    },
    {
        "code": 46610,
        "description": "Wholesale of agricultural machinery, equipment and supplies"
    },
    {
        "code": 46620,
        "description": "Wholesale of machine tools"
    },
    {
        "code": 46630,
        "description": "Wholesale of mining, construction and civil engineering machinery"
    },
    {
        "code": 46640,
        "description": "Wholesale of machinery for the textile industry and of sewing and knitting machines"
    },
    {
        "code": 46650,
        "description": "Wholesale of office furniture"
    },
    {
        "code": 46660,
        "description": "Wholesale of other office machinery and equipment"
    },
    {
        "code": 46690,
        "description": "Wholesale of other machinery and equipment"
    },
    {
        "code": 46711,
        "description": "Wholesale of petroleum and petroleum products"
    },
    {
        "code": 46719,
        "description": "Wholesale of other fuels and related products"
    },
    {
        "code": 46720,
        "description": "Wholesale of metals and metal ores"
    },
    {
        "code": 46730,
        "description": "Wholesale of wood, construction materials and sanitary equipment"
    },
    {
        "code": 46740,
        "description": "Wholesale of hardware, plumbing and heating equipment and supplies"
    },
    {
        "code": 46750,
        "description": "Wholesale of chemical products"
    },
    {
        "code": 46760,
        "description": "Wholesale of other intermediate products"
    },
    {
        "code": 46770,
        "description": "Wholesale of waste and scrap"
    },
    {
        "code": 46900,
        "description": "Non-specialised wholesale trade"
    },
    {
        "code": 47110,
        "description": "Retail sale in non-specialised stores with food, beverages or tobacco predominating"
    },
    {
        "code": 47190,
        "description": "Other retail sale in non-specialised stores"
    },
    {
        "code": 47210,
        "description": "Retail sale of fruit and vegetables in specialised stores"
    },
    {
        "code": 47220,
        "description": "Retail sale of meat and meat products in specialised stores"
    },
    {
        "code": 47230,
        "description": "Retail sale of fish, crustaceans and molluscs in specialised stores"
    },
    {
        "code": 47240,
        "description": "Retail sale of bread, cakes, flour confectionery and sugar confectionery in specialised stores"
    },
    {
        "code": 47250,
        "description": "Retail sale of beverages in specialised stores"
    },
    {
        "code": 47260,
        "description": "Retail sale of tobacco products in specialised stores"
    },
    {
        "code": 47290,
        "description": "Other retail sale of food in specialised stores"
    },
    {
        "code": 47300,
        "description": "Retail sale of automotive fuel in specialised stores"
    },
    {
        "code": 47410,
        "description": "Retail sale of computers, peripheral units and software in specialised stores"
    },
    {
        "code": 47421,
        "description": "Retail sale of mobile telephones"
    },
    {
        "code": 47429,
        "description": "Retail sale of telecommunications equipment other than mobile telephones"
    },
    {
        "code": 47430,
        "description": "Retail sale of audio and video equipment in specialised stores"
    },
    {
        "code": 47510,
        "description": "Retail sale of textiles in specialised stores"
    },
    {
        "code": 47520,
        "description": "Retail sale of hardware, paints and glass in specialised stores"
    },
    {
        "code": 47530,
        "description": "Retail sale of carpets, rugs, wall and floor coverings in specialised stores"
    },
    {
        "code": 47540,
        "description": "Retail sale of electrical household appliances in specialised stores"
    },
    {
        "code": 47591,
        "description": "Retail sale of musical instruments and scores"
    },
    {
        "code": 47599,
        "description": "Retail of furniture, lighting, and similar (not musical instruments or scores) in specialised store"
    },
    {
        "code": 47610,
        "description": "Retail sale of books in specialised stores"
    },
    {
        "code": 47620,
        "description": "Retail sale of newspapers and stationery in specialised stores"
    },
    {
        "code": 47630,
        "description": "Retail sale of music and video recordings in specialised stores"
    },
    {
        "code": 47640,
        "description": "Retail sale of sports goods, fishing gear, camping goods, boats and bicycles"
    },
    {
        "code": 47650,
        "description": "Retail sale of games and toys in specialised stores"
    },
    {
        "code": 47710,
        "description": "Retail sale of clothing in specialised stores"
    },
    {
        "code": 47721,
        "description": "Retail sale of footwear in specialised stores"
    },
    {
        "code": 47722,
        "description": "Retail sale of leather goods in specialised stores"
    },
    {
        "code": 47730,
        "description": "Dispensing chemist in specialised stores"
    },
    {
        "code": 47741,
        "description": "Retail sale of hearing aids"
    },
    {
        "code": 47749,
        "description": "Retail sale of medical and orthopaedic goods in specialised stores (not incl. hearing aids) n.e.c."
    },
    {
        "code": 47750,
        "description": "Retail sale of cosmetic and toilet articles in specialised stores"
    },
    {
        "code": 47760,
        "description": "Retail sale of flowers, plants, seeds, fertilizers, pet animals and pet food in specialised stores"
    },
    {
        "code": 47770,
        "description": "Retail sale of watches and jewellery in specialised stores"
    },
    {
        "code": 47781,
        "description": "Retail sale in commercial art galleries"
    },
    {
        "code": 47782,
        "description": "Retail sale by opticians"
    },
    {
        "code": 47789,
        "description": "Other retail sale of new goods in specialised stores (not commercial art galleries and opticians)"
    },
    {
        "code": 47791,
        "description": "Retail sale of antiques including antique books in stores"
    },
    {
        "code": 47799,
        "description": "Retail sale of other second-hand goods in stores (not incl. antiques)"
    },
    {
        "code": 47810,
        "description": "Retail sale via stalls and markets of food, beverages and tobacco products"
    },
    {
        "code": 47820,
        "description": "Retail sale via stalls and markets of textiles, clothing and footwear"
    },
    {
        "code": 47890,
        "description": "Retail sale via stalls and markets of other goods"
    },
    {
        "code": 47910,
        "description": "Retail sale via mail order houses or via Internet"
    },
    {
        "code": 47990,
        "description": "Other retail sale not in stores, stalls or markets"
    },
    {
        "code": "H",
        "description": "Transportation and storage"
    },
    {
        "code": 49100,
        "description": "Passenger rail transport, interurban"
    },
    {
        "code": 49200,
        "description": "Freight rail transport"
    },
    {
        "code": 49311,
        "description": "Urban and suburban passenger railway transportation by underground, metro and similar systems"
    },
    {
        "code": 49319,
        "description": "Other urban, suburban or metropolitan passenger land transport (not underground, metro or similar)"
    },
    {
        "code": 49320,
        "description": "Taxi operation"
    },
    {
        "code": 49390,
        "description": "Other passenger land transport"
    },
    {
        "code": 49410,
        "description": "Freight transport by road"
    },
    {
        "code": 49420,
        "description": "Removal services"
    },
    {
        "code": 49500,
        "description": "Transport via pipeline"
    },
    {
        "code": 50100,
        "description": "Sea and coastal passenger water transport"
    },
    {
        "code": 50200,
        "description": "Sea and coastal freight water transport"
    },
    {
        "code": 50300,
        "description": "Inland passenger water transport"
    },
    {
        "code": 50400,
        "description": "Inland freight water transport"
    },
    {
        "code": 51101,
        "description": "Scheduled passenger air transport"
    },
    {
        "code": 51102,
        "description": "Non-scheduled passenger air transport"
    },
    {
        "code": 51210,
        "description": "Freight air transport"
    },
    {
        "code": 51220,
        "description": "Space transport"
    },
    {
        "code": 52101,
        "description": "Operation of warehousing and storage facilities for water transport activities"
    },
    {
        "code": 52102,
        "description": "Operation of warehousing and storage facilities for air transport activities"
    },
    {
        "code": 52103,
        "description": "Operation of warehousing and storage facilities for land transport activities"
    },
    {
        "code": 52211,
        "description": "Operation of rail freight terminals"
    },
    {
        "code": 52212,
        "description": "Operation of rail passenger facilities at railway stations"
    },
    {
        "code": 52213,
        "description": "Operation of bus and coach passenger facilities at bus and coach stations"
    },
    {
        "code": 52219,
        "description": "Other service activities incidental to land transportation, n.e.c."
    },
    {
        "code": 52220,
        "description": "Service activities incidental to water transportation"
    },
    {
        "code": 52230,
        "description": "Service activities incidental to air transportation"
    },
    {
        "code": 52241,
        "description": "Cargo handling for water transport activities"
    },
    {
        "code": 52242,
        "description": "Cargo handling for air transport activities"
    },
    {
        "code": 52243,
        "description": "Cargo handling for land transport activities"
    },
    {
        "code": 52290,
        "description": "Other transportation support activities"
    },
    {
        "code": 53100,
        "description": "Postal activities under universal service obligation"
    },
    {
        "code": 53201,
        "description": "Licensed carriers"
    },
    {
        "code": 53202,
        "description": "Unlicensed carriers"
    },
    {
        "code": "I",
        "description": "Accommodation and food service activities"
    },
    {
        "code": 55100,
        "description": "Hotels and similar accommodation"
    },
    {
        "code": 55201,
        "description": "Holiday centres and villages"
    },
    {
        "code": 55202,
        "description": "Youth hostels"
    },
    {
        "code": 55209,
        "description": "Other holiday and other collective accommodation"
    },
    {
        "code": 55300,
        "description": "Recreational vehicle parks, trailer parks and camping grounds"
    },
    {
        "code": 55900,
        "description": "Other accommodation"
    },
    {
        "code": 56101,
        "description": "Licenced restaurants"
    },
    {
        "code": 56102,
        "description": "Unlicenced restaurants and cafes"
    },
    {
        "code": 56103,
        "description": "Take-away food shops and mobile food stands"
    },
    {
        "code": 56210,
        "description": "Event catering activities"
    },
    {
        "code": 56290,
        "description": "Other food services"
    },
    {
        "code": 56301,
        "description": "Licenced clubs"
    },
    {
        "code": 56302,
        "description": "Public houses and bars"
    },
    {
        "code": "J",
        "description": "Information and communication"
    },
    {
        "code": 58110,
        "description": "Book publishing"
    },
    {
        "code": 58120,
        "description": "Publishing of directories and mailing lists"
    },
    {
        "code": 58130,
        "description": "Publishing of newspapers"
    },
    {
        "code": 58141,
        "description": "Publishing of learned journals"
    },
    {
        "code": 58142,
        "description": "Publishing of consumer and business journals and periodicals"
    },
    {
        "code": 58190,
        "description": "Other publishing activities"
    },
    {
        "code": 58210,
        "description": "Publishing of computer games"
    },
    {
        "code": 58290,
        "description": "Other software publishing"
    },
    {
        "code": 59111,
        "description": "Motion picture production activities"
    },
    {
        "code": 59112,
        "description": "Video production activities"
    },
    {
        "code": 59113,
        "description": "Television programme production activities"
    },
    {
        "code": 59120,
        "description": "Motion picture, video and television programme post-production activities"
    },
    {
        "code": 59131,
        "description": "Motion picture distribution activities"
    },
    {
        "code": 59132,
        "description": "Video distribution activities"
    },
    {
        "code": 59133,
        "description": "Television programme distribution activities"
    },
    {
        "code": 59140,
        "description": "Motion picture projection activities"
    },
    {
        "code": 59200,
        "description": "Sound recording and music publishing activities"
    },
    {
        "code": 60100,
        "description": "Radio broadcasting"
    },
    {
        "code": 60200,
        "description": "Television programming and broadcasting activities"
    },
    {
        "code": 61100,
        "description": "Wired telecommunications activities"
    },
    {
        "code": 61200,
        "description": "Wireless telecommunications activities"
    },
    {
        "code": 61300,
        "description": "Satellite telecommunications activities"
    },
    {
        "code": 61900,
        "description": "Other telecommunications activities"
    },
    {
        "code": 62011,
        "description": "Ready-made interactive leisure and entertainment software development"
    },
    {
        "code": 62012,
        "description": "Business and domestic software development"
    },
    {
        "code": 62020,
        "description": "Information technology consultancy activities"
    },
    {
        "code": 62030,
        "description": "Computer facilities management activities"
    },
    {
        "code": 62090,
        "description": "Other information technology service activities"
    },
    {
        "code": 63110,
        "description": "Data processing, hosting and related activities"
    },
    {
        "code": 63120,
        "description": "Web portals"
    },
    {
        "code": 63910,
        "description": "News agency activities"
    },
    {
        "code": 63990,
        "description": "Other information service activities n.e.c."
    },
    {
        "code": "K",
        "description": "Financial and insurance activities"
    },
    {
        "code": 64110,
        "description": "Central banking"
    },
    {
        "code": 64191,
        "description": "Banks"
    },
    {
        "code": 64192,
        "description": "Building societies"
    },
    {
        "code": 64201,
        "description": "Activities of agricultural holding companies"
    },
    {
        "code": 64202,
        "description": "Activities of production holding companies"
    },
    {
        "code": 64203,
        "description": "Activities of construction holding companies"
    },
    {
        "code": 64204,
        "description": "Activities of distribution holding companies"
    },
    {
        "code": 64205,
        "description": "Activities of financial services holding companies"
    },
    {
        "code": 64209,
        "description": "Activities of other holding companies n.e.c."
    },
    {
        "code": 64301,
        "description": "Activities of investment trusts"
    },
    {
        "code": 64302,
        "description": "Activities of unit trusts"
    },
    {
        "code": 64303,
        "description": "Activities of venture and development capital companies"
    },
    {
        "code": 64304,
        "description": "Activities of open-ended investment companies"
    },
    {
        "code": 64305,
        "description": "Activities of property unit trusts"
    },
    {
        "code": 64306,
        "description": "Activities of real estate investment trusts"
    },
    {
        "code": 64910,
        "description": "Financial leasing"
    },
    {
        "code": 64921,
        "description": "Credit granting by non-deposit taking finance houses and other specialist consumer credit grantors"
    },
    {
        "code": 64922,
        "description": "Activities of mortgage finance companies"
    },
    {
        "code": 64929,
        "description": "Other credit granting n.e.c."
    },
    {
        "code": 64991,
        "description": "Security dealing on own account"
    },
    {
        "code": 64992,
        "description": "Factoring"
    },
    {
        "code": 64999,
        "description": "Financial intermediation not elsewhere classified"
    },
    {
        "code": 65110,
        "description": "Life insurance"
    },
    {
        "code": 65120,
        "description": "Non-life insurance"
    },
    {
        "code": 65201,
        "description": "Life reinsurance"
    },
    {
        "code": 65202,
        "description": "Non-life reinsurance"
    },
    {
        "code": 65300,
        "description": "Pension funding"
    },
    {
        "code": 66110,
        "description": "Administration of financial markets"
    },
    {
        "code": 66120,
        "description": "Security and commodity contracts dealing activities"
    },
    {
        "code": 66190,
        "description": "Activities auxiliary to financial intermediation n.e.c."
    },
    {
        "code": 66210,
        "description": "Risk and damage evaluation"
    },
    {
        "code": 66220,
        "description": "Activities of insurance agents and brokers"
    },
    {
        "code": 66290,
        "description": "Other activities auxiliary to insurance and pension funding"
    },
    {
        "code": 66300,
        "description": "Fund management activities"
    },
    {
        "code": "L",
        "description": "Real estate activities"
    },
    {
        "code": 68100,
        "description": "Buying and selling of own real estate"
    },
    {
        "code": 68201,
        "description": "Renting and operating of Housing Association real estate"
    },
    {
        "code": 68202,
        "description": "Letting and operating of conference and exhibition centres"
    },
    {
        "code": 68209,
        "description": "Other letting and operating of own or leased real estate"
    },
    {
        "code": 68310,
        "description": "Real estate agencies"
    },
    {
        "code": 68320,
        "description": "Management of real estate on a fee or contract basis"
    },
    {
        "code": "M",
        "description": "Professional, scientific and technical activities"
    },
    {
        "code": 69101,
        "description": "Barristers at law"
    },
    {
        "code": 69102,
        "description": "Solicitors"
    },
    {
        "code": 69109,
        "description": "Activities of patent and copyright agents; other legal activities n.e.c."
    },
    {
        "code": 69201,
        "description": "Accounting and auditing activities"
    },
    {
        "code": 69202,
        "description": "Bookkeeping activities"
    },
    {
        "code": 69203,
        "description": "Tax consultancy"
    },
    {
        "code": 70100,
        "description": "Activities of head offices"
    },
    {
        "code": 70210,
        "description": "Public relations and communications activities"
    },
    {
        "code": 70221,
        "description": "Financial management"
    },
    {
        "code": 70229,
        "description": "Management consultancy activities other than financial management"
    },
    {
        "code": 71111,
        "description": "Architectural activities"
    },
    {
        "code": 71112,
        "description": "Urban planning and landscape architectural activities"
    },
    {
        "code": 71121,
        "description": "Engineering design activities for industrial process and production"
    },
    {
        "code": 71122,
        "description": "Engineering related scientific and technical consulting activities"
    },
    {
        "code": 71129,
        "description": "Other engineering activities"
    },
    {
        "code": 71200,
        "description": "Technical testing and analysis"
    },
    {
        "code": 72110,
        "description": "Research and experimental development on biotechnology"
    },
    {
        "code": 72190,
        "description": "Other research and experimental development on natural sciences and engineering"
    },
    {
        "code": 72200,
        "description": "Research and experimental development on social sciences and humanities"
    },
    {
        "code": 73110,
        "description": "Advertising agencies"
    },
    {
        "code": 73120,
        "description": "Media representation services"
    },
    {
        "code": 73200,
        "description": "Market research and public opinion polling"
    },
    {
        "code": 74100,
        "description": "specialised design activities"
    },
    {
        "code": 74201,
        "description": "Portrait photographic activities"
    },
    {
        "code": 74202,
        "description": "Other specialist photography"
    },
    {
        "code": 74203,
        "description": "Film processing"
    },
    {
        "code": 74209,
        "description": "Photographic activities not elsewhere classified"
    },
    {
        "code": 74300,
        "description": "Translation and interpretation activities"
    },
    {
        "code": 74901,
        "description": "Environmental consulting activities"
    },
    {
        "code": 74902,
        "description": "Quantity surveying activities"
    },
    {
        "code": 74909,
        "description": "Other professional, scientific and technical activities n.e.c."
    },
    {
        "code": 74990,
        "description": "Non-trading company"
    },
    {
        "code": 75000,
        "description": "Veterinary activities"
    },
    {
        "code": "N",
        "description": "Administrative and support service activities"
    },
    {
        "code": 77110,
        "description": "Renting and leasing of cars and light motor vehicles"
    },
    {
        "code": 77120,
        "description": "Renting and leasing of trucks and other heavy vehicles"
    },
    {
        "code": 77210,
        "description": "Renting and leasing of recreational and sports goods"
    },
    {
        "code": 77220,
        "description": "Renting of video tapes and disks"
    },
    {
        "code": 77291,
        "description": "Renting and leasing of media entertainment equipment"
    },
    {
        "code": 77299,
        "description": "Renting and leasing of other personal and household goods"
    },
    {
        "code": 77310,
        "description": "Renting and leasing of agricultural machinery and equipment"
    },
    {
        "code": 77320,
        "description": "Renting and leasing of construction and civil engineering machinery and equipment"
    },
    {
        "code": 77330,
        "description": "Renting and leasing of office machinery and equipment (including computers)"
    },
    {
        "code": 77341,
        "description": "Renting and leasing of passenger water transport equipment"
    },
    {
        "code": 77342,
        "description": "Renting and leasing of freight water transport equipment"
    },
    {
        "code": 77351,
        "description": "Renting and leasing of air passenger transport equipment"
    },
    {
        "code": 77352,
        "description": "Renting and leasing of freight air transport equipment"
    },
    {
        "code": 77390,
        "description": "Renting and leasing of other machinery, equipment and tangible goods n.e.c."
    },
    {
        "code": 77400,
        "description": "Leasing of intellectual property and similar products, except copyright works"
    },
    {
        "code": 78101,
        "description": "Motion picture, television and other theatrical casting activities"
    },
    {
        "code": 78109,
        "description": "Other activities of employment placement agencies"
    },
    {
        "code": 78200,
        "description": "Temporary employment agency activities"
    },
    {
        "code": 78300,
        "description": "Human resources provision and management of human resources functions"
    },
    {
        "code": 79110,
        "description": "Travel agency activities"
    },
    {
        "code": 79120,
        "description": "Tour operator activities"
    },
    {
        "code": 79901,
        "description": "Activities of tourist guides"
    },
    {
        "code": 79909,
        "description": "Other reservation service activities n.e.c."
    },
    {
        "code": 80100,
        "description": "Private security activities"
    },
    {
        "code": 80200,
        "description": "Security systems service activities"
    },
    {
        "code": 80300,
        "description": "Investigation activities"
    },
    {
        "code": 81100,
        "description": "Combined facilities support activities"
    },
    {
        "code": 81210,
        "description": "General cleaning of buildings"
    },
    {
        "code": 81221,
        "description": "Window cleaning services"
    },
    {
        "code": 81222,
        "description": "Specialised cleaning services"
    },
    {
        "code": 81223,
        "description": "Furnace and chimney cleaning services"
    },
    {
        "code": 81229,
        "description": "Other building and industrial cleaning activities"
    },
    {
        "code": 81291,
        "description": "Disinfecting and exterminating services"
    },
    {
        "code": 81299,
        "description": "Other cleaning services"
    },
    {
        "code": 81300,
        "description": "Landscape service activities"
    },
    {
        "code": 82110,
        "description": "Combined office administrative service activities"
    },
    {
        "code": 82190,
        "description": "Photocopying, document preparation and other specialised office support activities"
    },
    {
        "code": 82200,
        "description": "Activities of call centres"
    },
    {
        "code": 82301,
        "description": "Activities of exhibition and fair organisers"
    },
    {
        "code": 82302,
        "description": "Activities of conference organisers"
    },
    {
        "code": 82911,
        "description": "Activities of collection agencies"
    },
    {
        "code": 82912,
        "description": "Activities of credit bureaus"
    },
    {
        "code": 82920,
        "description": "Packaging activities"
    },
    {
        "code": 82990,
        "description": "Other business support service activities n.e.c."
    },
    {
        "code": "O",
        "description": "Public administration and defence; compulsory social security"
    },
    {
        "code": 84110,
        "description": "General public administration activities"
    },
    {
        "code": 84120,
        "description": "Regulation of health care, education, cultural and other social services, not incl. social security"
    },
    {
        "code": 84130,
        "description": "Regulation of and contribution to more efficient operation of businesses"
    },
    {
        "code": 84210,
        "description": "Foreign affairs"
    },
    {
        "code": 84220,
        "description": "Defence activities"
    },
    {
        "code": 84230,
        "description": "Justice and judicial activities"
    },
    {
        "code": 84240,
        "description": "Public order and safety activities"
    },
    {
        "code": 84250,
        "description": "Fire service activities"
    },
    {
        "code": 84300,
        "description": "Compulsory social security activities"
    },
    {
        "code": "P",
        "description": "Education"
    },
    {
        "code": 85100,
        "description": "Pre-primary education"
    },
    {
        "code": 85200,
        "description": "Primary education"
    },
    {
        "code": 85310,
        "description": "General secondary education"
    },
    {
        "code": 85320,
        "description": "Technical and vocational secondary education"
    },
    {
        "code": 85410,
        "description": "Post-secondary non-tertiary education"
    },
    {
        "code": 85421,
        "description": "First-degree level higher education"
    },
    {
        "code": 85422,
        "description": "Post-graduate level higher education"
    },
    {
        "code": 85510,
        "description": "Sports and recreation education"
    },
    {
        "code": 85520,
        "description": "Cultural education"
    },
    {
        "code": 85530,
        "description": "Driving school activities"
    },
    {
        "code": 85590,
        "description": "Other education n.e.c."
    },
    {
        "code": 85600,
        "description": "Educational support services"
    },
    {
        "code": "Q",
        "description": "Human health and social work activities"
    },
    {
        "code": 86101,
        "description": "Hospital activities"
    },
    {
        "code": 86102,
        "description": "Medical nursing home activities"
    },
    {
        "code": 86210,
        "description": "General medical practice activities"
    },
    {
        "code": 86220,
        "description": "Specialists medical practice activities"
    },
    {
        "code": 86230,
        "description": "Dental practice activities"
    },
    {
        "code": 86900,
        "description": "Other human health activities"
    },
    {
        "code": 87100,
        "description": "Residential nursing care facilities"
    },
    {
        "code": 87200,
        "description": "Residential care activities for learning difficulties, mental health and substance abuse"
    },
    {
        "code": 87300,
        "description": "Residential care activities for the elderly and disabled"
    },
    {
        "code": 87900,
        "description": "Other residential care activities n.e.c."
    },
    {
        "code": 88100,
        "description": "Social work activities without accommodation for the elderly and disabled"
    },
    {
        "code": 88910,
        "description": "Child day-care activities"
    },
    {
        "code": 88990,
        "description": "Other social work activities without accommodation n.e.c."
    },
    {
        "code": "R",
        "description": "Arts, entertainment and recreation"
    },
    {
        "code": 90010,
        "description": "Performing arts"
    },
    {
        "code": 90020,
        "description": "Support activities to performing arts"
    },
    {
        "code": 90030,
        "description": "Artistic creation"
    },
    {
        "code": 90040,
        "description": "Operation of arts facilities"
    },
    {
        "code": 91011,
        "description": "Library activities"
    },
    {
        "code": 91012,
        "description": "Archives activities"
    },
    {
        "code": 91020,
        "description": "Museums activities"
    },
    {
        "code": 91030,
        "description": "Operation of historical sites and buildings and similar visitor attractions"
    },
    {
        "code": 91040,
        "description": "Botanical and zoological gardens and nature reserves activities"
    },
    {
        "code": 92000,
        "description": "Gambling and betting activities"
    },
    {
        "code": 93110,
        "description": "Operation of sports facilities"
    },
    {
        "code": 93120,
        "description": "Activities of sport clubs"
    },
    {
        "code": 93130,
        "description": "Fitness facilities"
    },
    {
        "code": 93191,
        "description": "Activities of racehorse owners"
    },
    {
        "code": 93199,
        "description": "Other sports activities"
    },
    {
        "code": 93210,
        "description": "Activities of amusement parks and theme parks"
    },
    {
        "code": 93290,
        "description": "Other amusement and recreation activities n.e.c."
    },
    {
        "code": "S",
        "description": "Other service activities"
    },
    {
        "code": 94110,
        "description": "Activities of business and employers membership organisations"
    },
    {
        "code": 94120,
        "description": "Activities of professional membership organisations"
    },
    {
        "code": 94200,
        "description": "Activities of trade unions"
    },
    {
        "code": 94910,
        "description": "Activities of religious organisations"
    },
    {
        "code": 94920,
        "description": "Activities of political organisations"
    },
    {
        "code": 94990,
        "description": "Activities of other membership organisations n.e.c."
    },
    {
        "code": 95110,
        "description": "Repair of computers and peripheral equipment"
    },
    {
        "code": 95120,
        "description": "Repair of communication equipment"
    },
    {
        "code": 95210,
        "description": "Repair of consumer electronics"
    },
    {
        "code": 95220,
        "description": "Repair of household appliances and home and garden equipment"
    },
    {
        "code": 95230,
        "description": "Repair of footwear and leather goods"
    },
    {
        "code": 95240,
        "description": "Repair of furniture and home furnishings"
    },
    {
        "code": 95250,
        "description": "Repair of watches, clocks and jewellery"
    },
    {
        "code": 95290,
        "description": "Repair of personal and household goods n.e.c."
    },
    {
        "code": 96010,
        "description": "Washing and (dry-)cleaning of textile and fur products"
    },
    {
        "code": 96020,
        "description": "Hairdressing and other beauty treatment"
    },
    {
        "code": 96030,
        "description": "Funeral and related activities"
    },
    {
        "code": 96040,
        "description": "Physical well-being activities"
    },
    {
        "code": 96090,
        "description": "Other service activities n.e.c."
    },
    {
        "code": "T",
        "description": "Activities of households as employers; undifferentiated goods- and services-producing activities of households for own use"
    },
    {
        "code": 97000,
        "description": "Activities of households as employers of domestic personnel"
    },
    {
        "code": 98000,
        "description": "Residents property management"
    },
    {
        "code": 98100,
        "description": "Undifferentiated goods-producing activities of private households for own use"
    },
    {
        "code": 98200,
        "description": "Undifferentiated service-producing activities of private households for own use"
    },
    {
        "code": "U",
        "description": "Activities of extraterritorial organisations and bodies"
    },
    {
        "code": 99000,
        "description": "Activities of extraterritorial organisations and bodies"
    },
    {
        "code": 99999,
        "description": "Dormant Company"
    }
];
